import React, { useContext } from "react";
import { Bounds, Vector2, Vector3 } from "@kitware/vtk.js/types";
import vtkMath, { radiansFromDegrees } from "@kitware/vtk.js/Common/Core/Math";
import { createAxesLinesAt } from "@features/view/axesLines";
import vtkOpenGLHardwareSelector from "@kitware/vtk.js/Rendering/OpenGL/HardwareSelector";

import vtkOpenGLRenderWindow from "@kitware/vtk.js/Rendering/OpenGL/RenderWindow";
import { FieldAssociations } from "@kitware/vtk.js/Common/DataModel/DataSet/Constants";
import { mat3, mat4, vec3 } from "gl-matrix";
import vtkGenericRenderWindow from "@kitware/vtk.js/Rendering/Misc/GenericRenderWindow";
import vtkRenderWindow, {
  DEFAULT_VIEW_API,
} from "@kitware/vtk.js/Rendering/Core/RenderWindow";
import vtkAnnotatedCubeActor, {
  IStyle,
} from "@kitware/vtk.js/Rendering/Core/AnnotatedCubeActor";
import vtkCubeSource from "@kitware/vtk.js/Filters/Sources/CubeSource";
import vtkOrientationMarkerWidget from "@kitware/vtk.js/Interaction/Widgets/OrientationMarkerWidget";
import vtkMouseCameraTrackballPanManipulator from "@kitware/vtk.js/Interaction/Manipulators/MouseCameraTrackballPanManipulator";
import vtkInteractorStyleManipulator from "@kitware/vtk.js/Interaction/Style/InteractorStyleManipulator";
import vtkRenderer from "@kitware/vtk.js/Rendering/Core/Renderer";
import vtkActor from "@kitware/vtk.js/Rendering/Core/Actor";
import vtkCellPicker from "@kitware/vtk.js/Rendering/Core/CellPicker";
import vtkPoints from "@kitware/vtk.js/Common/Core/Points";
import vtkCellArray from "@kitware/vtk.js/Common/Core/CellArray";
import vtkPolyData from "@kitware/vtk.js/Common/DataModel/PolyData";
import { getEnabledElements, Types } from "@cornerstonejs/core";
import vtkMapper from "@kitware/vtk.js/Rendering/Core/Mapper";
import {
  AnalysisType,
  defaultZoom,
  EditNames,
  getActorNameByAnalysisType,
  getSegmentationAnalysisType,
  getVolumeNameByAnalysisType,
  Segmentation,
  SegmentationControllerType,
  VOI,
} from "config/analysisConfig";
import { PanelComponentKeyType } from "config/panelConfig";
import vtkVolume from "@kitware/vtk.js/Rendering/Core/Volume";
import {
  getViewport,
  decomposeMask,
  clearCornerstoneResource,
  MIN_PARALLEL_SCALE,
  MAX_PARALLEL_SCALE,
  getVolumeFromSeriesId,
  getLabelVolumeFromSeriesId,
  fitDicomViewportsToScreen,
  isToolInteracting,
  createRegulatorSegmentation,
} from "./CornerstoneManager";
import vtkImageMapper from "@kitware/vtk.js/Rendering/Core/ImageMapper";
import vtkImageCPRMapper from "@kitware/vtk.js/Rendering/Core/ImageCPRMapper";
import vtkImageSlice from "@kitware/vtk.js/Rendering/Core/ImageSlice";
import vtkDataArray from "@kitware/vtk.js/Common/Core/DataArray";
import vtkCamera from "@kitware/vtk.js/Rendering/Core/Camera";
import { getCircleMeta } from "@features/view/vtkMarker";
import { downloadQueue } from "utils/DownloadQueue";
import {
  fetchSeries3DModel,
  fetchSeriesArrayBuffer,
  fetchSeriesJson,
} from "@api/files";
import {
  decompress,
  isCompressed,
  readHeader,
  readImage,
} from "nifti-reader-js";
import vtkImageData from "@kitware/vtk.js/Common/DataModel/ImageData";
import {
  CalculateResliceAxes,
  createCenterline,
  createCPRActorAndMapper,
  createCrossSectionActorAndMapper,
  createOutlineActorFromPoints,
  createOutlineMapper,
  getVtkImageOutlinePoints,
  getFastVtkImageBoundaryOutlinePoints,
  handleCPRAngle,
  hu_raw_to_vtkimage,
  LabelOpacityControl,
  MoveImageAndCamera,
  pathFunc,
  raw_to_vtkimage,
  rowdataUpscaleFunction,
} from "utils/vtkUtilites";
import vtkImageReslice from "@kitware/vtk.js/Imaging/Core/ImageReslice";
import vtkVolumeMapper from "@kitware/vtk.js/Rendering/Core/VolumeMapper";
import { scheduleRender } from "@features/analysis/rendering/Panels/renderState";
import { runWorkerTask } from "@features/analysis/rendering/Panels/workerPool";
import {
  BranchInfo,
  calcStenosisText,
  parseMappingData,
} from "utils/FunctionUtilites";
import { CalciumAnalysisResult } from "@models/Parameter";
import { hexToNormalizedRGB, hexToRgb } from "config/colorUtils";
import { AppColors } from "config/color";
import { AppConfig, LabelAllOnOff } from "config/config";
import type { Label } from "config/config";
import { vtkRange } from "@kitware/vtk.js/interfaces";
import { segmentation } from "@cornerstonejs/tools";
import {
  captureCanvaswithNotBlank,
  captureCPRPNG,
  trimMarginsToContent,
} from "utils/captureUtils";
import { cloneDeep } from "lodash";
import { registerCanvasCapture } from "@root/store";
import { configStore } from "config/configStore";
import { adjustSampleQuality } from "@features/view/VRUtilities";
import { RenderingQualityKeyType } from "config/settingsConfig";
import {
  getTaviCenterKeyFromActorKey,
  taviNodeRenderer,
} from "@features/view/Shared3DUtilities";
import { AorticNodeRenderer } from "@features/view/Surface3DUtilities";
import type { TaviPlaneConfig } from "config/config";
import { publishImageLayerStackLiveWindowingPolicy } from '../ImageLayerStack';
import type { ImageWindowingPolicy } from '../imageWindowing';
import vtkXMLPolyDataReader from "@kitware/vtk.js/IO/XML/XMLPolyDataReader";
import vtkPolyDataNormals from "@kitware/vtk.js/Filters/Core/PolyDataNormals";
import {
  DEFAULT_CPR_CROSS_SECTION_IMAGE_MANIFEST_TARGET,
  getCprCrossSectionImagesFromParameter,
  isCprCrossSectionImageModeEnabled,
  type CprCrossSectionImageMode,
  type CprCrossSectionImages,
} from "../cprImageResources";
import {
  iscprCrossSectionImages,
  loadcprCrossSectionImages,
} from "../cprCrossSectionImages";
import {
  checkServerCprFrontDataAvailableOnce,
  clearServerCprFrontDataRuntimeCache,
  isServerCprFrontDataAnalysisType,
  loadServerCprFrontDataImageManifestOnce,
} from "../frontDataCprDebugLoader";
import { clearServerImageFrontDataCacheExceptSeries } from "../serverImageCache";
import { getConfiguredCprCrossSectionImageMode } from "../cprImageModeConfig";
import { getServerFrontDataAvailability } from "../cprImageModeState";
import {
  moveInitialPointSphereIfInside,
  setInitialPointSphereOpacity,
} from "@features/view/initialPointSphereMarkers";
import vtkSTLReader from '@kitware/vtk.js/IO/Geometry/STLReader';

export interface Actors {
  [index: string]: vtkActor | vtkVolume | vtkImageSlice;
}
export interface Mappers {
  [index: string]: vtkImageMapper | vtkVolumeMapper | vtkImageCPRMapper;
}

export enum BranchType {
  RCA,
  LCX,
  LAD,
}

export const CPRType: Record<string, number> = {
  Proximal: 0,
  Middle: 1,
  Distal: 2,
};

let liveWindowLevelCommitRaf: number | null = null;
let liveWindowLevelCommitPayload: {
  update: ([wl, ww]: [number, number]) => void;
  values: [number, number];
} | null = null;

function scheduleLiveWindowLevelCommit(
  update: (([wl, ww]: [number, number]) => void) | undefined,
  values: [number, number],
) {
  if (!update) return;
  liveWindowLevelCommitPayload = { update, values };
  if (liveWindowLevelCommitRaf !== null) return;

  liveWindowLevelCommitRaf = window.requestAnimationFrame(() => {
    liveWindowLevelCommitRaf = null;
    const payload = liveWindowLevelCommitPayload;
    liveWindowLevelCommitPayload = null;
    payload?.update(payload.values);
  });
}

function isServerFrontDataVtkMode(
  pool: BufferPoolItem | null | undefined,
  analysisType?: AnalysisType,
): boolean {
  return (
    getConfiguredCprCrossSectionImageMode() === "server" &&
    isServerCprFrontDataAnalysisType(analysisType) &&
    getServerFrontDataAvailability(pool) !== "missing"
  );
}

function releaseServerFrontDataViewPropGraphicsResources(
  prop: any,
  renderWindow?: any,
) {
  if (!prop) return;
  try {
    prop.getMapper?.()?.releaseGraphicsResources?.(renderWindow);
    prop.releaseGraphicsResources?.(renderWindow);
  } catch (err) {
    console.warn(
      "[vtk][server-front-data] failed to release view prop graphics resources",
      err,
    );
  }
}

function removeAllServerFrontDataRendererViewProps(
  renderer?: any,
  renderWindow?: any,
) {
  if (!renderer) return;
  try {
    renderer.getActors?.().forEach((actor: any) =>
      releaseServerFrontDataViewPropGraphicsResources(actor, renderWindow),
    );
    renderer.getVolumes?.().forEach((volume: any) =>
      releaseServerFrontDataViewPropGraphicsResources(volume, renderWindow),
    );
  } catch (err) {
    console.warn(
      "[vtk][server-front-data] failed to release renderer resources",
      err,
    );
  }
  renderer.removeAllActors?.();
  renderer.removeAllVolumes?.();
}

function getOpenGLRenderWindowFromRenderWindow(renderWindow?: any): any {
  return renderWindow?.getViews?.()?.[0];
}

function isVtkObjectDeleted(obj?: any): boolean {
  if (!obj) return true;
  try {
    return Boolean(obj.isDeleted?.());
  } catch {
    return true;
  }
}

function disposeOrientationMarkerWidget(
  orientationWidget?: vtkOrientationMarkerWidget | null,
) {
  if (!orientationWidget) return;

  try {
    orientationWidget.setEnabled?.(false);
  } catch {
    // best-effort only
  }

  try {
    orientationWidget.setInteractor?.(null as any);
  } catch {
    // best-effort only
  }

  try {
    orientationWidget.delete?.();
  } catch {
    // best-effort only
  }
}

function isServerFrontDataRenderWindowUsable(renderWindow?: any): boolean {
  const openGLRenderWindow = getOpenGLRenderWindowFromRenderWindow(renderWindow);
  if (!openGLRenderWindow) return false;

  const canvas = openGLRenderWindow.getCanvas?.() as HTMLCanvasElement | undefined;
  if (!canvas || !canvas.isConnected) return false;

  const size = openGLRenderWindow.getSize?.();
  if (!size || size[0] === 0 || size[1] === 0) return false;

  const gl =
    openGLRenderWindow.getContext?.() ??
    openGLRenderWindow.get3DContext?.();
  if (!gl || gl.isContextLost?.()) return false;

  return true;
}

export function renderVtkContextWindow(
  context?: Partial<Context> | null,
  reason: string = "render",
): boolean {
  const renderWindow = context?.renderWindow;
  if (!renderWindow) return false;

  if (!context?.serverFrontDataVtkMode) {
    renderWindow.render?.();
    return true;
  }

  if (context.serverFrontDataRenderBlocked || context.serverFrontDataVtkDisposed) return false;
  if (!isServerFrontDataRenderWindowUsable(renderWindow)) return false;

  try {
    renderWindow.render?.();
    return true;
  } catch (err) {
    console.warn("[vtk][server-front-data] render skipped", {
      viewportId: context.viewportId,
      reason,
      err,
    });
    return false;
  }
}

export function disposeServerFrontDataVtkContext(
  context?: Partial<Context> | null,
  reason: string = "dispose",
) {
  if (!context || context.serverFrontDataVtkDisposed) return;

  context.serverFrontDataRenderBlocked = true;
  context.serverFrontDataVtkDisposed = true;

  disposeOrientationMarkerWidget(context.orientationWidget ?? null);
  context.orientationWidget = undefined;

  try {
    if (!isVtkObjectDeleted(context.renderWindow)) {
      removeAllServerFrontDataRendererViewProps(context.renderer, context.renderWindow);
      removeAllServerFrontDataRendererViewProps(context.overlayRenderer, context.renderWindow);
    }
    if (context.actorIds) {
      Object.keys(context.actorIds).forEach((key) => {
        delete context.actorIds![key];
      });
    }
  } catch {
    // best-effort cleanup only. The context may already be partially disposed.
  }

  try {
    if (!isVtkObjectDeleted(context.renderWindow)) {
      const views = context.renderWindow?.getViews?.() ?? [];
      views.forEach((view: any) => {
        if (isVtkObjectDeleted(view)) return;
        try {
          const gl = view.getContext?.() ?? view.get3DContext?.();
          view.releaseGraphicsResources?.(gl);
        } catch {
          // best-effort only
        }
        try {
          view.setContainer?.(null);
        } catch {
          // best-effort only
        }
      });
    }
  } catch {
    // best-effort only
  }

  try {
    if (!isVtkObjectDeleted(context.genericRenderWindow)) {
      context.genericRenderWindow?.delete?.();
    }
  } catch {
    // VTK may already have disposed part of the render window. Do not surface
    // noisy cleanup errors during normal feature switching.
  }
}


export interface territoryInfo {
  json: ArrayBuffer;
  territory: Uint8Array;
  territoryMappingData: Record<number, number>;
  branches: Uint8Array;
  centerline: Uint16Array;
  mappingBranches: Record<number, number>;
}

export interface mappingBranchInfo {
  stenosisBranches: Record<number, number>;
  branches: Record<number, Set<number>>;
}

export const TAVI_POOL_KEY_PREFIX = "tavi.";
export const TAVI_RESOURCE_NAMES = [
  "centerline",
  "cusp",
  "ostium",
  "sinotubularjunction",
  "sinus",
  "annulus",
  "center_dx",
  "center_dy",
  "centerTangent",
  "measurements",
  "heightPlane",
] as const;
export const TAVI_RESOURCE_KEYS = TAVI_RESOURCE_NAMES.map(
  (name) => `${TAVI_POOL_KEY_PREFIX}${name}`,
);

export type CPRBufferKeys =
  | "branchLabel"
  | "centerline"
  | "fcx"
  | "fcy"
  | "fcz"
  | "fxx"
  | "fxy"
  | "fxz"
  | "fyx"
  | "fyy"
  | "fyz"
  | "newType"
  | "labelData"
  | "splitData"
  | "splitLabelData"
  | "centerData"
  | "fxData"
  | "fyData"
  | "new_area_path"
  | "new_diameter"
  | "new_wall_path"
  | "new_wall_area_path"
  | "plaque_info_path"
  | "branchGraphDataset"
  | "branchid"
  | "mapping_branches"
  | "parameter"
  | "type_total"
  // | 'dilatedLabel'
  | "segmentationVolumeMapper"
  | "aorta_centerline"
  | "startNode_list"
  | "segmentationResult_la"
  | "segmentationResult_lv"
  | "segmentationResult_ra"
  | "segmentationResult_rv"
  | "segmentationResult_laWall"
  | "segmentationResult_lvWall"
  | "segmentationResult_raWall"
  | "segmentationResult_rvWall"
  | "segmentationResult_aorta"
  | "segmentationResult_coronary"
  | "segmentationResult_avc"
  | "segmentationResult_epicardial"
  | "segmentationResult_calcium"
  | "segmentationResult_lowAttenuation"
  | "segmentationResult_nonCalcified"
  | "segmentationResult_calcified"
  | "EF"
  | "LAWT"
  | "territory"
  | "oriLabelData"
  // | 'oriHuData'
  | "oriCenterData"
  | "ffrGraphDataset"
  | "FFR_main_rca"
  | "FFR_main_lca"
  | "FFR_cap_rca"
  | "FFR_cap_lca"
  | "FFR_centerline"
  | "FFR_aorta"
  | "ct_ffr"
  | "cprCrossSectionImages";

type ParameterPath = string[] | string[][] | undefined;

interface BufferPoolData {
  loaded: boolean;
  loading?: boolean;
  error?: boolean;
  frontDataAvailability?: "available" | "missing";
  requestedAt?: number;
  retryExhausted?: boolean;
  path?: ParameterPath;
  data?:
    | ArrayBuffer
    | Uint8Array
    | Float32Array
    | Uint16Array
    | Int32Array
    | Record<number, Vector3[]>
    | Record<number, Uint8Array | null>
    | Record<number, number[]>
    | Record<number, number>
    | Record<number, vtkPolyData>
    | Record<number, vtkImageData>
    | Record<number, Set<number>>
    | vtkPolyData
    | vtkImageData
    | vtkVolumeMapper
    | BranchInfo
    | CalciumAnalysisResult
    | Record<
        string,
        {
          percent: number;
          data: Uint8Array;
          chamberInfo: { normal: vec3; right: vec3; up: vec3; center: vec3 };
        }
      >
    | territoryInfo
    | mappingBranchInfo
    | { index: number; value: number }[]
    | {
        byNode: Record<
          number,
          {
            ffr: Array<{ x: number; y: number }>;
            area: Array<{ x: number; y: number }>;
            diameter: Array<{ x: number; y: number }>;
          }
        >;
      }
    | CprCrossSectionImages;
}

export interface BufferPoolDataRaw extends BufferPoolData {
  loading: boolean;
  target: string;
  dataType:
    | typeof Uint8Array
    | typeof Int32Array
    | typeof Float32Array
    | typeof ArrayBuffer;
}
type BufferPoolType = BufferPoolDataRaw | BufferPoolData;

export type BufferPoolItem = {
  [key in CPRBufferKeys]: BufferPoolType;
} & Record<string, BufferPoolType>;
// Record<CPRBufferKeys, BufferPoolType>;
const initialBufferPool: BufferPoolItem = {
  //download files
  parameter: {
    target: "parameter.json",
    dataType: ArrayBuffer,
    loaded: false,
    loading: false,
  } as BufferPoolDataRaw,

  cprCrossSectionImages: {
    target: DEFAULT_CPR_CROSS_SECTION_IMAGE_MANIFEST_TARGET,
    dataType: ArrayBuffer,
    loaded: false,
    loading: false,
    path: [
      ["ImageResources", "cprCrossSectionImagesPath"],
      ["Interpretation", "cprCrossSectionImagesPath"],
      ["cprCrossSectionImagesPath"],
    ],
  } as BufferPoolDataRaw,

  branchLabel: {
    target: "branchcut_results_path.bin",
    dataType: Uint8Array,
    loaded: false,
    loading: false,
    path: ["Coronary", "branchcut_path"],
  } as BufferPoolDataRaw,
  centerline: {
    target: "newCenterline_path.bin",
    dataType: Int32Array,
    loaded: false,
    loading: false,
    path: ["Coronary", "newCenterline_path"],
  } as BufferPoolDataRaw,
  fcx: {
    target: "fcX_total.nii.gz",
    dataType: Float32Array,
    loaded: false,
    loading: false,
    path: ["Coronary", "fcX_total"],
  } as BufferPoolDataRaw,
  fcy: {
    target: "fcY_total.nii.gz",
    dataType: Float32Array,
    loaded: false,
    loading: false,
    path: ["Coronary", "fcY_total"],
  } as BufferPoolDataRaw,
  fcz: {
    target: "fcZ_total.nii.gz",
    dataType: Float32Array,
    loaded: false,
    loading: false,
    path: ["Coronary", "fcZ_total"],
  } as BufferPoolDataRaw,

  fxx: {
    target: "fxX_total.nii.gz",
    dataType: Float32Array,
    loaded: false,
    loading: false,
    path: ["Coronary", "fxX_total"],
  } as BufferPoolDataRaw,
  fxy: {
    target: "fxY_total.nii.gz",
    dataType: Float32Array,
    loaded: false,
    loading: false,
    path: ["Coronary", "fxY_total"],
  } as BufferPoolDataRaw,
  fxz: {
    target: "fxZ_total.nii.gz",
    dataType: Float32Array,
    loaded: false,
    loading: false,
    path: ["Coronary", "fxZ_total"],
  } as BufferPoolDataRaw,
  fyx: {
    target: "fyX_total.nii.gz",
    dataType: Float32Array,
    loaded: false,
    loading: false,
    path: ["Coronary", "fyX_total"],
  } as BufferPoolDataRaw,
  fyy: {
    target: "fyY_total.nii.gz",
    dataType: Float32Array,
    loaded: false,
    loading: false,
    path: ["Coronary", "fyY_total"],
  } as BufferPoolDataRaw,
  fyz: {
    target: "fyZ_total.nii.gz",
    dataType: Float32Array,
    loaded: false,
    loading: false,
    path: ["Coronary", "fyZ_total"],
  } as BufferPoolDataRaw,

  newType: {
    target: "newType.nii.gz",
    dataType: Int32Array,
    loaded: false,
    loading: false,
    path: ["Coronary", "newType_path"],
  } as BufferPoolDataRaw,

  branchid: {
    target: "branchid_total.nii.gz",
    dataType: Int32Array,
    loaded: false,
    loading: false,
    path: ["Coronary", "branchid_total"],
  } as BufferPoolDataRaw,

  type_total: {
    target: "type_total.nii.gz",
    dataType: Int32Array,
    loaded: false,
    loading: false,
    path: ["Coronary", "type_total"],
  } as BufferPoolDataRaw,

  new_area_path: {
    target: "new_area_path.bin",
    dataType: Int32Array,
    loaded: false,
    loading: false,
    path: ["Coronary", "new_area_path"],
  } as BufferPoolDataRaw,
  new_diameter: {
    target: "new_diameter.bin",
    dataType: Int32Array,
    loaded: false,
    loading: false,
    path: ["Coronary", "new_diameter_path"],
  } as BufferPoolDataRaw,

  new_wall_path: {
    target: "new_wall_path.bin",
    dataType: Int32Array,
    loaded: false,
    loading: false,
    path: ["Coronary", "new_wall_path"],
  } as BufferPoolDataRaw,

  new_wall_area_path: {
    target: "new_wall_area_path.bin",
    dataType: Int32Array,
    loaded: false,
    loading: false,
    path: ["Coronary", "new_wall_area_path"],
  } as BufferPoolDataRaw,

  plaque_info_path: {
    target: "plaque_info_path.bin",
    dataType: ArrayBuffer,
    loaded: false,
    loading: false,
    path: ["Coronary", "plaque_info_path"],
  } as BufferPoolDataRaw,

  aorta_centerline: {
    target: "aorta_centerline.bin",
    dataType: Float32Array,
    loaded: false,
    loading: false,
    path: [
      ["Aorta", "AortaCenterline"],
      ["Aorta", "aorta_centerline"],
      ["AortaCenterline"],
      ["aorta_centerline"],
    ],
  } as BufferPoolDataRaw,

  startNode_list: {
    target: "startNode_list.nii.gz",
    dataType: Int32Array,
    loaded: false,
    loading: false,
    path: ["Coronary", "startNode_list"],
  } as BufferPoolDataRaw,

  labelData: {
    loaded: false,
  } as BufferPoolData,
  // Record<number,Vector3[]>
  splitData: {
    loaded: false,
  } as BufferPoolData,
  // Record<number,Int32Array>
  splitLabelData: {
    loaded: false,
  } as BufferPoolData,
  // Record<number,vtkPolyData>
  centerData: {
    loaded: false,
  } as BufferPoolData,
  fxData: {
    loaded: false,
  } as BufferPoolData,
  fyData: {
    loaded: false,
  } as BufferPoolData,

  branchGraphDataset: {
    loaded: false,
  } as BufferPoolData,
  ffrGraphDataset: {
    loaded: false,
  } as BufferPoolData,

  oriLabelData: {
    loaded: false,
  } as BufferPoolData,
  // oriHuData: {
  //   loaded: false,
  // } as BufferPoolData,
  oriCenterData: {
    loaded: false,
  } as BufferPoolData,

  // Record<number,vtkPolyData>
  // dilatedLabel: {
  //   loaded: false,
  // } as BufferPoolData,
  segmentationVolumeMapper: {
    loaded: false,
  } as BufferPoolData,

  //임시로 꾸겨넣는 코드
  segmentationResult_la: {
    target: "la.drc",
    loaded: false,
    path: [
      ["Modification", "draco", "la_draco_path"],
      ["draco", "la_draco_path"],
    ],
  } as BufferPoolData,

  segmentationResult_lv: {
    target: "lv.drc",
    path: [
      ["Modification", "draco", "lv_draco_path"],
      ["draco", "lv_draco_path"],
    ],
    loaded: false,
  } as BufferPoolData,

  segmentationResult_ra: {
    target: "ra.drc",
    path: [
      ["Modification", "draco", "ra_draco_path"],
      ["draco", "ra_draco_path"],
    ],
    loaded: false,
  } as BufferPoolData,

  segmentationResult_rv: {
    target: "rv.drc",
    path: [
      ["Modification", "draco", "rv_draco_path"],
      ["draco", "rv_draco_path"],
    ],
    loaded: false,
  } as BufferPoolData,

  segmentationResult_avc: {
    target: "avc.drc",
    path: [
      ["Modification", "draco", "avc_draco_path"],
      ["draco", "avc_draco_path"],
    ],
    loaded: false,
  } as BufferPoolData,
  segmentationResult_epicardial: {
    target: "epicardial_fat.drc",
    path: [
      ["Modification", "draco", "epicardial_fat_draco_path"],
      ["draco", "epicardial_fat_draco_path"],
    ],
    loaded: false,
  } as BufferPoolData,

  segmentationResult_laWall: {
    target: "la_wall.drc",
    loaded: false,
    path: [
      ["Modification", "draco", "la_wall_draco_path"],
      ["draco", "la_wall_draco_path"],
    ],
  } as BufferPoolData,
  segmentationResult_lvWall: {
    target: "lv_wall.drc",
    loaded: false,
    path: [
      ["Modification", "draco", "lv_wall_draco_path"],
      ["draco", "lv_wall_draco_path"],
    ],
  } as BufferPoolData,
  segmentationResult_raWall: {
    target: "ra_wall.drc",
    path: [
      ["Modification", "draco", "ra_wall_draco_path"],
      ["draco", "ra_wall_draco_path"],
    ],
    loaded: false,
  } as BufferPoolData,
  segmentationResult_rvWall: {
    target: "rv_wall.drc",
    path: [
      ["Modification", "draco", "rv_wall_draco_path"],
      ["draco", "rv_wall_draco_path"],
    ],
    loaded: false,
  } as BufferPoolData,

  segmentationResult_aorta: {
    target: "aorta.drc",
    path: [
      ["Modification", "draco", "aorta_draco_path"],
      ["draco", "aorta_draco_path"],
    ],
    loaded: false,
  } as BufferPoolData,
  segmentationResult_coronary: {
    target: "lumen.drc",
    path: [
      ["Modification", "draco", "lumen_draco_path"],
      ["draco", "lumen_draco_path"],
    ],
    loaded: false,
  } as BufferPoolData,
  segmentationResult_calcium: {
    target: "coronary_calcium.drc",
    path: [
      ["Modification", "draco", "coronary_calcium_draco_path"],
      ["draco", "coronary_calcium_draco_path"],
    ],
    loaded: false,
  } as BufferPoolData,

  segmentationResult_lowAttenuation: {
    target: "low_attenuation.drc",
    path: [
      ["Modification", "draco", "low_attenuation_draco_path"],
      ["draco", "low_attenuation_draco_path"],
    ],
    loaded: false,
  } as BufferPoolData,

  segmentationResult_calcified: {
    target: "calcified.drc",
    path: [
      ["Modification", "draco", "calcified_draco_path"],
      ["draco", "calcified_draco_path"],
    ],
    loaded: false,
  } as BufferPoolData,

  segmentationResult_nonCalcified: {
    target: "noncalcified.drc",
    path: [
      ["Modification", "draco", "noncalcified_draco_path"],
      ["draco", "noncalcified_draco_path"],
    ],
    loaded: false,
  } as BufferPoolData,

  mapping_branches: {
    target: "compressed_merge_mapping_branches.nii.gz",
    path: [
      ["Modification", "Coronary", "coronary_merge_mapping_path"],
      ["Coronary", "coronary_merge_mapping_path"],
    ],
    loaded: false,
  } as BufferPoolData,

  EF: {
    loaded: false,
  } as BufferPoolData,

  territory: {
    loaded: false,
    path: ["Modification", "MyoCardial_path"],
  } as BufferPoolData,

  LAWT: {
    target: "la_wall_thickness_quality.bin",
    path: [["LAWT_quality_path"], ["LAWT", "LAWT_quality_path"]],
    loaded: false,
  } as BufferPoolData,

  "tavi.centerline": {
    target: "TAVI%2Fcenterline.bin",
    dataType: ArrayBuffer,
    loaded: false,
    loading: false,
    path: ["TAVI", "centerline"],
  } as BufferPoolDataRaw,
  "tavi.cusp": {
    target: "TAVI%2Fcusp.bin",
    dataType: ArrayBuffer,
    loaded: false,
    loading: false,
    path: ["TAVI", "cusp"],
  } as BufferPoolDataRaw,
  "tavi.ostium": {
    target: "TAVI%2Fostium.bin",
    dataType: ArrayBuffer,
    loaded: false,
    loading: false,
    path: ["TAVI", "ostium"],
  } as BufferPoolDataRaw,
  "tavi.sinotubularjunction": {
    target: "TAVI%2Fsinotubularjunction.bin",
    dataType: ArrayBuffer,
    loaded: false,
    loading: false,
    path: ["TAVI", "sinotubularjunction"],
  } as BufferPoolDataRaw,
  "tavi.sinus": {
    target: "TAVI%2Fsinus.bin",
    dataType: ArrayBuffer,
    loaded: false,
    loading: false,
    path: ["TAVI", "sinus"],
  } as BufferPoolDataRaw,
  "tavi.annulus": {
    target: "TAVI%2Fannulus.bin",
    dataType: ArrayBuffer,
    loaded: false,
    loading: false,
    path: ["TAVI", "annulus"],
  } as BufferPoolDataRaw,
  "tavi.center_dx": {
    target: "TAVI%2Fcenter_dx.bin",
    dataType: ArrayBuffer,
    loaded: false,
    loading: false,
    path: ["TAVI", "center_dx"],
  } as BufferPoolDataRaw,
  "tavi.center_dy": {
    target: "TAVI%2Fcenter_dy.bin",
    dataType: ArrayBuffer,
    loaded: false,
    loading: false,
    path: ["TAVI", "center_dy"],
  } as BufferPoolDataRaw,
  "tavi.centerTangent": {
    target: "TAVI%2FcenterTangent.bin",
    dataType: ArrayBuffer,
    loaded: false,
    loading: false,
    path: ["TAVI", "centerTangent"],
  } as BufferPoolDataRaw,
  "tavi.measurements": {
    target: "TAVI%2Fmeasurements.bin",
    dataType: ArrayBuffer,
    loaded: false,
    loading: false,
    path: ["TAVI", "measurements"],
  } as BufferPoolDataRaw,
  "tavi.heightPlane": {
    target: "TAVI%2FheightPlane.bin",
    dataType: ArrayBuffer,
    loaded: false,
    loading: false,
    path: ["TAVI", "heightPlane"],
  } as BufferPoolDataRaw,

  FFR_cap_rca: {
    target: "result_cap_rca.vtp",
    dataType: ArrayBuffer,
    loaded: false,
    loading: false,
    path: [["result_cap_rca"], ["CT_FFR", "result_cap_rca"]],
  },
  FFR_cap_lca: {
    target: "result_cap_lca.vtp",
    dataType: ArrayBuffer,
    loaded: false,
    loading: false,
    path: [["result_cap_lca"], ["CT_FFR", "result_cap_lca"]],
  },
  FFR_main_rca: {
    target: "result_main_rca.vtp",
    dataType: ArrayBuffer,
    loaded: false,
    loading: false,
    path: [["result_main_rca"], ["CT_FFR", "result_main_rca"]],
  },
  FFR_main_lca: {
    target: "result_main_lca.vtp",
    dataType: ArrayBuffer,
    loaded: false,
    loading: false,
    path: [["result_main_lca"], ["CT_FFR", "result_main_lca"]],
  },
  FFR_centerline: {
    target: "result_cent_regen.vtp",
    dataType: ArrayBuffer,
    loaded: false,
    loading: false,
    path: [["result_cent_regen"], ["CT_FFR", "result_cent_regen"]],
  },
  ct_ffr: {
    target: "ct_ffr%2Fct_ffr.json",
    dataType: ArrayBuffer,
    loaded: false,
    loading: false,
  },
  FFR_aorta:{
    target: "aorta.stl",
    dataType: ArrayBuffer,
    loaded: false,
    loading: false,
  },
};

function resolveTargetFromParameter(
  parameter: CalciumAnalysisResult | undefined,
  value: BufferPoolDataRaw,
) {
  if (!parameter || !value.path) return value.target;
  const candidates = Array.isArray(value.path[0])
    ? (value.path as string[][])
    : [value.path as string[]];
  const roots = [parameter?.SavedInfo, parameter] as Array<any>;
  for (const path of candidates) {
    for (const root of roots) {
      const resolved = path.reduce<any>((acc, key) => acc?.[key], root);
      if (typeof resolved === "string" && resolved.length > 0) {
        const target = resolved.replace(/^\.\//, "").replaceAll("/", "%2F");
        return target;
      }
    }
  }
  return value.target;
}

export function applyParameterPaths(
  pool: BufferPoolItem,
  parameter: any,
): BufferPoolItem {
  if (!parameter) return pool;

  let changed = false;
  const next = { ...pool } as BufferPoolItem;

  Object.entries(pool).forEach(([key, value]) => {
    const current = value as BufferPoolType;
    if (!("target" in current) || !current.target || !current.path) return;

    let resolved = resolveTargetFromParameter(
      parameter,
      current as BufferPoolDataRaw,
    );
    if (resolved === current.target) return;

    console.log(`때려넣은거 : ${current.target}, 읽어온거: ${resolved}`);
    if (
      current.target.endsWith(".drc") &&
      typeof resolved === "string" &&
      resolved.endsWith(".stl")
    ) {
      resolved = resolved.replace(/\.stl$/i, ".drc");
      console.log(`Parameter 경로 예외처리: ${resolved}`);
    }
    changed = true;
    next[key as CPRBufferKeys] = {
      ...current,
      target: resolved,
    } as BufferPoolType;
  });

  return changed ? next : pool;
}

function safeVTKDispose(obj: any) {
  if (!obj || typeof obj.delete !== "function") return;

  function clearPointCellData(pd: any, cd: any) {
    [pd, cd].forEach((data) => {
      data?.getArrays?.().forEach((arr: any) => {
        if (arr?.setData) {
          arr.setData(new Uint8Array(0), 1);
        }
        arr?.delete?.();
      });
    });
  }

  if (obj.isA?.("vtkImageData")) {
    clearPointCellData(obj.getPointData?.(), obj.getCellData?.());
  }

  if (obj.isA?.("vtkPolyData")) {
    clearPointCellData(obj.getPointData?.(), obj.getCellData?.());
  }

  if (obj.isA?.("vtkVolumeMapper")) {
    obj.setInputData?.(null);
    obj.setInputConnection?.(null);
  }

  obj.delete();
}

function disposeBufferPoolItem(item: BufferPoolType) {
  const { data } = item;
  if (!data) return;
  (item as any).loaded = false;
  (item as any).loading = false;

  if (data instanceof ArrayBuffer || ArrayBuffer.isView(data)) {
    (item as any).data = null;
    return;
  }

  if (typeof data === "object" && !(data as any).isA) {
    Object.values(data).forEach((val) => {
      if (!val) return;
      if (typeof (val as any).delete === "function") {
        safeVTKDispose(val);
      } else if (val instanceof ArrayBuffer || ArrayBuffer.isView(val)) {
        val = null;
      } else if (typeof val === "object") {
        disposeBufferPoolItem({ data: val } as any);
      }
    });
    (item as any).data = null;
    return;
  }

  if ((data as any).isA && typeof (data as any).delete === "function") {
    safeVTKDispose(data);
    (item as any).data = null;
    // (data as any).delete();
  }
}

export function getSegmentationBufferKeys(type: AnalysisType): CPRBufferKeys[] {
  const keys = getSegmentationAnalysisType(type).filter(
    (label) => !(type === "FFR" && label === "coronary"),
  );
  return keys.map((label) => `segmentationResult_${label}` as CPRBufferKeys);
}

export type BufferPool = Record<string, BufferPoolItem>;
export type ContextKey =
  | "CPR.0.stretched"
  | "CPR.0.straightened"
  | "CrossSection.0.0"
  | "CrossSection.0.1"
  | "CrossSection.0.2"
  // 'CPR.1.stretched'|'CPR.1.straightened'|'CrossSection.1.0'| 'CrossSection.1.1'|'CrossSection.1.2'|
  // 'CPR.2.stretched'|'CPR.2.straightened'|'CrossSection.2.0'| 'CrossSection.2.1'|'CrossSection.2.2'|
  | "CPR.1.straightened"
  | "CrossSection.1.0"
  | "CrossSection.1.1"
  | "CrossSection.1.2"
  | "CPR.2.straightened"
  | "CrossSection.2.0"
  | "CrossSection.2.1"
  | "CrossSection.2.2"
  // 'CPR.3.straightened' | 'CrossSection.3.0' |
  | "VR"
  | "Surface3D";
function disposeImageReslice(reslice: any) {
  if (!reslice) return;

  try {
    // 출력 데이터 정리
    const output = reslice?.getOutputData?.();
    if (output && typeof output.delete === "function") {
      // pointData / cellData 까지 정리
      const pd = output.getPointData?.();
      const cd = output.getCellData?.();
      [pd, cd].forEach((data) => {
        data?.getArrays?.().forEach((arr: any) => {
          arr.setData?.(new Uint8Array(0), 1);
          arr.delete?.();
        });
      });
      output.delete();
    }

    // 입력 해제
    if (reslice.setInputData) reslice.setInputData(null);
    if (reslice.setInputConnection) reslice.setInputConnection(null);

    reslice.delete?.();
  } catch (err) {
    console.warn("disposeImageReslice failed:", err);
  }
}

function disposeActor(
  obj: vtkActor | vtkVolume | vtkImageSlice,
  renderer?: any,
) {
  if (!obj) return;

  try {
    // 1. renderer에서 detach
    if (renderer) {
      if (renderer.removeActor && obj.isA?.("vtkActor")) {
        renderer.removeActor(obj);
      }
      if (renderer.removeVolume && obj.isA?.("vtkVolume")) {
        renderer.removeVolume(obj);
      }
      if (renderer.removeViewProp && obj.isA?.("vtkImageSlice")) {
        renderer.removeViewProp(obj);
      }
    }

    // 2. mapper 정리
    const mapper = obj.getMapper?.();
    if (mapper) {
      // 연결 해제
      if (mapper.setInputData) {
        mapper.setInputData(null);
      }
      // setInputConnection(null)은 타입상 안전하지 않음
      // 필요하다면 강제로 undefined 처리:
      if (mapper.getInputConnection && mapper.setInputConnection) {
        if (mapper.getInputConnection()) {
          mapper.setInputConnection(undefined as any); // 연결 해제
        }
      }

      // 입력 데이터 정리
      const input = mapper.getInputData?.();
      if (input && typeof input.delete === "function") {
        const pd = input.getPointData?.();
        const cd = input.getCellData?.();

        [pd, cd].forEach((data) => {
          data?.getArrays?.().forEach((arr: any) => {
            arr.setData?.(new Uint8Array(0), 1); // clear values
            arr.delete?.();
          });
        });

        input.delete();
      }

      mapper.delete?.();
    }

    // 3. 최종 actor 삭제
    obj.delete?.();
  } catch (err) {
    console.warn("disposeActor failed:", err);
  }
}

function cleanPool(
  pool: BufferPoolItem,
  keys: CPRBufferKeys[],
  deleteMode: "excludeKeys" | "includeKeys",
) {
  const keySet = new Set(keys);
  Object.entries(pool).forEach(([key, value]) => {
    const isInKeys = keySet.has(key as CPRBufferKeys);
    const shouldDelete =
      deleteMode === "includeKeys"
        ? isInKeys // keys 포함 → 삭제
        : !isInKeys; // keys 제외 → 삭제
    if (shouldDelete) {
      disposeBufferPoolItem(value);
    }
  });
}

function clearBufferPool(
  bufferPool: BufferPool,
  keepSeriesId?: string,
): BufferPool {
  const newPool: BufferPool = {};

  clearServerCprFrontDataRuntimeCache(keepSeriesId);
  clearServerImageFrontDataCacheExceptSeries(keepSeriesId);

  console.log(bufferPool);
  //아니, 다날려.
  Object.entries(bufferPool).forEach(([seriesId, item]) => {
    // if (keepSeriesId === seriesId) {
    // newPool[seriesId] = item;
    // } else {
    Object.entries(item).forEach(([key, value]) => {
      disposeBufferPoolItem(value);
    });
    // }
  });

  console.log("bufferPool:", newPool);
  return newPool;
}

function clearContextPool(contextPool: ContextPool): ContextPool {
  let newPool: ContextPool = contextPool;
  Object.entries(newPool).forEach(([key, context]) => {
    if (!context) return;
    const { actorIds, rendererId, viewportId, renderer, overlayRenderer } =
      context as Context;

    // 액터를 renderer에서 먼저 분리한 뒤 정리해야 다음 기능 진입 시
    // render pass가 삭제된 mapper/actor를 다시 그리며 WebGL shader 에러를
    // 내는 상황을 피할 수 있다.
    if ((context as Context).serverFrontDataVtkMode) {
      Object.entries(actorIds).forEach(([, value]) => {
        releaseServerFrontDataViewPropGraphicsResources(
          value,
          (context as Context).renderWindow,
        );
      });
      removeAllServerFrontDataRendererViewProps(
        renderer,
        (context as Context).renderWindow,
      );
      removeAllServerFrontDataRendererViewProps(
        overlayRenderer,
        (context as Context).renderWindow,
      );
    } else {
      Object.entries(actorIds).forEach(([, value]) => {
        disposeActor(value, renderer);
      });
      renderer?.removeAllActors?.();
      renderer?.removeAllVolumes?.();
      overlayRenderer?.removeAllActors?.();
      overlayRenderer?.removeAllVolumes?.();
    }
    //리슬라이스는?
    //몰라

    // 꺠끗한녀석
    const refreshContext = getPartialContext(rendererId, viewportId);

    // 컨텍스트별 뭘 냄겨야하는지 몰라서 그냥패스
    newPool = {
      ...newPool,
      [key as ContextKey]: {
        ...context,
        ...refreshContext,
      },
    };
  });

  console.log("contextPool:", newPool);
  return newPool;
}

function clearConfingData() {
  //SeriesId 바뀔때 초기화 해야되는거
  //아마 일시적인 변수거나, 저장하지않는거
  const segmentationLabel = configStore.get().segmentationLabel;
  segmentationLabel.clear();
  configStore.setData({ segmentationLabel, EditMode: null });
}

export type ContextPool = Record<
  ContextKey,
  CPRContext | CrossSectionContext | VRContext | SurfaceContext | null
>;
export const BufferPoolContext = React.createContext<{
  bufferPool: BufferPool;
  contextPool: ContextPool;
  setBufferPool: React.Dispatch<React.SetStateAction<BufferPool>>;
  setContextPool: React.Dispatch<React.SetStateAction<ContextPool>>;
} | null>(null);

export function useBufferPool() {
  const ctx = useContext(BufferPoolContext);
  if (!ctx)
    throw new Error("useBufferPool must be used within a BufferPoolProvider");

  const { bufferPool, contextPool, setBufferPool, setContextPool } = ctx;
  const clear = React.useCallback(
    (keepSeriesId?: string) => {
      console.log(keepSeriesId);
      setBufferPool((prev) => clearBufferPool(prev, keepSeriesId));
      setContextPool((prev) => clearContextPool(prev));
      clearCornerstoneResource(keepSeriesId);
      clearConfingData();
    },
    [setBufferPool],
  );

  const deletePoolItems = React.useCallback(
    (
      selectedSeriesId: string,
      keys: CPRBufferKeys[],
      deleteMode: 'includeKeys' | 'excludeKeys',
    ) => {
      if (keys.length === 0) {
        setBufferPool((prev) => clearBufferPool(prev, selectedSeriesId));
      } else {
        setBufferPool((prev) => {
          const prevSeries = prev[selectedSeriesId] ?? {};
          const updatedSeries = { ...prevSeries };
          cleanPool(updatedSeries, keys, deleteMode);
          return {
            ...prev,
            [selectedSeriesId]: updatedSeries,
          };
        });
      }
      clearConfingData();
      //setContextPool((prev) => clearContextPool(prev));
    },
    [setBufferPool],
  );

  return {
    bufferPool,
    contextPool,
    setBufferPool,
    setContextPool,
    clearPool: clear, // 얘는 전체
    deletePoolItems, // includeKeys 일때 내가 원하는거만 pool제거  excludeKeys 원하는거만 제외하고 나머지 삭제
  };
}
const enum MouseButton {
  Left = 0,
  Middle = 1,
  Right = 2,
  LeftAndRight = Left | Right,
}

const enum MouseButtons {
  Left = 1,
  Right = 2,
  Middle = 4,
  LeftAndRight = Left | Right,
}

const FFR_RIGHT_CLICK_GAP_PX = 6;

interface DragState {
  isDragging: boolean;
  dragIndex: MouseButton | -1;
  actorPickState?: {
    pickId: string;
    cellId: number;
    aorticPrevMousePosition?: [number, number];
    aorticAnchorPoint?: [number, number, number];
    aorticAnchorNormal?: [number, number, number];
    pickdownPoint?: [number, number, number];
  };
}

const sharedContext = (() => {
  const oriHUData = vtkImageData.newInstance();
  const oriLabelData = vtkImageData.newInstance();
  return { oriHUData, oriLabelData };
})();
export type SharedContext = typeof sharedContext;

type ManualCprLabelSyncEntry = {
  rendererId: string;
  branchId?: number;
  labelData: vtkImageData;
  baselineScalars?: Uint8Array | Int16Array | Uint16Array;
};

const manualCprLabelSyncMap = new Map<string, ManualCprLabelSyncEntry>();

const cloneCprLabelScalars = (
  scalars?: Uint8Array | Int16Array | Uint16Array,
) => {
  if (!scalars) return undefined;
  if (scalars instanceof Int16Array) return new Int16Array(scalars);
  if (scalars instanceof Uint16Array) return new Uint16Array(scalars);
  return new Uint8Array(scalars);
};

export function registerManualCprLabelSyncTarget(
  params: ManualCprLabelSyncEntry,
) {
  if (!params.rendererId || !params.labelData) return;

  const scalars = getCprLabelScalars(params.labelData);
  const previous = manualCprLabelSyncMap.get(params.rendererId);
  const canReuseBaseline =
    previous?.labelData === params.labelData &&
    previous.baselineScalars &&
    scalars &&
    previous.baselineScalars.length === scalars.length;

  manualCprLabelSyncMap.set(params.rendererId, {
    ...params,
    baselineScalars: canReuseBaseline
      ? previous.baselineScalars
      : cloneCprLabelScalars(scalars),
  });
}

export function unregisterManualCprLabelSyncTarget(rendererId: string) {
  if (!rendererId) return;
  manualCprLabelSyncMap.delete(rendererId);
}

const getCprLabelScalars = (labelData: vtkImageData) => {
  return labelData?.getPointData?.()?.getScalars?.()?.getData?.() as
    | Uint8Array
    | Int16Array
    | Uint16Array
    | undefined;
};

export function applyManualLumenStrokeToCprLabelData(params: {
  addedIndices?: Uint32Array;
  removedIndices?: Uint32Array;
}) {
  if (!manualCprLabelSyncMap.size) return;

  const added = params.addedIndices ?? new Uint32Array();
  const removed = params.removedIndices ?? new Uint32Array();
  if (!added.length && !removed.length) return;

  manualCprLabelSyncMap.forEach(({ rendererId, labelData }) => {
    const scalars = getCprLabelScalars(labelData);
    if (!scalars) return;

    let changed = false;

    for (const idx of removed) {
      if (idx < 0 || idx >= scalars.length) continue;
      if (scalars[idx] === 0) continue;
      scalars[idx] = 0;
      changed = true;
    }

    for (const idx of added) {
      if (idx < 0 || idx >= scalars.length) continue;
      if (scalars[idx] === 255) continue;
      scalars[idx] = 255;
      changed = true;
    }

    if (!changed) return;

    labelData.getPointData?.().getScalars?.().modified?.();
    labelData.modified?.();
    scheduleRender(rendererId, { type: "updateRenderWindow" });
  });
}

export function resetManualCprLabelSyncTargetsToBaseline() {
  if (!manualCprLabelSyncMap.size) return;

  manualCprLabelSyncMap.forEach(
    ({ rendererId, labelData, baselineScalars }) => {
      const scalars = getCprLabelScalars(labelData);
      if (!scalars || !baselineScalars) return;
      if (scalars.length !== baselineScalars.length) return;

      let changed = false;
      for (let idx = 0; idx < scalars.length; idx += 1) {
        if (scalars[idx] !== baselineScalars[idx]) {
          changed = true;
          break;
        }
      }

      if (!changed) return;

      scalars.set(baselineScalars as any);
      labelData.getPointData?.().getScalars?.().modified?.();
      labelData.modified?.();
      scheduleRender(rendererId, { type: "updateRenderWindow" });
    },
  );
}

export interface Context {
  genericRenderWindow: vtkGenericRenderWindow;
  renderWindow: vtkRenderWindow;
  renderer: vtkRenderer;
  overlayRenderer?: vtkRenderer;
  canvas: HTMLCanvasElement;
  viewportId: PanelComponentKeyType;
  viewport?: Types.IVolumeViewport;

  sharedContext: SharedContext;
  // axesActorsRef?: vtkActor[];

  rendererId: string;

  // pickPosition?: Vector3;//{ x: number; y: number; z: number };

  savedTranslation: Vector3;
  startPosition: Vector3;
  previousPosition: Vector3;
  lastMousePosition: [number, number] | null;
  dragState: DragState;

  orientationWidget?: vtkOrientationMarkerWidget;

  actorIds: Actors;

  focus?: Vector3;
  axes: {
    x: Vector3;
    y: Vector3;
    z: Vector3;
  };
  axesPicker?: vtkCellPicker;
  width?: number;
  height?: number;
  spacing?: Vector3;
  zoom: number;

  markerline_visibility?: boolean;
  windowLevelDragPolicy?: ImageWindowingPolicy;

  isAxis3DVisible: boolean;

  colorPoints?: Array<[number, number, number, number]>;
  opacityPoints?: Array<[number, number]>;
  selectedBranchNum?: number;
  svgref?: React.RefObject<SVGSVGElement>;

  /**
   * Guard flags used only in server CPR/front_data image mode.
   * Legacy VTK mode must not depend on these values.
   */
  serverFrontDataVtkMode?: boolean;
  activeAnalysisType?: AnalysisType;
  activeSeriesId?: string;
  serverFrontDataRenderBlocked?: boolean;
  serverFrontDataVtkDisposed?: boolean;
}
export interface SurfaceContext extends Context {
  //cfunc?: vtkColorTransferFunction;
  normal?: Vector3;
  actorsCenter?: Vector3;
  valueLAWT: number;
  measurementHistory?: {
    undoStack: Vector3[][][];
    redoStack: Vector3[][][];
  };
  // program: WebGLProgram;
}
export interface VRContext extends Context {
  cellPicker?: vtkCellPicker;
  normal?: Vector3;
  segHuMax: number;
  segHuMin: number;
  vtkVR: boolean; //vtk로 사용하겠다
  centerX: number;
  actorsCenter?: Vector3;
  savedQuality?: RenderingQualityKeyType;
  efId?: string;
  measurementHistory?: {
    undoStack: Vector3[][][];
    redoStack: Vector3[][][];
  };
  // program: WebGLProgram
}

export interface CPRContext extends Context {
  imageData?: vtkImageData;
  labelData?: vtkImageData;
  centerlineData?: vtkPolyData;
  raf: number | null;
  angle: number;
  axis?: vec3;
  overlayTranslation?: [number, number];
  moveCountY: number | null;
  path: { x: number; y: number }[];
  // path: { x: number; y: number }[];
  mode?: "stretched" | "straightened";
  //paths: Record<'straightened' | 'stretched', { x: number; y: number }[]>;
}

export interface CrossSectionContext extends Context {
  prevIndex?: vec3;
  imageData?: vtkImageData;
  labelData?: vtkImageData;
  centerlineData?: vtkPolyData;
  CSReslice?: vtkImageReslice;
  CSPaintReslice?: vtkImageReslice;
  raf: number | null;
  index?: number;
  /**
   * Server front-data cross section only:
   * fitCrossSectionCameraToImageCenter() should run on create/init only,
   * not during marker/index updates.
   */
  serverFrontDataCameraFitOnInit?: boolean;
}

// export const initSurfaceContext : SurfaceContext = {
//   genericRenderWindow:vtkGenericRenderWindow.newInstance(),
// }

const getScreenPosition = (
  canvas: HTMLCanvasElement,
  clientX: number,
  clientY: number,
): Vector2 => {
  // if (!context.current) return { x: 0, y: 0, z: 0 };
  // const { canvas } = context.current;
  // if (!canvas) return { x: 0, y: 0, z: 0 };
  const bounds = canvas.getBoundingClientRect();
  const scaleX = canvas.width / bounds.width;
  const scaleY = canvas.height / bounds.height;
  const x = scaleX * (clientX - bounds.left);
  const y = scaleY * (bounds.height - clientY + bounds.top);
  const pixelX = Math.floor(x * (bounds.width / bounds!.width));
  const pixelY = Math.floor(y * (bounds.height / bounds!.height));
  return [pixelX, pixelY];
};

// 사전에 반드시 cellPicker검사
const pickingTestVR = (
  context: React.MutableRefObject<VRContext | null>, //다른 context가
  pos: Vector2,
) => {
  if (!context.current) return;
  const { renderer, viewport, cellPicker, actorIds } = context.current;
  if (!cellPicker) return;
  const point = [pos[0], pos[1], 0.0];
  cellPicker.pick(point, renderer);
  const actors = cellPicker.getActors();

  if (actors.length === 0) {
    return; //cellPicker.getPickPosition();
  }

  const pickedPoints = cellPicker.getPickedPositions();

  if (pickedPoints.length === 0) return;
  console.log("Picked:", pickedPoints);
  return pickedPoints[0];
};

// Picker 사용안함.
// 여기도 차라리 selector를 만들어두는게??
const pickingTestSurface = (
  context: React.MutableRefObject<Context | null>,
  pos: Vector2,
  preferOverlay = false,
) => {
  if (!context.current) return;
  const { renderer, renderWindow, overlayRenderer } = context.current;
  const [x, y] = pos;
  const selector = vtkOpenGLHardwareSelector.newInstance({
    captureZValues: true,
    fieldAssociation: FieldAssociations.FIELD_ASSOCIATION_POINTS,
  });
  const openGLRenderWindow: vtkOpenGLRenderWindow = renderWindow.getViews()[0];

  // const gl = canvas.getContext('webgl2');
  // const [width, height] = openGLRenderWindow.getSize();
  // const flipY = height - y;

  // const depth = new Float32Array(4);
  // // const gl = openGLRenderWindow.get3DContext({depth:true});
  // gl?.readPixels(x, flipY, 1, 1, gl.DEPTH_COMPONENT, gl.FLOAT, depth);

  // console.log(renderer.getUseShadows(),depth);

  const renderers =
    preferOverlay && overlayRenderer
      ? [overlayRenderer]
      : [renderer, overlayRenderer].filter(Boolean);

  for (const currentRenderer of renderers) {
    const hit = getPickInfo(
      selector,
      openGLRenderWindow,
      currentRenderer!,
      pos,
    );

    if (hit) {
      return {
        ...hit,
        renderer: currentRenderer,
      };
    }
  }

  return undefined;
};

function getPickInfo(
  selector: vtkOpenGLHardwareSelector,
  openGLRenderWindow: vtkOpenGLRenderWindow,
  renderer: vtkRenderer,
  pos: Vector2,
  areaSize = 0.5,
) {
  const [x, y] = pos;

  // Attach selector
  selector.attach(openGLRenderWindow, renderer);

  // Picking area 설정
  selector.setArea(x - areaSize, y - areaSize, x + areaSize, y + areaSize);

  // Capture GPU buffers
  if (!selector.captureBuffers()) return undefined;

  // 픽셀 정보 얻기
  const info = selector.getPixelInformation(pos, 1, [0, 0]);
  if (!info || !info.prop) return undefined;

  // World 좌표 변환
  const worldPosition = openGLRenderWindow.displayToWorld(
    info.displayPosition[0],
    info.displayPosition[1],
    info.zValue,
    renderer,
  );

  return {
    // worldPosition: worldPosition.map(Math.round) as Vector3,
    worldPosition,
    actor: info.prop as vtkActor,
    cellId: info.attributeID,
  };
}

export const pickFromWorld = (
  context: React.MutableRefObject<SurfaceContext | null>,
  world: Vector3,
) => {
  if (!context.current) return;
  const { renderer, renderWindow } = context.current;
  const selector = vtkOpenGLHardwareSelector.newInstance({
    captureZValues: true,
    fieldAssociation: FieldAssociations.FIELD_ASSOCIATION_POINTS_THEN_CELLS,
  });
  const openGLRenderWindow: vtkOpenGLRenderWindow = renderWindow.getViews()[0];

  // world → display
  const display = openGLRenderWindow.worldToDisplay(
    world[0],
    world[1],
    world[2],
    renderer,
  );

  const pos = display.map((v: number) => Math.round(v)) as Vector3;

  const [x, y] = [pos[0], pos[1]];
  // const gl = canvas.getContext('webgl2');
  // const [width, height] = openGLRenderWindow.getSize();
  // const flipY = height - y;

  // const depth = new Float32Array(4);
  // gl?.readPixels(x, flipY, 1, 4, gl.DEPTH_COMPONENT, gl.FLOAT, depth);
  selector.attach(openGLRenderWindow, renderer);
  selector.setArea(x - 0.5, y - 0.5, x + 0.5, y + 0.5);

  if (selector.captureBuffers()) {
    const info = selector.getPixelInformation([x, y], 1, [0, 0]);
    if (info == null || info.prop == null) return;
    const worldPosition = openGLRenderWindow.displayToWorld(
      info.displayPosition[0],
      info.displayPosition[1],
      info.zValue,
      renderer,
    );
    console.log(info);
    return {
      worldPosition: worldPosition.map((v: number) => Math.round(v)) as Vector3,
      actor: info.prop as vtkActor,
      cellId: info.attributeID,
    };
  }
};

const rotate = async (
  context: React.MutableRefObject<Context | null>,
  currPos: Vector2,
) => {
  if (!context.current) return;
  if (context.current.dragState.dragIndex !== MouseButton.Left) return;
  const { renderer, renderWindow, rendererId } = context.current;
  // const renderer = genericRenderWindow.getRenderer();
  // const renderWindow = genericRenderWindow.getRenderWindow();

  if(!renderWindow) return;
  let move = context.current.savedTranslation;
  if (isNaN(move[0]) || isNaN(move[1]) || isNaN(move[2])) {
    move = [0, 0, 0];
  }

  const prevPos = context.current.previousPosition;

  const camera = renderer.getActiveCamera();
  const cameraPos = camera.getPosition();
  const cameraFp = camera.getFocalPoint();
  const viewUp = camera.getViewUp();
  const size = renderWindow.getInteractor().getView().getViewportSize(renderer);
  if (!size) return;

  const minSize = Math.min(size[0], size[1]);

  const dx = prevPos[0] - currPos[0];
  const dy = prevPos[1] - currPos[1];

  let center: vec3;
  if (move[0] === 0 && move[1] === 0 && move[2] === 0) {
    center = vec3.fromValues(cameraFp[0], cameraFp[1], cameraFp[2]);
  } else {
    center = vec3.fromValues(move[0], move[1], move[2]);
  }

  const trans = mat4.create();
  const newCamPos = vec3.create();
  const newFp = vec3.create();

  const azimuthAngle = vtkMath.radiansFromDegrees((360 * dx) / minSize);
  const elevationAngle = vtkMath.radiansFromDegrees((360 * -dy) / minSize);

  // Azimuth: 좌우 회전
  const newViewUp = vec3.create();
  mat4.translate(trans, trans, center);
  mat4.rotate(trans, trans, azimuthAngle, viewUp);
  // Elevation: 상하 회전
  const sideAxis = vec3.create();
  const dop = camera.getDirectionOfProjection();
  vec3.cross(sideAxis, dop, viewUp); // 측면 축 (카메라 오른쪽 방향)
  vec3.normalize(sideAxis, sideAxis);

  mat4.rotate(trans, trans, elevationAngle, sideAxis);
  mat4.translate(trans, trans, [-center[0], -center[1], -center[2]]);

  vec3.transformMat4(newCamPos, cameraPos, trans);
  vec3.transformMat4(newFp, cameraFp, trans);
  const direction = vec3.add(vec3.create(), viewUp, cameraPos);
  vec3.transformMat4(newViewUp, direction, trans);

  camera.setPosition(newCamPos[0], newCamPos[1], newCamPos[2]);
  camera.setFocalPoint(newFp[0], newFp[1], newFp[2]);
  camera.setViewUp(
    newViewUp[0] - newCamPos[0],
    newViewUp[1] - newCamPos[1],
    newViewUp[2] - newCamPos[2],
  );

  const bounds = renderer.computeVisiblePropBounds();
  // [xmin, xmax, ymin, ymax, zmin, zmax]

  const pts = [
    [bounds[0], bounds[2], bounds[4]],
    [bounds[1], bounds[2], bounds[4]],
    [bounds[0], bounds[3], bounds[4]],
    [bounds[1], bounds[3], bounds[4]],
    [bounds[0], bounds[2], bounds[5]],
    [bounds[1], bounds[2], bounds[5]],
    [bounds[0], bounds[3], bounds[5]],
    [bounds[1], bounds[3], bounds[5]],
  ];

  const pos = vec3.clone(newCamPos);
  const fp = vec3.clone(newFp);

  const viewDir = vec3.create();
  vec3.sub(viewDir, fp, pos);
  vec3.normalize(viewDir, viewDir);

  let minD = Infinity;
  let maxD = -Infinity;

  for (const p of pts) {
    const v = vec3.sub(vec3.create(), p as any, pos);
    const d = vec3.dot(v, viewDir);
    minD = Math.min(minD, d);
    maxD = Math.max(maxD, d);
  }

  const SAFE_NEAR = 0.1;

  if (minD < SAFE_NEAR) {
    const pushBack = SAFE_NEAR - minD;

    // 🔑 각도 유지 + 카메라만 뒤로
    vec3.scaleAndAdd(pos, pos, viewDir, -pushBack);
    vec3.scaleAndAdd(fp, fp, viewDir, -pushBack);

    camera.setPosition(pos[0], pos[1], pos[2]);
    camera.setFocalPoint(fp[0], fp[1], fp[2]);

    minD += pushBack;
    maxD += pushBack;
  }

  renderer.updateLightsGeometryToFollowCamera();
  scheduleRender(rendererId, { type: "updateRenderWindow" });

  context.current.previousPosition = [currPos[0], currPos[1], 0];
};

//맵퍼 조절해보기
function changeQuality(params: { context: VRContext; rollback?: boolean }) {
  const { context, rollback } = params;
  const renderingQuality =
    configStore.get()?.generalSetting?.renderingQuality ?? "high";

  const targetQuality: RenderingQualityKeyType = rollback
    ? renderingQuality
    : "low";

  context.renderer.getVolumes().forEach((volume) => {
    const mapper = volume.getMapper();
    if (!mapper) return;
    const imageData = mapper.getInputData() as vtkImageData;
    if (!imageData) return;

    const dimensions = imageData.getDimensions();
    const spacing = imageData.getSpacing();

    adjustSampleQuality({
      mapper,
      dimensions,
      spacing,
      quality: targetQuality,
    });
  });
}

export const handleMouseDown = async (
  context: React.MutableRefObject<Context | null>,
  evt: MouseEvent,
  analysisType?: AnalysisType,
) => {
  if (!context.current) return;
  //console.log("다운 다운 다운 다운 다운")
  const { viewportId } = context.current;
  let actorPickState: DragState["actorPickState"];
  const { EditMode, manual3DTool } = configStore.get();
  if (
    evt.button === MouseButton.Right &&
    analysisType === "FFR" &&
    (viewportId === "Surface3D" || viewportId === "VR")
  ) {
    const [x, y] = getScreenPosition(
      context.current.canvas,
      evt.clientX,
      evt.clientY,
    );
    context.current.startPosition = [x, y, 0];
  }

  if (evt.button === MouseButton.Left) {
    const [x, y] = getScreenPosition(
      context.current.canvas,
      evt.clientX,
      evt.clientY,
    );
    context.current.startPosition = [x, y, 0];
    context.current.previousPosition = [x, y, 0];
    if (viewportId === 'Surface3D' || viewportId === 'VR') {
      if (isToolInteracting()) {
        return;
      }
      if (analysisType === 'tavi') {
        const hit = pickingTestSurface(context, [x, y]);
        if (hit) {
          const pickId = Object.keys(context.current.actorIds).find(
            (key) => context.current!.actorIds[key] === hit.actor,
          );
          if (pickId) {
            const cellId = typeof hit.cellId === 'number' ? hit.cellId : -1;
            const circleMeta = getCircleMeta(hit.actor as vtkActor);
            const anchorNormal = circleMeta?.normal;
            actorPickState = {
              pickId,
              cellId,
              aorticPrevMousePosition: [evt.clientX, evt.clientY],
              aorticAnchorNormal: anchorNormal
                ? [anchorNormal[0], anchorNormal[1], anchorNormal[2]]
                : undefined,
            };
            if (context.current.rendererId === 'VR') {
              changeQuality({ context: context.current as VRContext });
            }
            context.current.dragState = {
              isDragging: true,
              dragIndex: evt.button,
              actorPickState,
            };
            return;
          }
        }
      } else if (
        analysisType === 'FFR' &&
        EditMode === 'lumen' &&
        manual3DTool.initialPoint
      ) {
        const hit = pickingTestSurface(context, [x, y], true);
        if (hit) {
          const pickId = Object.keys(context.current.actorIds).find(
            (key) => context.current!.actorIds[key] === hit.actor,
          );
          if (pickId) {
            const cellId = typeof hit.cellId === 'number' ? hit.cellId : -1;
            actorPickState = {
              pickId,
              cellId,
              pickdownPoint:hit.worldPosition,
            };
          }
        }
      }
    }
    //actorPickState = getMeasurementDragState(context, [x, y]);

    //맵퍼 조절해보기
    if (context.current.rendererId === 'VR') {
      changeQuality({ context: context.current as VRContext });
      // context.current.renderer.getVolumes().forEach(volume=>{
      //   const mapper = volume.getMapper();
      //   if(!mapper) return;
      //   const dimensions = (mapper.getInputData() as vtkImageData).getDimensions();
      //   const spacing = (mapper.getInputData() as vtkImageData).getSpacing();
      //   console.log(dimensions,spacing)
      //   const prevMaxPerRay = mapper.getMaximumSamplesPerRay();
      //   if(prevMaxPerRay === 2048) (context.current as VRContext).savedQuality = 'high';
      //   else if(prevMaxPerRay === 1024) (context.current as VRContext).savedQuality = 'medium';
      //   else  (context.current as VRContext).savedQuality = 'low';
      //   adjustSampleQuality({
      //     mapper,
      //     dimensions,
      //     spacing,
      //     quality:'low'
      //   })
      // })
    }
  }
  context.current.windowLevelDragPolicy = undefined;
  context.current.dragState = {
    isDragging: true,
    dragIndex: evt.button,
    actorPickState,
  };
  context.current.lastMousePosition = [evt.x, evt.y];
};

export const handleMouseUp = async (
  context: React.MutableRefObject<Context | null>,
  evt: MouseEvent,
  analysisType?: AnalysisType,
  selectedSeriesId?: string,
) => {
  if (!context.current) return;
  //console.log("업 업 업")

  const { EditMode, manual3DTool } = configStore.get();
  if (evt.button === MouseButton.Left) {
    const { viewport, viewportId, canvas, actorIds, renderer, dragState } =
      context.current;

    if (viewportId === 'VR' || viewportId === 'Surface3D') {
      const [x, y] = getScreenPosition(canvas, evt.clientX, evt.clientY);
      const distance = vtkMath.distance2BetweenPoints(
        context.current.startPosition,
        [x, y, 0],
      );
      if (context.current.rendererId === 'VR') {
        changeQuality({
          context: context.current as VRContext,
          rollback: true,
        });
        scheduleRender(context.current.rendererId, {
          type: 'updateRenderWindow',
        });
      }
      if (
        analysisType === 'tavi' &&
        selectedSeriesId &&
        dragState.actorPickState?.pickId
      ) {
        const pickId = dragState.actorPickState.pickId;
        const actor = actorIds[pickId] as vtkActor | undefined;
        const meta = actor ? getCircleMeta(actor) : undefined;
        const taviCenterKey = getTaviCenterKeyFromActorKey(pickId);
        const { imageData } =
          getVolumeFromSeriesId(
            selectedSeriesId,
            configStore.get().generalSetting.renderingQuality !== 'high',
          ) ?? {};

        if (
          meta?.center &&
          meta?.normal &&
          meta?.basis &&
          taviCenterKey &&
          imageData
        ) {
          const nextPlane = rotateTaviBasisToNormal(
            meta.basis,
            vec3.fromValues(meta.normal[0], meta.normal[1], meta.normal[2]),
            imageData,
          );

          if (nextPlane) {
            configStore.setData((prev) => ({
              taviPlaneOverrides: {
                ...prev.taviPlaneOverrides,
                [taviCenterKey]: nextPlane,
              },
            }));
            taviNodeRenderer(
              context as React.MutableRefObject<
                SurfaceContext | VRContext | null
              >,
              meta.center,
              meta.normal,
              pickId,
              nextPlane,
            );
          }
        }
      } else if (
        analysisType === 'FFR' &&
        dragState.actorPickState?.pickId &&
        dragState.actorPickState?.pickdownPoint &&
        EditMode === 'lumen' &&
        manual3DTool.initialPoint
      ) {
        const res = pickingTestSurface(context, [x, y]);
        if (res) {
          moveInitialPointSphereIfInside(
            context.current,
            res.worldPosition,
            dragState.actorPickState?.pickId,
            viewportId,
          );

          context.current.dragState = {
            isDragging: false,
            dragIndex: -1,
            actorPickState: undefined,
          };
          context.current.lastMousePosition = null;
          return;
        }
      }
      if (distance < 2) {
        // clearAxesLinesAt({context});
        let flipWorldPos: Vector3 | undefined;
        let worldPosition: Vector3 | undefined;

        if (isToolInteracting()) {
          context.current.dragState = {
            isDragging: false,
            dragIndex: -1,
            actorPickState: undefined,
          };
          return;
        }

        if (viewportId === 'VR') {
          worldPosition = pickingTestVR(
            context as React.MutableRefObject<VRContext | null>,
            [x, y],
          ) as Vector3;

          if (worldPosition) {
            const world = [
              -worldPosition[0],
              worldPosition[1],
              worldPosition[2],
            ];
            scheduleRender('VR', {
              type: 'focus',
              world,
              mode: 'mouse',
            });

            scheduleRender('Axial', { type: 'focus', world, mode: 'mouse' });
            scheduleRender('Coronal', {
              type: 'focus',
              world,
              mode: 'mouse',
            });
            scheduleRender('Sagittal', {
              type: 'focus',
              world,
              mode: 'mouse',
            });
          }
        } else if (viewportId === 'Surface3D') {
          const res = pickingTestSurface(context, [x, y]);
          if (res) {
            const key = Object.keys(actorIds).find(
              (k) => actorIds[k] === res.actor,
            );
            const world = [
              -res.worldPosition[0],
              res.worldPosition[1],
              res.worldPosition[2],
            ];
            if (
              (key && !key.includes('node')) ||
              (analysisType === 'FFR' && configStore.get().EditMode)
            ) {
              scheduleRender('Surface3D', {
                type: 'focus',
                world,
                pickId: key,
                mode: 'mouse',
                cellId: res.cellId,
              });
              scheduleRender('Axial', {
                type: 'focus',
                world,
                mode: 'mouse',
              });
              scheduleRender('Coronal', {
                type: 'focus',
                world,
                mode: 'mouse',
              });
              scheduleRender('Sagittal', {
                type: 'focus',
                world,
                mode: 'mouse',
              });
            }
          }else if(EditMode){
            scheduleRender('Surface3D', {
              type: 'FFR_Manual_3D',
              mode:'Eraser',
            });
          }
        }
      }
    }
  } else if (analysisType === 'FFR' && evt.button === MouseButton.Right) {
    const [x, y] = getScreenPosition(
      context.current.canvas,
      evt.clientX,
      evt.clientY,
    );
    const moveDistance = vtkMath.distance2BetweenPoints(
      context.current.startPosition,
      [x, y, 0],
    );
    const movedAsPan =
      moveDistance > FFR_RIGHT_CLICK_GAP_PX * FFR_RIGHT_CLICK_GAP_PX;
    if (movedAsPan) {

      context.current.dragState = {
        isDragging: false,
        dragIndex: -1,
        actorPickState: undefined,
      };
      context.current.lastMousePosition = null;

      if(EditMode){
        scheduleRender('Surface3D', {
          type: 'FFR_Manual_3D',
          mode:'Eraser',
        });
      }
      return;
    }

    const res = pickingTestSurface(context, [x, y]);
    if (res) {
      const pickId = Object.keys(context.current.actorIds).find(
        (key) => context.current!.actorIds[key] === res.actor,
      );
      if (pickId) {
        const cellId = typeof res.cellId === 'number' ? res.cellId : -1;
        const world = [
          -res.worldPosition[0],
          res.worldPosition[1],
          res.worldPosition[2],
        ];
        scheduleRender('Surface3D', {
          type: 'focus',
          world,
          pickId,
          mode: 'mouse',
          buttonType: 'Right',
          cellId: res.cellId,
        });
      }
    } else {
       const payload = EditMode
         ? {
             type: 'FFR_Manual_3D' as const,
             mode: 'Eraser' as const,
           }
         : {
             type: 'focus' as const,
             mode: 'mouse' as const,
             buttonType: 'Right' as const,
           };

       scheduleRender('Surface3D', payload);
    }
  }
  context.current.windowLevelDragPolicy = undefined;
  context.current.dragState = {
    isDragging: false,
    dragIndex: -1,
    actorPickState: undefined,
  };
  context.current.lastMousePosition = null;
};

export const handleDragMove = (
  context: React.MutableRefObject<Context | null>,
  evt: MouseEvent,
  analysisType?: AnalysisType,
) => {
  if (!context.current) return false;
  const { viewportId, actorIds, dragState } = context.current;

  if (evt.buttons !== MouseButtons.Left) return false;
  if (!["Surface3D", "VR"].includes(viewportId)) return false;
  if (!dragState.actorPickState) return false;
  const draggingActorKey = dragState.actorPickState.pickId;

  const hasNode = draggingActorKey.includes("node"); //node 이름이 달린거만 움직여보쟈
  if (!hasNode) return false;
  const prevActor = actorIds[draggingActorKey] as vtkActor | undefined;
  if (!prevActor) return false;
  const meta = getCircleMeta(prevActor);
  const dragRenderer =
    analysisType === "tavi" && context.current.overlayRenderer
      ? context.current.overlayRenderer
      : context.current.renderer;

  const normal = meta?.normal;
  if (!meta?.normal || !meta?.center) return true;
  const newNormal = rotateAorticNormalByMouse(
    context,
    evt,
    meta.normal,
    meta.center as vec3,
    meta.radius,
    dragRenderer,
  );
  if (!newNormal) return true;

  if (analysisType === "aorticValveCalcium") {
    AorticNodeRenderer(
      context as React.MutableRefObject<SurfaceContext | null>,
      meta?.center,
      newNormal,
    );
  } else if (analysisType === "tavi") {
    taviNodeRenderer(
      context as React.MutableRefObject<SurfaceContext | VRContext | null>,
      meta?.center,
      newNormal,
      draggingActorKey,
    );
    //console.log(draggingActorKey, normal, newNormal);
  }
  return true;
};

function imageVectorToWorld(
  vector: [number, number, number],
  imageData: vtkImageData,
): vec3 | null {
  const spacing = imageData.getSpacing() as [number, number, number];
  const direction = imageData.getDirection() as mat3;
  const scaled = vec3.fromValues(
    vector[0] * spacing[0],
    vector[1] * spacing[1],
    vector[2] * spacing[2],
  );
  const world = vec3.create();
  vec3.transformMat3(world, scaled, direction);
  if (vec3.sqrLen(world) < 1e-12) return null;
  vec3.normalize(world, world);
  return world;
}

function worldVectorToImage(
  vector: vec3,
  imageData: vtkImageData,
): [number, number, number] | null {
  const spacing = imageData.getSpacing() as [number, number, number];
  const direction = imageData.getDirection() as mat3;
  const invDir =
    mat3.invert(mat3.create(), direction) ??
    mat3.transpose(mat3.create(), direction);

  const image = vec3.create();
  vec3.transformMat3(image, vector, invDir);
  image[0] /= spacing[0];
  image[1] /= spacing[1];
  image[2] /= spacing[2];
  if (vec3.sqrLen(image) < 1e-12) return null;
  vec3.normalize(image, image);
  return [image[0], image[1], image[2]];
}

function rotateTaviBasisToNormal(
  basis: TaviPlaneConfig,
  nextWorldNormal: vec3,
  imageData: vtkImageData,
): TaviPlaneConfig | null {
  const baseWorldNormal = imageVectorToWorld(basis.normal, imageData);
  const baseWorldDx = imageVectorToWorld(basis.dx, imageData);
  const baseWorldDy = imageVectorToWorld(basis.dy, imageData);
  if (!baseWorldNormal || !baseWorldDx || !baseWorldDy) return null;

  const targetNormal = vec3.normalize(vec3.create(), nextWorldNormal);
  const rotationAxis = vec3.cross(vec3.create(), baseWorldNormal, targetNormal);
  const axisLength = vec3.length(rotationAxis);
  const clampedDot = Math.max(
    -1,
    Math.min(1, vec3.dot(baseWorldNormal, targetNormal)),
  );

  const rotationMat = mat4.create();
  if (axisLength < 1e-6) {
    if (clampedDot < 0) {
      const fallbackAxis = vec3.cross(
        vec3.create(),
        baseWorldNormal,
        baseWorldDx,
      );
      if (vec3.length(fallbackAxis) < 1e-6) {
        vec3.cross(fallbackAxis, baseWorldNormal, baseWorldDy);
      }
      if (vec3.length(fallbackAxis) < 1e-6) return null;
      vec3.normalize(fallbackAxis, fallbackAxis);
      mat4.fromRotation(rotationMat, Math.PI, fallbackAxis);
    }
  } else {
    vec3.scale(rotationAxis, rotationAxis, 1 / axisLength);
    const angle = Math.atan2(axisLength, clampedDot);
    mat4.fromRotation(rotationMat, angle, rotationAxis);
  }

  const rotatedWorldDx = vec3.transformMat4(
    vec3.create(),
    baseWorldDx,
    rotationMat,
  );
  const rotatedWorldDy = vec3.transformMat4(
    vec3.create(),
    baseWorldDy,
    rotationMat,
  );
  vec3.normalize(rotatedWorldDx, rotatedWorldDx);
  vec3.normalize(rotatedWorldDy, rotatedWorldDy);

  const normal = worldVectorToImage(targetNormal, imageData);
  const dx = worldVectorToImage(rotatedWorldDx, imageData);
  const dy = worldVectorToImage(rotatedWorldDy, imageData);
  if (!normal || !dx || !dy) return null;

  return {
    center: [...basis.center] as [number, number, number],
    normal,
    dx,
    dy,
  };
}

export function rotateAorticNormalByMouse(
  context: React.MutableRefObject<Context | null>,
  evt: MouseEvent,
  normal: vec3,
  center: vec3,
  radius: number,
  rayRenderer?: vtkRenderer,
): Vector3 | undefined {
  const current = context.current;
  const actorPickState = current?.dragState.actorPickState;
  if (!current || !actorPickState) return;

  const startMouse = actorPickState.aorticPrevMousePosition;
  if (!startMouse || !Number.isFinite(radius) || radius <= 0) return;

  const openGLRenderWindow = current.renderWindow.getViews()[0] as
    | vtkOpenGLRenderWindow
    | undefined;
  if (!openGLRenderWindow) return;
  const activeRenderer = rayRenderer ?? current.renderer;

  const getSpherePointFromMouse = (
    clientX: number,
    clientY: number,
  ): vec3 | undefined => {
    const [displayX, displayY] = getScreenPosition(
      current.canvas,
      clientX,
      clientY,
    );
    const nearWorld = openGLRenderWindow.displayToWorld(
      displayX,
      displayY,
      0,
      activeRenderer,
    );
    const farWorld = openGLRenderWindow.displayToWorld(
      displayX,
      displayY,
      1,
      activeRenderer,
    );
    if (!nearWorld || !farWorld) return;

    const rayOrigin = vec3.fromValues(
      -nearWorld[0],
      nearWorld[1],
      nearWorld[2],
    );

    const rayDirection = vec3.fromValues(
      -(farWorld[0] - nearWorld[0]),
      farWorld[1] - nearWorld[1],
      farWorld[2] - nearWorld[2],
    );
    const rayLength = vec3.length(rayDirection);
    if (rayLength < 1e-6) return;
    vec3.scale(rayDirection, rayDirection, 1 / rayLength);

    const sphereCenter = vec3.fromValues(center[0], center[1], center[2]);
    const oc = vec3.sub(vec3.create(), rayOrigin, sphereCenter);
    const a = vec3.dot(rayDirection, rayDirection);
    const b = 2 * vec3.dot(oc, rayDirection);
    const c = vec3.dot(oc, oc) - radius * radius;
    const discriminant = b * b - 4 * a * c;

    if (!Number.isFinite(discriminant)) return;
    if (discriminant < 0) {
      const tClosest = Math.max(0, -vec3.dot(oc, rayDirection) / a);
      const closestPoint = vec3.add(
        vec3.create(),
        rayOrigin,
        vec3.scale(vec3.create(), rayDirection, tClosest),
      );
      const outward = vec3.sub(vec3.create(), closestPoint, sphereCenter);
      if (vec3.length(outward) < 1e-6) return;
      vec3.normalize(outward, outward);
      return vec3.add(
        vec3.create(),
        sphereCenter,
        vec3.scale(vec3.create(), outward, radius),
      );
    }

    const sqrtDiscriminant = Math.sqrt(discriminant);
    const t0 = (-b - sqrtDiscriminant) / (2 * a);
    const t1 = (-b + sqrtDiscriminant) / (2 * a);
    let t = Number.POSITIVE_INFINITY;
    if (t0 >= 0) t = t0;
    if (t1 >= 0 && t1 < t) t = t1;
    if (!Number.isFinite(t)) return;

    return vec3.add(
      vec3.create(),
      rayOrigin,
      vec3.scale(vec3.create(), rayDirection, t),
    );
  };

  if (!actorPickState.aorticAnchorPoint || !actorPickState.aorticAnchorNormal) {
    const anchorPoint = getSpherePointFromMouse(startMouse[0], startMouse[1]);
    if (!anchorPoint) return;
    const baseNormal = vec3.normalize(vec3.create(), normal as vec3);
    actorPickState.aorticAnchorPoint = [
      anchorPoint[0],
      anchorPoint[1],
      anchorPoint[2],
    ];
    actorPickState.aorticAnchorNormal = [
      baseNormal[0],
      baseNormal[1],
      baseNormal[2],
    ];
  }

  const anchorPoint = actorPickState.aorticAnchorPoint;
  const anchorNormal = actorPickState.aorticAnchorNormal;
  if (!anchorPoint || !anchorNormal) return;

  const currentPoint = getSpherePointFromMouse(evt.clientX, evt.clientY);
  if (!currentPoint) return;
  scheduleRender(current.viewportId, {
    type: "focus",
    world: [currentPoint[0], currentPoint[1], currentPoint[2]],
    mode: "mouse",
  });

  const sphereCenter = vec3.fromValues(center[0], center[1], center[2]);
  const fromVec = vec3.sub(
    vec3.create(),
    vec3.fromValues(anchorPoint[0], anchorPoint[1], anchorPoint[2]),
    sphereCenter,
  );
  const toVec = vec3.sub(vec3.create(), currentPoint, sphereCenter);
  if (vec3.length(fromVec) < 1e-6 || vec3.length(toVec) < 1e-6) return;

  vec3.normalize(fromVec, fromVec);
  vec3.normalize(toVec, toVec);

  const rotationAxis = vec3.cross(vec3.create(), fromVec, toVec);
  const axisLength = vec3.length(rotationAxis);
  const baseNormal = vec3.fromValues(
    anchorNormal[0],
    anchorNormal[1],
    anchorNormal[2],
  );
  vec3.normalize(baseNormal, baseNormal);

  if (axisLength < 1e-6) {
    return [baseNormal[0], baseNormal[1], baseNormal[2]] as Vector3;
  }

  vec3.scale(rotationAxis, rotationAxis, 1 / axisLength);
  const clampedDot = Math.max(-1, Math.min(1, vec3.dot(fromVec, toVec)));
  const rotationAngle = Math.atan2(axisLength, clampedDot);
  const rotationMat = mat4.create();
  mat4.fromRotation(rotationMat, rotationAngle, rotationAxis);

  const nextNormal = vec3.transformMat4(vec3.create(), baseNormal, rotationMat);
  vec3.normalize(nextNormal, nextNormal);

  return [nextNormal[0], nextNormal[1], nextNormal[2]] as Vector3;
}

export const handleMouseMove = async ({
  context,
  evt,
  toolName,
  analysisType,
  mainIndex,
  subIndex,
  indices,
  voi,
  updateWindowLevelWidth,
}: {
  context: React.MutableRefObject<Context | null>;
  evt: MouseEvent;
  toolName?: string;
  analysisType?: AnalysisType;
  mainIndex?: number;
  subIndex?: number;
  indices?: [number, number, number];
  voi?: VOI;
  updateWindowLevelWidth?: ([wl, ww]: [number, number]) => void;
}) => {
  if (!context.current) return;
  const { viewportId, renderer, orientationWidget, dragState } =
    context.current;

  const { EditMode, manual3DTool } = configStore.get();
   if (
    viewportId === 'Surface3D' &&
    analysisType === 'FFR' &&
    EditMode === 'lumen' &&
    evt.buttons === 0 &&
    manual3DTool.initialPoint
  ) {
    const [x, y] = getScreenPosition(
      context.current.canvas,
      evt.clientX,
      evt.clientY,
    );
    const hit = pickingTestSurface(context, [x, y], true);
    const pickId = hit
      ? Object.keys(context.current.actorIds).find(
          (key) => context.current!.actorIds[key] === hit.actor,
        )
      : undefined;

    setInitialPointSphereOpacity(context.current, pickId, viewportId);
    return;
  }

  if (!dragState.isDragging) return;

  if (evt.buttons === MouseButtons.LeftAndRight) {
    toolName = "Zoom";
  } else if (evt.buttons === MouseButtons.Right) {
    toolName = "Pan";
  } else if (evt.buttons === MouseButtons.Middle) {
    toolName = "WindowLevel";
  }
  if (["Surface3D", "VR"].includes(viewportId)) {
    if (!toolName) {
      console.log("없는데?", toolName);
      toolName = "Rotation";
    }

    const forceRotation =
      toolName === "Scroll" ||
      (viewportId === "VR" &&
        toolName === "WindowLevel" &&
        analysisType !== "segmentation");

    if (forceRotation) {
      toolName = "Rotation";
    }
    if (dragState.dragIndex === -1) {
      context.current.dragState.dragIndex = evt.button;
    }
  }

  if (handleDragMove(context, evt, analysisType) || dragState.actorPickState?.pickdownPoint) {
    return;
  }

  const currentMouseX = evt.clientX;
  const currentMouseY = evt.clientY;

  if (context.current.lastMousePosition === null) {
    context.current.lastMousePosition = [currentMouseX, currentMouseY];
    return;
  }
  const deltaX = currentMouseX - context.current.lastMousePosition[0];
  const deltaY = currentMouseY - context.current.lastMousePosition[1];

  switch (toolName) {
    case "Zoom": {
      if (!(evt.buttons & MouseButtons.Left)) break;
      const { renderer, viewportId } = context.current;
      const zoomScale = defaultZoom[viewportId]?.mouseMove ?? 0;
      if ((viewportId === "Surface3D" || viewportId === "VR") && deltaY === 0)
        break;
      const camera = renderer.getActiveCamera();
      const rate = camera.getParallelScale() * 2;
      handleZoom(context, deltaY > 0 ? zoomScale : -zoomScale);
      if (viewportId === "CPR") {
        handleCPRPostZoom(
          context as React.MutableRefObject<CPRContext>,
          deltaY,
          zoomScale,
          rate,
        );
      }
      context.current.zoom = camera.getParallelScale();
      break;
    }
    case "Pan": {
      if (
        evt.buttons !== MouseButtons.Right &&
        evt.buttons !== MouseButtons.Left
      )
        break;
      moveCamera(context, deltaX, deltaY);
      break;
    }
    case "Scroll": {
      if (evt.buttons !== MouseButtons.Left) break;
      if (viewportId === "CPR") {
        updateCPROnDeltaAngle(context, deltaX * 0.3);
      } else if (viewportId === "CrossSection") {
        const delta = deltaY > 0 ? 1 : -1;
        moveCrossSectionIndex(
          context as React.MutableRefObject<CrossSectionContext>,
          analysisType!,
          delta,
          mainIndex,
          subIndex,
          indices,
        );
      }
      break;
    }
    case "WindowLevel": {
      if (
        evt.buttons !== MouseButtons.Middle &&
        evt.buttons !== MouseButtons.Left
      )
        break;
      if (!["CrossSection", "CPR", "VR"].includes(viewportId)) return;
      if (!updateWindowLevelWidth || !voi) return;
      const basePolicy = context.current.windowLevelDragPolicy ?? {
        windowLevel: voi.windowLevel,
        windowWidth: voi.windowWidth,
        invert: voi.invert,
      };
      const { windowLevel: lev, windowWidth: win } = basePolicy;
      const scale = viewportId === "VR" ? 1 : 2;
      let lower = lev - win / 2 + (deltaY - deltaX) * scale;
      let upper = lev + win / 2 + (deltaY + deltaX) * scale;
      if (lower > upper) return;
      const newWW = upper - lower;
      const newWL = (upper + lower) * 0.5;
      const nextPolicy: ImageWindowingPolicy = {
        windowLevel: newWL,
        windowWidth: newWW,
        invert: basePolicy.invert,
      };
      context.current.windowLevelDragPolicy = nextPolicy;
      publishImageLayerStackLiveWindowingPolicy(
        nextPolicy,
        `vtk-${viewportId}-${context.current.rendererId}`,
      );
      if (viewportId === "CrossSection") {
        const actor = context.current.actorIds?.["CSActor"] as vtkImageSlice | undefined;
        const property = actor?.getProperty?.();
        if (property) {
          property.setColorLevel(newWL);
          property.setColorWindow(newWW);
          scheduleRender(context.current.rendererId, { type: "updateRenderWindow" });
        }
      }
      scheduleLiveWindowLevelCommit(updateWindowLevelWidth, [newWL, newWW]);
      if (viewportId === "VR" && configStore.get().visibleMIP) {
        scheduleRender("VR", {
          type: "updateWindowLevel",
          windowLevel: newWL,
          windowWidth: newWW,
          mode: "mouseMove",
        });
      }
      break;
    }
    case "Measurement3D": //Rotation하면서 3d 측정도구 굴려야대
    case "Rotation": {
      const currPos = getScreenPosition(
        context.current.canvas,
        evt.clientX,
        evt.clientY,
      );
      rotate(context, currPos);
      break;
    }
    default:
      break;
  }
  context.current.lastMousePosition = [currentMouseX, currentMouseY];
  renderer.resetCameraClippingRange();
};

export const handleMouseWheel = async (
  context: React.MutableRefObject<
    CPRContext | CrossSectionContext | Context | null
  >,
  evt: WheelEvent,
  analysisType?: AnalysisType,
  mainIndex?: number,
  subIndex?: number,
  indices?: [number, number, number],
) => {
  if (!context.current) return;
  const { renderer, renderWindow, viewportId, canvas } = context.current;
  if (!renderer || !renderWindow) return;
  if (evt.ctrlKey) {
    const zoomScale = defaultZoom[viewportId]?.mouseWheel ?? 0;
    const delta = evt.deltaY;
    const camera = renderer.getActiveCamera();
    const rate = camera.getParallelScale() * 2;
    handleZoom(context, delta > 0 ? zoomScale : -zoomScale);
    if (viewportId === "CPR") {
      handleCPRPostZoom(
        context as React.MutableRefObject<CPRContext>,
        delta,
        zoomScale,
        rate,
      );
    }
    context.current.zoom = camera.getParallelScale();
  } else {
    if (viewportId === "CPR") {
      const delta = evt.deltaY;
      updateCPROnDeltaAngle(context, delta * 0.05);
    } else if (viewportId === "CrossSection") {
      const delta = evt.deltaY > 0 ? 1 : -1;
      moveCrossSectionIndex(
        context as React.MutableRefObject<CrossSectionContext>,
        analysisType!,
        delta,
        mainIndex,
        subIndex,
        indices,
      );
    }
  }
};

function updateCPROnDeltaAngle(
  context: React.MutableRefObject<Context | null>,
  deltaAngle: number,
) {
  const cprContext = context as React.MutableRefObject<CPRContext>;
  if (cprContext.current?.overlayTranslation) {
    const updatedAngle = cprContext.current?.angle! + deltaAngle;
    const moveY = cprContext.current?.overlayTranslation![1];
    cprContext.current.overlayTranslation[0] = 0;
    updateCPRAngleAndPath(cprContext, updatedAngle, moveY!, true, false);
  }
}

function moveCrossSectionIndex(
  context: React.MutableRefObject<CrossSectionContext | null>,
  analysisType: AnalysisType,
  delta: number,
  mainIndex?: number,
  subIndex?: number,
  indices?: [number, number, number],
) {
  if (!context.current || !context.current.centerlineData) return;
  const index = context.current?.index! + delta;

  const maxlen = context.current.centerlineData!.getNumberOfPoints();
  const isAorticMode =
    analysisType === "aorticValveCalcium" || analysisType === "tavi";
  const front = isAorticMode ? 0 : configStore.get().coronary_front_expand;
  const back = isAorticMode ? 0 : configStore.get().coronary_back_expand;
  if (index < 0 + front || index >= maxlen - back) return;

  if (isAorticMode) {
    scheduleRender(`CrossSection.${mainIndex}.${subIndex}`, {
      type: "aorticCalcium_move",
      newIndex: index,
    });

    scheduleRender(`MarkerLine.${mainIndex}.stretched`, {
      type: "markerMove",
      index,
      which: subIndex,
    });
    scheduleRender(`MarkerLine.${mainIndex}.straightened`, {
      type: "markerMove",
      index,
      which: subIndex,
    });
  } else {
    const [left, middle, right] = indices!;
    switch (subIndex) {
      case 0:
        if (index > middle) return;
        break;
      case 1:
        if (index < left || index > right) return;
        break;
      case 2:
        if (index < middle) return;
        break;
    }
    const nodedata = handleNodeChange(context.current.centerlineData!, index);
    if (nodedata === null) return;
    const { position, normal } = nodedata;
    const newPickedPoint = { position, normal, index };
    scheduleRender(`CPR.${mainIndex}.straightened`, {
      type: "markerMove",
      realIdx: 0,
      which: subIndex,
      newIndices: index,
      newPickedPoints: [newPickedPoint],
    });

    scheduleRender(`CrossSection.${mainIndex}.${subIndex}`, {
      type: "markerMove",
      which: subIndex,
      newIndex: index,
    });
    if (
      analysisType === "plaque" ||
      analysisType === "fai" ||
      analysisType === "stenosis" ||
      analysisType === "isr"
    ) {
      //markline 업데이트 markerMove 이걸로 쓰고싶으나 내용물이 달라서 따로 돌쟈
      scheduleRender(`MarkerLine.${mainIndex}.stretched`, {
        type: "markerMove",
        index,
        which: subIndex,
      });
      scheduleRender(`MarkerLine.${mainIndex}.straightened`, {
        type: "markerMove",
        index,
        which: subIndex,
      });

      scheduleRender("Surface3D", {
        type: "markerMove",
        mainIndex,
        which: subIndex,
        newPickedPoints: [newPickedPoint],
      });
      scheduleRender("VR", {
        type: "markerMove",
        mainIndex,
        which: subIndex,
        newPickedPoints: [newPickedPoint],
      });
      //scheduleRender('Graph', {
      //  type: 'markerMove',
      //  mainIndex,
      //  which: subIndex,
      //  newIndex: index - config.coronary_front_expand,
      //});
      scheduleRender("Report", {
        type: "markerMove",
        mainIndex,
        which: subIndex,
        newIndex: index - configStore.get().coronary_front_expand,
      });
    }
    //if (analysisType === 'fai') {
    scheduleRender(`CPR-${mainIndex}-20`, {
      type: "markerMove",
      mainIndex,
      which: subIndex,
      newIndex: index - configStore.get().coronary_front_expand,
    });
    //}
  }
}

export const handleZoom = async (
  context: React.MutableRefObject<Context | null>,
  zoomScale: number,
) => {
  if (!context.current) return;
  const { renderer, renderWindow, viewportId } = context.current;
  const zoomFactor = 1 + zoomScale;

  const camera = renderer.getActiveCamera();
  // 카메라 줌 적용
  if (camera.getParallelProjection()) {
    let currentScale = camera.getParallelScale();
    if (viewportId === "Surface3D" || viewportId === "VR") {
      currentScale = Math.min(
        Math.max(currentScale, MIN_PARALLEL_SCALE),
        MAX_PARALLEL_SCALE,
      );
    }
    camera.setParallelScale(currentScale * zoomFactor);
  } else {
    const position = camera.getPosition();
    const focalPoint = camera.getFocalPoint();
    const direction = [
      position[0] - focalPoint[0],
      position[1] - focalPoint[1],
      position[2] - focalPoint[2],
    ];
    const currentDistance = Math.hypot(...direction);
    const newDistance = currentDistance * zoomFactor;
    const directionNormalized = direction.map(
      (v) => (v / currentDistance) * newDistance,
    );
    const newPosition = [
      focalPoint[0] + directionNormalized[0],
      focalPoint[1] + directionNormalized[1],
      focalPoint[2] + directionNormalized[2],
    ];
    camera.setPosition(newPosition[0], newPosition[1], newPosition[2]);
  }
  renderer.resetCameraClippingRange();
  renderWindow.render();
};

function handleCPRPostZoom(
  context: React.MutableRefObject<CPRContext | null>,
  delta: number,
  zoomScale: number,
  rate: number,
) {
  if (!context.current) return;
  const zoomFactorTemp = 1 + (delta > 0 ? -zoomScale : +zoomScale);
  const height = context.current.canvas.getBoundingClientRect().height;
  const moveY =
    ((context.current.moveCountY! * height) / rate) * zoomFactorTemp;
  context.current.overlayTranslation![0] = 0;
  context.current.overlayTranslation![1] = moveY;
  updateCPRPath(context, moveY);
}

export const volumeInputCallback = (params: {
  volumeActor: vtkVolume;
  volumeId: string;
}) => {
  const { volumeActor } = params;
  const property = volumeActor.getProperty();
  const rgbTF = property.getRGBTransferFunction(0);
  const opacityTF = property.getScalarOpacity(0);

  rgbTF.removeAllPoints();
  opacityTF.removeAllPoints();

  rgbTF.addRGBPoint(-1024, 0.0, 0.0, 0.0);
  opacityTF.addPoint(-1024, 0.0);

  opacityTF.addPoint(-2048, 0.0);
  rgbTF.addRGBPoint(-2048, 0.0, 0.0, 0.0);

  rgbTF.addRGBPoint(128.643, 0.0, 0.0, 0.0);
  opacityTF.addPoint(128.643, 0.0);

  rgbTF.addRGBPoint(129.982, 0.615686, 0.0, 0.0156863);
  opacityTF.addPoint(129.982, 0.0982143);

  rgbTF.addRGBPoint(173.636, 0.909804, 0.454902, 0.0);
  opacityTF.addPoint(173.636, 0.669643);

  rgbTF.addRGBPoint(255.884, 0.886275, 0.886275, 0.886275);
  opacityTF.addPoint(255.884, 0.857143);

  rgbTF.addRGBPoint(584.878, 0.968627, 0.968627, 0.968627);
  opacityTF.addPoint(584.878, 0.866071);

  rgbTF.addRGBPoint(3661, 1.0, 1.0, 1.0);
  opacityTF.addPoint(3661, 1.0);

  volumeActor.modified();
};

function initAxes(style: IStyle) {
  const axes = vtkAnnotatedCubeActor.newInstance();
  axes.setDefaultStyle(style);

  axes.setYMinusFaceProperty({ text: "P", faceRotation: 180 });
  axes.setYPlusFaceProperty({ text: "A", faceRotation: 0 });

  axes.setXPlusFaceProperty({ text: "L", faceRotation: 270 });
  axes.setXMinusFaceProperty({ text: "R", faceRotation: 90 });

  axes.setZPlusFaceProperty({ text: "F", faceRotation: 180 });
  axes.setZMinusFaceProperty({ text: "H", faceRotation: 180 });

  return axes;
}

function initOrientationWidget(
  renderWindow: vtkRenderWindow,
  axes: vtkAnnotatedCubeActor,
) {
  const orientationWidget = vtkOrientationMarkerWidget.newInstance({
    actor: axes,
    interactor: renderWindow.getInteractor(),
  });
  orientationWidget.setEnabled(true);
  orientationWidget.setViewportCorner(
    vtkOrientationMarkerWidget.Corners.BOTTOM_RIGHT,
  );
  orientationWidget.setViewportSize(0.15);
  orientationWidget.setMinPixelSize(100);
  orientationWidget.setMaxPixelSize(300);

  return orientationWidget;
}

export function updateCellPicker(context: React.RefObject<VRContext>) {
  if (!context.current) return; //왜 계쏙;;;
  const { viewport, cellPicker } = context.current;
  // const {volumeActor, volumeId} = params;

  const defaultActor = (viewport as Types.IVolumeViewport).getDefaultActor();
  const volumeActor = defaultActor.actor as vtkVolume;
  volumeActor.setPickable(true);
  cellPicker!.addPickList(volumeActor);

  // 기존에 prepareImageDataForPicking함수
  // 강제로 getData 주입해야하는듯?
  {
    const imageData = volumeActor.getMapper()?.getInputData();
    if (!imageData) {
      console.log("No imageData found in the volumeActor");
      return;
    }

    const { voxelManager } = imageData.get("voxelManager");
    if (!voxelManager) {
      console.log("No voxelManager found in the imageData");
      return;
    }

    const fakeScalars = {
      getData: () => {
        return voxelManager.getCompleteScalarDataArray();
      },
      getNumberOfComponents: () => voxelManager.numberOfComponents,
      getDataType: () =>
        voxelManager.getCompleteScalarDataArray().constructor.name,
    };

    imageData.setPointData({
      getScalars: () => fakeScalars,
    });
  }
  context.current.actorIds["Default"] = volumeActor;
}

export function setSharedContextData(
  id: string,
  mode: "oriLabelData" | "oriHUData",
  data?: vtkImageData,
) {
  if (data) {
    data.set({ id });
    if (mode === "oriLabelData") {
      sharedContext.oriLabelData = data;
    } else {
      sharedContext.oriHUData = data;
    }
  } else {
    //id만 다시 설정하는거야
    const target =
      mode === "oriLabelData"
        ? sharedContext.oriLabelData
        : sharedContext.oriHUData;
    target.set({ id });
  }
}

export function processOriginalData({
  // pool,
  setBufferPool,
  seriesId,
  accessToken,
}: {
  // pool: BufferPoolItem,
  setBufferPool: React.Dispatch<React.SetStateAction<BufferPool>>;
  seriesId: string;
  accessToken: string;
  // , tags:ImageTag
}) {
  const { oriHUData, oriLabelData } = sharedContext;
  const shouldDownload =
    ((oriHUData as vtkImageData).get("id") as { id: string }).id !== seriesId ||
    ((oriLabelData as vtkImageData).get("id") as { id: string }).id !==
      seriesId;

  if (!shouldDownload) return;

  downloadQueue.enqueue(async () => {
    configStore.setVolatileData({ isLowSegReady: false });
    console.log("똥컴ㅎㅇ 원본", seriesId);
    {
      const target = `${seriesId}_interpoled.nii.gz`;
      const { data, status } = await fetchSeriesArrayBuffer({
        accessToken,
        seriesId,
        target,
      });
      if (status !== 200) throw "이게 없으면 아무것도 못해요";
      await runWorkerTask({ type: "decompress", data, genHeader: true }).then(
        ({ imageData, headers }) => {
          const { dimensions, spacing, origin, direction } = headers;
          const HUdata = hu_raw_to_vtkimage({
            scalarArray: new Int16Array(imageData),
            dimensions,
            spacing,
            origin,
            direction,
          }) as vtkImageData;
          setSharedContextData(seriesId, "oriHUData", HUdata);
          // HUdata.set({ id: seriesId })
          // sharedContext.oriHUData = HUdata;
          console.log("똥컴ㅂㅇ 원본", seriesId);
          // return {hudata,headers}
        },
      );
    }
    //위에꺼 기둘하고, headers가 필요하니.
    console.log("똥컴ㅎㅇ 라벨", seriesId);
    {
      const target = "compressed_merge_components.nii.gz";
      const { data, status } = await fetchSeriesArrayBuffer({
        accessToken,
        seriesId,
        target,
      });
      if (status !== 200) throw "라벨맵이 아직 없너요";
      runWorkerTask({
        type: "decompress",
        data,
      }).then(({ imageData }) => {
        const dimensions = sharedContext.oriHUData.getDimensions();
        const spacing = sharedContext.oriHUData.getSpacing();
        const origin = sharedContext.oriHUData.getOrigin();
        const direction = sharedContext.oriHUData.getDirection();
        console.log({ dimensions, spacing, origin, direction });
        const labelData = raw_to_vtkimage({
          scalarArray: new Uint8Array(imageData),
          dimensions,
          spacing,
          origin,
          direction,
        }) as vtkImageData;
        setSharedContextData(seriesId, "oriLabelData", labelData);
        // labelData.set({ id: seriesId })
        // sharedContext.oriLabelData = labelData;
        setBufferPool((prev) => {
          const base = prev[seriesId] ?? cloneDeep(initialBufferPool);
          const pool = {
            ...base,
          };
          // 이거 다운받기도 전에 거 arrangeLabelDataWorker가 돌아버린상태.
          if (pool.splitLabelData?.loaded) {
            const data = pool.splitLabelData.data! as Record<
              number,
              Uint8Array | null
            >;
            pool.oriLabelData.data = {};
            Object.entries(data).forEach(([id, scalarArray]) => {
              pool.oriLabelData.data = {
                ...(pool.oriLabelData.data as Record<number, vtkImageData>),
                [id]: raw_to_vtkimage({
                  scalarArray,
                  dimensions,
                  spacing,
                  origin,
                  direction,
                }) as vtkImageData,
              };
            });
            console.log({ oriLabelData }, "요거 끝");
            pool.oriLabelData.loaded = true;
            pool.splitLabelData.data = undefined;
            pool.splitLabelData.loaded = false;
          }
          return {
            ...prev,
            [seriesId]: pool,
          };
        });
        configStore.setVolatileData({ isLowSegReady: true });
      });
    }
  }, 2);
}

export const loadRegulatorBuffer = async ({
  pool,
  accessToken,
  selectedSeriesId,
}: {
  pool: BufferPoolItem;
  accessToken: string;
  selectedSeriesId: string;
}) => {
  if (!pool) return;
  const parameter = pool.parameter.data as CalciumAnalysisResult;
  if (!parameter) return;
  const { renderingQuality } = configStore.get().generalSetting;

  const regulatePath = parameter.SavedInfo.regulatePath
    ? parameter.SavedInfo.regulatePath
        .replace(/^\.\//, "")
        .replaceAll("/", "%2F")
    : "regulator.nii.gz";

  const { data } = await fetchSeriesArrayBuffer({
    accessToken,
    seriesId: selectedSeriesId,
    target: regulatePath,
  });

  if (!data) {
    console.log("regulate load fail");
    return;
  }
  const { imageData, headers } = await runWorkerTask({
    type: "decompress",
    data,
    genHeader: true,
  });

  if (!parameter.imageTag) return;
  const dims_interpolated = [
    parameter.imageTag.width,
    parameter.imageTag.height,
    parameter.imageTag.depth_interpolated,
  ];

  const { dimensions } = headers;

  const src = new Uint8Array(imageData);

  const [x, y, z] = dimensions;

  //늘리고
  //const resizedData = zResize(src, x, y, z, dims_interpolated[2]);
  const { data: resizedData } = await runWorkerTask({
    type: 'zResize',
    src,
    xSize: x,
    ySize: y,
    zSize: z,
    targetSize: dims_interpolated[2],
  });

  if (!resizedData) return;

  let finalData = resizedData;
  //절반
  const isLowQuality = renderingQuality !== "high";

  if (isLowQuality) {
    const rawData = await runWorkerTask({
      type: "halfResolution",
      data: resizedData,
      dim: dims_interpolated,
    });
    finalData = new Uint8Array(rawData.imageData);
  }
  createRegulatorSegmentation(
    selectedSeriesId,
    finalData,
    z,
    dims_interpolated[2],
    isLowQuality,
  );
};

function cellIdMapping(cellId: number) {
  switch (cellId) {
    case 0:
      return "R";
    case 1:
      return "L";
    case 2:
      return "P";
    case 3:
      return "A";
    case 4:
      return "H";
    case 5:
      return "F";
    default:
      return "";
  }
}
function setOrientationWidgetEvent({
  renderWindow,
  renderer,
  orientationWidget,
}: {
  renderWindow: vtkRenderWindow;
  renderer: vtkRenderer;
  orientationWidget: vtkOrientationMarkerWidget;
}) {
  const canvas = renderWindow.getViews()[0].getCanvas() as HTMLCanvasElement;
  const highlightSource = vtkCubeSource.newInstance({
    xLength: 0.95,
    yLength: 0.95,
    zLength: 0.95,
  });
  const highlightMapper = vtkMapper.newInstance();
  const highlightActor = vtkActor.newInstance();

  highlightActor.setMapper(highlightMapper);
  highlightMapper.setInputConnection(highlightSource.getOutputPort());
  highlightActor.getProperty().setColor(1, 1, 1);
  highlightActor.getProperty().setOpacity(0.1);
  highlightActor.setVisibility(false);

  const axesPicker = vtkCellPicker.newInstance();
  axesPicker.setPickFromList(true);
  axesPicker.setTolerance(0.01);
  axesPicker.addPickList(orientationWidget.getActor());

  orientationWidget.getRenderer().addActor(highlightActor);

  const getCanvasDisplayPosition = (event: MouseEvent) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / Math.max(1, rect.width);
    const scaleY = canvas.height / Math.max(1, rect.height);
    const x = (event.clientX - rect.left) * scaleX;
    const y = canvas.height - (event.clientY - rect.top) * scaleY - 1;
    return {
      width: canvas.width,
      height: canvas.height,
      position: { x, y },
    };
  };

  canvas.addEventListener("mousemove", (event: MouseEvent) => {
    const { width, height, position } = getCanvasDisplayPosition(event);
    const vpBound = orientationWidget.computeViewport();
    const normed = [position.x / width, position.y / height];
    const isInside =
      normed[0] >= vpBound[0] &&
      normed[0] <= vpBound[2] &&
      normed[1] >= vpBound[1] &&
      normed[1] <= vpBound[3];
    if (!isInside) {
      canvas.style.cursor = "default";
      return;
    }

    const markerRenderer = orientationWidget.getRenderer();
    if (!markerRenderer) return;
    axesPicker.pick([position.x, position.y, 0], markerRenderer);
    const cellId = axesPicker.getCellId();

    const isHit = cellId !== -1;
    canvas.style.cursor = isHit ? "pointer" : "default";
    highlightActor.setVisibility(isHit);
    if (isHit) {
      const faceIndex = cellId;
      const positions = [
        [-0.05, 0, 0],
        [0.05, 0, 0],
        [0, -0.05, 0],
        [0, 0.05, 0],
        [0, 0, -0.05],
        [0, 0, 0.05],
      ];

      highlightActor.setPosition(
        positions[faceIndex][0],
        positions[faceIndex][1],
        positions[faceIndex][2],
      );
    }
    renderWindow.render();
  });

  canvas.addEventListener(
    "mousedown",
    (event: MouseEvent) => {
      const { position } = getCanvasDisplayPosition(event);
      const markerRenderer = orientationWidget.getRenderer();
      if (!markerRenderer) return;
      axesPicker.pick([position.x, position.y, 0], markerRenderer);
      const cellId = axesPicker.getCellId();
      if (cellId !== -1) {
        event.stopImmediatePropagation();
        const camera = renderer.getActiveCamera();
        // const camera: vtkCamera = renderer.getActiveCamera();
        const key = cellIdMapping(cellId);
        setConstantPose(key, camera);
        renderer.resetCameraClippingRange();
        renderer.updateLightsGeometryToFollowCamera();
        scheduleRender("Surface3D", { type: "updateRenderWindow" });
      }
    },
    true,
  );
}

function initializeVtkRendererVR(
  // container: HTMLDivElement,
  partialContext: Partial<Context>,
): VRContext {
  const vtkVR = true;

  const cellPicker = vtkCellPicker.newInstance({ opacityThreshold: 0.0001 });
  cellPicker.setPickFromList(true);
  cellPicker.setTolerance(0.01);
  cellPicker.initializePickList();

  if (vtkVR) {
    const genericRenderWindow = vtkGenericRenderWindow.newInstance();
    const renderer = genericRenderWindow.getRenderer();
    const renderWindow = genericRenderWindow.getRenderWindow();

    // while (container.firstChild) container.removeChild(container.firstChild);
    genericRenderWindow.setBackground([0.05, 0.05, 0.05]);
    // genericRenderWindow.setContainer(container);
    genericRenderWindow.resize();

    renderer.resetCamera();
    renderer.getActiveCamera().setParallelProjection(true);
    renderWindow.setDefaultViewAPI("WebGL" as unknown as DEFAULT_VIEW_API);

    const font = configStore.get().generalSetting.font;
    const axes = initAxes({
      text: "A",
      fontFamily: font ?? "Pretendard",
      fontStyle: "bold",
      fontColor: "white",
      fontSizeScale: (res) => res / 1.5,
      faceColor: "#1C2228",
      edgeThickness: 0,
      // resolution: 400,
    });

    const orientationWidget = initOrientationWidget(renderWindow, axes);
    orientationWidget.setInteractor(renderWindow.getInteractor());
    axes.setPickable(true);
    // 인터렉터
    {
      // const zoomManipulator =
      //   vtkMouseCameraTrackballZoomManipulator.newInstance({
      //     scrollEnabled: true,
      //     control: true,
      //     button: 2, // 휠
      //   });

      //const panManipulator = vtkMouseCameraTrackballPanManipulator.newInstance({
      //  button: 3, // 👉 오른쪽 마우스 버튼
      //});
      //const iStyle = vtkInteractorStyleManipulator.newInstance();
      ////iStyle.addMouseManipulator(zoomManipulator);
      //iStyle.addMouseManipulator(panManipulator);
      renderWindow.getInteractor().setInteractorStyle(null as any);
      setOrientationWidgetEvent({ renderWindow, renderer, orientationWidget });
    }

    //카메라
    {
      const EPS = 1e-6;
      renderer.getActiveCamera().setPosition(EPS, 1 + EPS, EPS);
      renderer.getActiveCamera().setViewUp(EPS, EPS, -1 + EPS);
    }

    const view: vtkOpenGLRenderWindow = renderWindow.getViews()[0];
    const canvas = view.getCanvas() as HTMLCanvasElement;
    if (canvas) {
      // canvas.style.width = '100%';
      // canvas.style.height = '100%';
      canvas.style.cursor = "default";
      registerCanvasCapture(canvas, () => captureCPRPNG(renderWindow));
    }

    renderWindow.setNumberOfLayers(2);
    renderer.setLayer(0);
    const overlayRenderer = vtkRenderer.newInstance();
    overlayRenderer.setLayer(1); // layer 1 : 항상 위
    overlayRenderer.setActiveCamera(renderer.getActiveCamera());
    overlayRenderer.setPreserveColorBuffer(true);
    overlayRenderer.setPreserveDepthBuffer(true);
    renderWindow.addRenderer(overlayRenderer);

    // const program =canvas.getContext('webgl2',{depth:true,antialias:true,powerPreference:"high-performance" })?.createProgram();

    return {
      ...partialContext,
      genericRenderWindow,
      canvas,
      orientationWidget,
      // axesPicker,
      renderer,
      overlayRenderer,
      renderWindow,
      cellPicker,
      segHuMin: -2048,
      segHuMax: 4096,
      vtkVR,
      centerX: 0,
      axes: {
        x: [1, 0, 0],
        y: [0, 1, 0],
        z: [0, 0, 1],
      },
      // program
      // actorIds:{'MPR':defaultActor.actor}
    } as VRContext;
  } else {
    const viewport = getViewport("VR");
    const renderer = viewport.getRenderer();
    const renderWindow = viewport.getRenderer().getRenderWindow();
    const canvas = viewport.getCanvas();
    return {
      ...partialContext,
      canvas,
      viewport,
      renderer,
      renderWindow,
      cellPicker,
      segHuMin: -2048,
      segHuMax: 4096,
      actorsCenter: [0, 0, 0],
      efId: "",
      // actorIds:{'MPR':defaultActor.actor}
    } as VRContext;
  }
}

function initializeVtkRendererSurface(
  // container: HTMLDivElement,
  partialContext: Partial<Context>,
  colors?: AppColors,
): SurfaceContext {
  const genericRenderWindow = vtkGenericRenderWindow.newInstance();
  const renderer = genericRenderWindow.getRenderer();
  const renderWindow = genericRenderWindow.getRenderWindow();

  genericRenderWindow.setBackground([0.05, 0.05, 0.05]);
  // genericRenderWindow.setContainer(container);
  genericRenderWindow.resize();

  renderer.resetCamera();
  renderer.getActiveCamera().setParallelProjection(true);
  renderWindow.setDefaultViewAPI("WebGL" as unknown as DEFAULT_VIEW_API);

  const font = configStore.get().generalSetting.font;
  const axes = initAxes({
    text: "A",
    fontFamily: font ?? "Pretendard",
    fontStyle: "bold",
    fontColor: "white",
    fontSizeScale: (res) => res / 1.5,
    faceColor: "#1C2228",
    edgeThickness: 0,
    // resolution: 400,
  });

  // renderWindow.getInteractor().onLeftButtonRelease((v) => {
  //   let pos = v.position;
  //   let point = [pos.x, pos.y, 0];

  //   // vtkAnnotatedCubeActor spawns on a new renderer
  //   cellPicker.pick(point, renderWindow.getRenderers()[1]);
  // });
  const orientationWidget = initOrientationWidget(renderWindow, axes);
  orientationWidget.setInteractor(renderWindow.getInteractor());

  axes.setPickable(true);
  // 인터렉터
  {
    // const zoomManipulator = vtkMouseCameraTrackballZoomManipulator.newInstance({
    //   scrollEnabled: true,
    //   control: true,
    //   button: 2, // 휠
    // });

    // const panManipulator = vtkMouseCameraTrackballPanManipulator.newInstance({
    //   button: 3, // 👉 오른쪽 마우스 버튼
    // });
    // const iStyle = vtkInteractorStyleManipulator.newInstance();
    // //iStyle.addMouseManipulator(zoomManipulator);
    // iStyle.addMouseManipulator(panManipulator);
    renderWindow.getInteractor().setInteractorStyle(null as any);
    setOrientationWidgetEvent({ renderWindow, renderer, orientationWidget });
  }

  //카메라
  {
    const EPS = 1e-6;
    renderer.getActiveCamera().setViewUp(EPS, EPS, -1 + EPS);
    renderer.getActiveCamera().setPosition(EPS, 1 + EPS, EPS);
  }

  // //전달함수
  //const cfunc = vtkColorTransferFunction.newInstance();

  // //스칼라바
  // if (colors) {
  //   cfunc.removeAllPoints();
  //   const minValue = Math.round((0 / 255) * 255 - 128); // 0
  //   const maxValue = Math.round((110 / 255) * 255 - 128); //5.5 , 110 ,5.5*0.05    // 균등 간격으로 색상 추가
  //   const palette = getThicknessPalette(colors);

  //   palette.forEach((color, index) => {
  //     const t =
  //       minValue + (index / (palette.length - 1)) * (maxValue - minValue);
  //     const [r, g, b, a] = color;
  //     cfunc.addRGBPoint(t, r, g, b);
  //   });
  // }

  // const view: vtkOpenGLRenderWindow = renderWindow.getViews()[0];
  // const canvas = view.getCanvas() as HTMLCanvasElement;

  const view: vtkOpenGLRenderWindow = renderWindow.getViews()[0];
  // get3DContext는 이미 만든 컨텍스트가 있으면 반환하고,
  // 없으면 전달한 옵션으로 생성합니다.
  view.get3DContext({
    preserveDrawingBuffer: true, // ★ 가장 중요
    antialias: true,
    premultipliedAlpha: false,
    alpha: false,
  });

  renderer.resetCamera();
  renderWindow.render();
  genericRenderWindow.resize();
  //renderWindow.getInteractor()?.setInteractorStyle(null);

  const canvas = view.getCanvas() as HTMLCanvasElement;
  if (canvas) {
    // canvas.style.width = '100%';
    // canvas.style.height = '100%';
    canvas.style.cursor = "default";
    registerCanvasCapture(canvas, () => captureCPRPNG(renderWindow));
  }
  renderWindow.setNumberOfLayers(2);
  renderer.setLayer(0);
  const overlayRenderer = vtkRenderer.newInstance();
  overlayRenderer.setLayer(1); // layer 1 : 항상 위
  overlayRenderer.setActiveCamera(renderer.getActiveCamera());
  // overlayRenderer.setPreserveColorBuffer(true);
  // overlayRenderer.setPreserveDepthBuffer(true);
  renderWindow.addRenderer(overlayRenderer);

  // const program =canvas.getContext('webgl2',{depth:true,antialias:true,powerPreference:"high-performance" })?.createProgram();
  // renderWindow.render();
  scheduleRender(partialContext.viewportId!, { type: "updateRenderWindow" });
  return {
    ...partialContext,
    genericRenderWindow,
    renderer,
    overlayRenderer,
    renderWindow,
    canvas,
    orientationWidget,
    // axesPicker,
    //cfunc,
    // focus : [0,0,0],
    axes: {
      x: [1, 0, 0],
      y: [0, 1, 0],
      z: [0, 0, 1],
    },
    actorsCenter: [0, 0, 0],
    valueLAWT: 0,
    // program
  } as SurfaceContext;
}

function initializeVtkRendererCPR(
  // container: HTMLDivElement,
  partialContext: Partial<Context>,
): CPRContext {
  const genericRenderWindow = vtkGenericRenderWindow.newInstance();
  // genericRenderWindow.setContainer(container!);
  const renderer = genericRenderWindow.getRenderer();
  const renderWindow = genericRenderWindow.getRenderWindow();
  genericRenderWindow.setBackground([0.05, 0.05, 0.05]);

  // renderer.resetCamera();
  // renderWindow.render();
  // genericRenderWindow.resize();
  // renderWindow.getInteractor()?.setInteractorStyle(null);

  // const view: vtkOpenGLRenderWindow = renderWindow.getViews()[0];
  // const canvas = view.getCanvas() as HTMLCanvasElement;
  // if (canvas) {
  //   canvas.style.width = '100%';
  //   canvas.style.height = '100%';
  // }

  // === 여기 추가: WebGL 컨텍스트를 보존 모드로 확보 ===
  const view: vtkOpenGLRenderWindow = renderWindow.getViews()[0];
  // get3DContext는 이미 만든 컨텍스트가 있으면 반환하고,
  // 없으면 전달한 옵션으로 생성합니다.
  view.get3DContext({
    preserveDrawingBuffer: true, // ★ 가장 중요
    antialias: true,
    premultipliedAlpha: false,
    alpha: false,
  });

  renderer.resetCamera();
  renderWindow.render();
  genericRenderWindow.resize();
  renderWindow.getInteractor()?.setInteractorStyle(null);

  const canvas = view.getCanvas() as HTMLCanvasElement;
  if (canvas) {
    canvas.style.width = "100%";
    canvas.style.height = "100%";
    canvas.style.cursor = "default";
    registerCanvasCapture(canvas, () => captureCPRPNG(renderWindow));
  }

  const path = [] as { x: number; y: number }[];
  // const paths = {
  //  straightened: [] as { x: number; y: number }[],
  //  stretched: [] as { x: number; y: number }[],
  // }

  return {
    ...partialContext,
    genericRenderWindow,
    renderer,
    renderWindow,
    canvas,
    path,
    angle: 0,
    axis: [0, 0, 1],
    overlayTranslation: [0, 0],
    moveCountY: 0,
    zoom: 0,
    // className: `CPR.${container.className.split('-').pop()}.${container.className.split(' ')[2]}`,
  } as CPRContext;
}

function initializeVtkRendererCrossSection(
  // container: HTMLDivElement,
  partialContext: Partial<Context>,
): CrossSectionContext {
  const genericRenderWindow = vtkGenericRenderWindow.newInstance();
  // genericRenderWindow.setContainer(container!);
  const renderer = genericRenderWindow.getRenderer();
  const renderWindow = genericRenderWindow.getRenderWindow();
  genericRenderWindow.setBackground([0.05, 0.05, 0.05]);

  // renderer.resetCamera();
  // renderWindow.render();
  // genericRenderWindow.resize();
  // renderWindow.getInteractor()?.setInteractorStyle(null);

  // const view: vtkOpenGLRenderWindow = renderWindow.getViews()[0];
  // const canvas = view.getCanvas() as HTMLCanvasElement;

  // if (canvas) {
  //   canvas.style.width = '100%';
  //   canvas.style.height = '100%';
  // }

  const view: vtkOpenGLRenderWindow = renderWindow.getViews()[0];
  // get3DContext는 이미 만든 컨텍스트가 있으면 반환하고,
  // 없으면 전달한 옵션으로 생성합니다.
  view.get3DContext({
    preserveDrawingBuffer: true, // ★ 가장 중요
    antialias: true,
    premultipliedAlpha: false,
    alpha: false,
  });

  renderer.resetCamera();
  renderWindow.render();
  genericRenderWindow.resize();
  renderWindow.getInteractor()?.setInteractorStyle(null);

  const canvas = view.getCanvas() as HTMLCanvasElement;
  if (canvas) {
    canvas.style.width = "100%";
    canvas.style.height = "100%";
    canvas.style.cursor = "default";
    registerCanvasCapture(canvas, () => captureCPRPNG(renderWindow));
  }

  const ro = new ResizeObserver(() => {
    genericRenderWindow.resize();
  });
  if (canvas) ro.observe(canvas);

  return {
    ...partialContext,
    genericRenderWindow,
    renderer,
    renderWindow,
    canvas,
    zoom: 0,
  } as CrossSectionContext;
}
export function getPartialContext(
  rendererId: string,
  viewportId: PanelComponentKeyType,
) {
  return {
    savedTranslation: [0, 0, 0],
    startPosition: [0, 0, 0],
    previousPosition: [0, 0, 0],
    dragState: { isDragging: false, dragIndex: -1 },
    rendererId,
    viewportId,
    actorIds: {},
    lastMousePosition: null,
    markerline_visibility: true,
    isAxis3DVisible: true,
    sharedContext,
  } as Partial<Context>;
}

export function refreshVtkContext(
  viewportId: PanelComponentKeyType,
  context: CPRContext | CrossSectionContext | VRContext | SurfaceContext,
) {}
export function initializeVtkRenderer(
  rendererId: string,
  viewportId: PanelComponentKeyType,
  colors?: AppColors,
) {
  const partialContext = getPartialContext(rendererId, viewportId);
  let cntx:
    | CPRContext
    | CrossSectionContext
    | VRContext
    | SurfaceContext
    | undefined = undefined;
  switch (viewportId) {
    case "VR":
      cntx = initializeVtkRendererVR(
        // ref.current!,
        partialContext,
      );
      break;
    case "Surface3D":
      cntx = initializeVtkRendererSurface(
        // ref.current!,
        partialContext,
        colors,
      );
      // console.log(context.current as SurfaceContext);
      break;
    case "CPR":
      cntx = initializeVtkRendererCPR(
        // ref.current!,
        partialContext,
      );
      // console.log(context.current as CPRContext);
      break;
    case "CrossSection":
      cntx = initializeVtkRendererCrossSection(
        // ref.current!,
        partialContext,
      );
      break;
    default:
      throw "???";
  }

  console.debug("initializeVtkRenderer", viewportId);
  return cntx;
  // context.current = {
  //   ...partialContext,
  //   viewportId,
  //   savedTranslation:[0,0,0],
  //   startPosition:[0,0,0],
  //   previousPosition:[0,0,0],
  //   dragState:{isDragging:false,dragIndex:-1},
  // }
}
export function setContainer(
  context: CPRContext | CrossSectionContext | VRContext | SurfaceContext,
  element: React.RefObject<HTMLDivElement>,
) {
  const { genericRenderWindow, renderWindow } = context;
  if (!element.current || !genericRenderWindow) return;
  if (
    isVtkObjectDeleted(genericRenderWindow) ||
    isVtkObjectDeleted(renderWindow)
  )
    return;

  try {
    genericRenderWindow.setContainer(element.current);
    genericRenderWindow.resize?.();
  } catch (error) {
    console.warn('[vtk] setContainer skipped');
  }
}
function cleanUpVtkRendererVR(context: VRContext) {
  const { overlayRenderer, renderer, cellPicker, genericRenderWindow } =
    context;
  genericRenderWindow.setBackground([0.05, 0.05, 0.05]);

  if (context.serverFrontDataVtkMode) {
    context.serverFrontDataRenderBlocked = true;
    removeAllServerFrontDataRendererViewProps(renderer, context.renderWindow);
    removeAllServerFrontDataRendererViewProps(overlayRenderer, context.renderWindow);
  } else {
    renderer.removeAllActors();
    renderer.removeAllVolumes();
    overlayRenderer?.removeAllActors();
    overlayRenderer?.removeAllVolumes();
  }
  context.actorIds = {};
  // renderer?.delete?.();
  // renderWindow?.delete?.();
  // genericRenderWindow?.delete?.();
  context.centerX = 0;
  context.efId = "";
  context.axes = {
    x: [1, 0, 0],
    y: [0, 1, 0],
    z: [0, 0, 1],
  };
  context.measurementHistory = {
    undoStack: [],
    redoStack: [],
  };
  if (cellPicker) {
    cellPicker.setPickFromList(true);
    cellPicker.setTolerance(0.01);
    cellPicker.initializePickList();
  }
}
function cleanUpVtkRendererSurface(context: SurfaceContext) {
  const { overlayRenderer, renderer } = context;
  if (context.serverFrontDataVtkMode) {
    context.serverFrontDataRenderBlocked = true;
  }

  // renderWindow?.getInteractor?.()?.cancelAnimation?.(false);
  // const oglrw = genericRenderWindow
  //   ?.getRenderWindow?.()
  //   ?.getViews?.()[0]
  //   ?.getRenderable?.();
  // oglrw?.releaseResources?.();
  // oglrw?.setContainer?.(null);
  // oglrw?.getShaderCache?.()?.releaseGraphicsResources?.(oglrw?.getContext?.());
  // Object.entries(context.current.actorIds).forEach(([key,val])=>{
  //   disposeActor(val,renderer);
  // })
  // context.current.actorIds = {};
  if (context.serverFrontDataVtkMode) {
    removeAllServerFrontDataRendererViewProps(renderer, context.renderWindow);
    removeAllServerFrontDataRendererViewProps(overlayRenderer, context.renderWindow);
  } else {
    renderer.removeAllActors();
    overlayRenderer?.removeAllActors();
    overlayRenderer?.removeAllVolumes();
  }
  context.actorIds = {};
  context.measurementHistory = {
    undoStack: [],
    redoStack: [],
  };
  context.valueLAWT = 0;

  // renderer.removeAllVolumes();
  // renderer?.delete?.();
  // renderWindow?.delete?.();
  // genericRenderWindow?.delete?.();
}

function cleanUpVtkRendererCPR(context: CPRContext) {
  const { genericRenderWindow, renderer, renderWindow, viewportId } = context;

  renderer.removeAllActors();
  renderer.removeAllVolumes();
  // Object.entries(context.current.actorIds).forEach(([key,val])=>{
  //   disposeActor(val,renderer);
  // })
  context.labelData = undefined;
  context.imageData = undefined;
  context.centerlineData = undefined;
  context.path = [];
  context.actorIds = {};

  // renderer?.delete?.();
  // renderWindow?.delete?.();
  // genericRenderWindow?.delete?.();
}

function cleanUpVtkRendererCrossSection(context: CrossSectionContext) {
  const {
    genericRenderWindow,
    renderer,
    renderWindow,
    viewportId,
    labelData,
    imageData,
    CSPaintReslice,
    CSReslice,
  } = context;

  renderer.removeAllActors();
  renderer.removeAllVolumes();
  // Object.entries(context.current.actorIds).forEach(([key,val])=>{
  //   disposeActor(val,renderer);
  // })
  context.labelData = undefined;
  context.imageData = undefined;
  context.centerlineData = undefined;
  disposeImageReslice(CSReslice);
  disposeImageReslice(CSPaintReslice);
  context.CSReslice = undefined;
  context.CSPaintReslice = undefined;
  context.actorIds = {};

  // renderer?.delete?.();
  // renderWindow?.delete?.();
  // genericRenderWindow?.delete?.();
}

export function cleanUpVtkRenderer(
  refContext: React.MutableRefObject<Context | null>,
  context: CPRContext | CrossSectionContext | VRContext | SurfaceContext | null,
) {
  if (context) {
    const { viewportId } = context;

    if (context.serverFrontDataVtkMode && (viewportId === "VR" || viewportId === "Surface3D")) {
      disposeServerFrontDataVtkContext(context, `cleanup ${viewportId}`);
      refContext.current = null;
      return;
    }

    if (viewportId === "VR") {
      cleanUpVtkRendererVR(context as VRContext);
    }
    if (viewportId === "Surface3D") {
      cleanUpVtkRendererSurface(context as SurfaceContext);
    }
    if (viewportId === "CrossSection") {
      cleanUpVtkRendererCrossSection(context as CrossSectionContext);
    }
    if (viewportId === "CPR") {
      cleanUpVtkRendererCPR(context as CPRContext);
    }
    // const { renderer,renderWindow,orientationWidget,viewportId } = context.current;
    //     orientationWidget?.delete?.();

    //     renderer?.removeAllActors?.();
    //     renderer?.delete?.();
    //     renderWindow?.delete?.();
    //     if('genericRenderWindow' in context.current){
    //       context.current.genericRenderWindow?.delete?.();
    //     }
    // console.debug('cleanUpVtkRenderer',context.current)
  }
  refContext.current = null;
}
function getYawPitchRoll(camera: vtkCamera) {
  const pos = camera.getPosition();
  const focal = camera.getFocalPoint();
  const viewUp = camera.getViewUp();

  const fwd = [focal[0] - pos[0], focal[1] - pos[1], focal[2] - pos[2]];
  const fNorm = Math.hypot(...fwd);
  const forward = fwd.map((v) => v / fNorm);

  const yaw = (Math.atan2(forward[0], forward[2]) * 180) / Math.PI;
  const pitch =
    (Math.atan2(forward[1], Math.sqrt(forward[0] ** 2 + forward[2] ** 2)) *
      180) /
    Math.PI;

  // 오른쪽 벡터 (cross product)
  const right = [
    forward[1] * viewUp[2] - forward[2] * viewUp[1],
    forward[2] * viewUp[0] - forward[0] * viewUp[2],
    forward[0] * viewUp[1] - forward[1] * viewUp[0],
  ];
  const rNorm = Math.hypot(...right);
  const rightNorm = right.map((v) => v / rNorm);

  const roll = (Math.atan2(rightNorm[1], viewUp[1]) * 180) / Math.PI;

  return { yaw, pitch, roll };
}

export function setCameraOnModifiedForSurface(
  context: React.MutableRefObject<Context | null>,
  updateYawPitchRoll: ({
    yaw,
    pitch,
    roll,
  }: {
    yaw: number;
    pitch: number;
    roll: number;
  }) => void,
) {
  if (!context.current) return;
  const { renderer } = context.current;
  renderer.getActiveCamera().onModified((instance) => {
    updateYawPitchRoll(getYawPitchRoll(instance as vtkCamera));
  });
}

function extractKey(str: string): [SegmentationControllerType, string] {
  if (str.startsWith("segmentation_") && str.endsWith("_surface_255")) {
    const core = str.slice("segmentation_".length, -"_surface_255".length);
    const parts = core.split(".");
    return [parts[parts.length - 1] as SegmentationControllerType, str];
  }
  const parts = str.split(".");
  return [
    parts[parts.length - 1] as SegmentationControllerType,
    `segmentation_${str}_surface_255`,
  ];
}

// export async function addActorFromId(
//   segmentationOrGeometryId: string,
//   context: React.MutableRefObject<SurfaceContext | null>,
//   refSegmentation: React.RefObject<Segmentation>,
// ) {
//   if (!context.current) return; // 계속막아야함

//   const { renderer, renderWindow, actorIds } =
//     context.current as SurfaceContext;
//   // const renderer = genericRenderWindow.getRenderer()
//   // const renderWindow = genericRenderWindow.getRenderWindow()

//   // const key = geometryId.match(/^segmentation_(.+?)\.([^.]+)_surface_1$/)?.[2] as SegmentationControllerType;
//   // const res = (e: MessageEvent)=>{
//   // console.log(e,'여긴 오긴 해?')

//   const [key, geometryId] = extractKey(segmentationOrGeometryId);
//   const geometry = cache.getGeometry(geometryId);
//   if (!geometry) return; // 계속막아야함

//   const surface = geometry.data as Types.ISurface;

//   const polyData = vtkPolyData.newInstance();
//   polyData.getPoints().setData(surface.points, 3);
//   polyData.getPolys().setData(surface.polys);
//   const mapper = vtkMapper.newInstance();
//   mapper.setInputData(polyData);
//   const actor = vtkActor.newInstance();
//   actor.setMapper(mapper);
//   actor.setPickable(true);
//   if (refSegmentation.current) {
//     const meshData = refSegmentation.current[key];
//     const color = meshData.color ?? [1, 1, 1];

//     actor
//       .getProperty()
//       .setColor(color[0] / 255, color[1] / 255, color[2] / 255);
//     actor.getProperty().setOpacity(meshData.opacity);
//     actor.setVisibility(meshData.visibility);

//     actor.getProperty().setInterpolationToPhong();
//     actor.setScale(-1, 1, 1);
//   }
//   if (Object.keys(actorIds).some((k) => k === key)) {
//     renderer.removeActor(actorIds[key]);
//   }
//   renderer.addActor(actor);
//   actorIds[key] = actor;
//   renderer.resetCamera();
//   renderWindow.render();

//   // }
//   // const err = (e:ErrorEvent)=>{console.log(e)}

//   // new Promise(() => {
//   // workerQueue.push([{type:'AIMEDIC'}]);
//   // workerProcess(res,err);
//   // });
// }

// 반드시 la와 lawt의 볼륨이 생성되어있다.

export function updataAorticNode(
  context: React.MutableRefObject<Context | null>,
  // seriesId: string,
  centerlineData: vtkPolyData,
  id: number,
  normalOverride?: [number, number, number] | Vector3 | null,
) {
  const { viewportId } = context.current!;
  // const labelVolume = getOriginalVolumeFromSeriesId(seriesId);
  // if (!labelVolume) return;
  // const { voxelManager, imageData } = labelVolume;
  // if (!voxelManager || !imageData) return;
  const nodedata = handleNodeChange(centerlineData, id);
  if (nodedata === null) return { world: [0, 0, 0], normal: [0, 0, 0] };
  const { position, normal } = nodedata;
  // const world = imageData.indexToWorld(position) as [number,number,number];
  const world = position as [number, number, number];
  return {
    world,
    normal: (normalOverride as [number, number, number]) ?? normal,
  };
}

export function registerOriginalVolumeBufferPool(
  volume: any,
  // seriesId : string,
) {
  const { volumeId, voxelManager, dimensions, imageData, additionalDetails } =
    volume as Types.IImageVolume;

  const numberOfVoxels = dimensions[0] * dimensions[1] * dimensions[2];
  // const scalarArray = (voxelManager!.getCompleteScalarDataArray!() as Int16Array);
  const { buffer } = voxelManager!.getCompleteScalarDataArray!() as Int16Array;
  const scalarArray = new Int16Array(
    buffer,
    /* byteOffset? */ 0,
    numberOfVoxels,
  );
  const scalars = vtkDataArray.newInstance({
    name: "",
    values: scalarArray,
    numberOfComponents: 1,
    rangeTuple: [
      additionalDetails!["HUMin"] as number,
      additionalDetails!["HUMax"] as number,
    ],
  });
  imageData?.getPointData().setScalars(scalars);
  // imageData?.getPointData().setScalars(scalars);
  if (!imageData) throw "EEEEERRRRRRRRRRRRRROOOOOOR";

  if (additionalDetails) {
    imageData.set(additionalDetails);
    imageData
      .getPointData()
      .getScalars()
      .setRange(
        [
          additionalDetails["HUMin"] as number,
          additionalDetails["HUMax"] as number,
        ] as unknown as vtkRange,
        -1,
      );
  }

  console.log("뭔가..뭔가 생김");
}

async function requestBufferPool(
  fetchSource: any,
  value: BufferPoolDataRaw,
): Promise<BufferPoolDataRaw> {
  const { target, dataType } = value;
  const { data, status } = await fetchSeriesArrayBuffer({
    ...fetchSource,
    target,
  });
  if (status !== 200) return { ...value, error: true, loading: false };

  // bin 파일이 isCompressed 이거에서 true가 나오는 상황이 생겨서 추가 예외처리
  const compressed0 = isCompressed(data);
  let compressed = compressed0;
  if (compressed0) {
    const u8 = new Uint8Array(data);
    const isGzip = u8.length >= 2 && u8[0] === 0x1f && u8[1] === 0x8b;
    const isZlib =
      u8.length >= 2 &&
      u8[0] === 0x78 &&
      (u8[1] === 0x01 || u8[1] === 0x9c || u8[1] === 0xda);
    if (!(isGzip || isZlib)) {
      compressed = false;
    }
  }

  let imageData: ArrayBuffer;

  // const compressed = isCompressed(data);
  if (compressed) {
    const decompressed = compressed ? decompress(data) : data;
    const header = readHeader(decompressed);
    imageData = readImage(header, decompressed);
  } else {
    imageData = data;
  }
  const buffer = dataType === ArrayBuffer ? imageData : new dataType(imageData);

  return {
    ...value,
    loaded: true,
    error: false,
    loading: false,
    data: buffer,
  };
}
export function setupOwnPool(
  setBufferPool: React.Dispatch<React.SetStateAction<BufferPool>>,
  seriesId: string,
) {
  setBufferPool((prev) => {
    const base = prev[seriesId] ?? cloneDeep(initialBufferPool);
    const pool = {
      ...base,
    };
    return {
      ...prev,
      [seriesId]: pool,
    };
  });
}

export function handleParameter(res: any) {
  const result = res as CalciumAnalysisResult;
  if (result) {
    const branhCnt = 50;
    const newTypes: [string, number][] =
      result?.SavedInfo?.Coronary?.newType || [];
    const desiredOrder = ["RCA", "LAD", "LCX", "LM"];
    // newTypes가 비었거나 없으면 빈 배열 반환
    const orderedResult = desiredOrder
      .map((name) => newTypes.find(([label]) => label === name))
      .filter((item): item is [string, number] => !!item);

    // id -1이 아니고 label이 'uncertain'이 아닌 경우만 필터링
    const filtered: [number, string][] = newTypes
      .filter(([label, id]) => id !== -1 && label.toLowerCase() !== "uncertain")
      .map(([label, id]) => [id, label]);

    let branchIds: Record<number, string>[];
    let mainBranchIds: [number, number, number];

    if (newTypes && newTypes.length > 0) {
      // 정상적으로 newTypes가 있을 때
      branchIds = [];
      filtered.forEach(([id, name], idx) => {
        if (name && name.trim() !== "") {
          branchIds.push({ [id]: name });
        }
      });

      for (let idx = 0; idx < branhCnt; idx++) {
        const alreadyExists = filtered.find(([id]) => id === idx);
        if (!alreadyExists) {
          branchIds.push({ [idx]: "" });
        }
      }
      mainBranchIds = orderedResult.slice(0, 3).map(([, id]) => id) as [
        number,
        number,
        number,
      ];
    } else {
      // newTypes가 없을 때

      branchIds = [];
      for (let idx = 0; idx < branhCnt; idx++) {
        branchIds.push({ [idx]: "" });
      }
      mainBranchIds = [0, 0, 0];
    }
    return { branchIds, mainBranchIds, result };
  }
}

export function buildBitMaskIndex(imageData: ArrayBuffer | Uint32Array): {
  bitMaskSet: Record<number, Set<number>>;
  arrangeBitMask: Record<number, number>;
} {
  const lookUp =
    imageData instanceof Uint32Array ? imageData : new Uint32Array(imageData);
  const bitMaskSet: Record<number, Set<number>> = {};
  for (let i = 0; i < 32; i++) {
    bitMaskSet[i] = new Set<number>();
  }

  const arrangeBitMask: Record<number, number> = {};
  lookUp.forEach((mask, index) => {
    if (mask === 0) return;

    const bitMasks = decomposeMask(mask);
    for (const bm of bitMasks) {
      const bit = Math.log2(bm) | 0;
      if (bit >= 0 && bit < 32) {
        bitMaskSet[bit].add(index);
      }
    }
    arrangeBitMask[index] = (mask >>> 28) & 0xf;
  });
  return { bitMaskSet, arrangeBitMask };
}

export async function requestBranches(
  pool: BufferPoolItem,
  setBufferPool: React.Dispatch<React.SetStateAction<BufferPool>>,
  accessToken: string,
  seriesId: string,
  key: CPRBufferKeys,
) {
  const keyData = pool[key] as BufferPoolDataRaw;
  if (keyData.loading) {
    return;
  }
  keyData.loading = true;
  setBufferPool((prev) => ({
    ...prev,
    [seriesId]: {
      ...prev[seriesId],
      [key]: keyData,
    },
  }));

  const parameter = pool.parameter.data as CalciumAnalysisResult;
  console.log(keyData, parameter?.SavedInfo?.Interpretation);
  const path =
    parameter?.SavedInfo?.Interpretation?.coronary_merge_mapping_path
      ?.replace(/^\.\//, "") // 앞의 ./ 제거
      ?.replaceAll("/", "%2F") ?? keyData.target; // / → %2F 변환

  console.log(path);
  downloadQueue.enqueue(async () => {
    const { data, status } = await fetchSeriesArrayBuffer({
      accessToken,
      seriesId,
      target: path,
    });
    if (status !== 200) return;
    runWorkerTask({ type: "decompress", data }).then(
      ({ imageData }: { imageData: ArrayBuffer }) => {
        const { bitMaskSet, arrangeBitMask } = buildBitMaskIndex(imageData);
        setBufferPool((prev) => ({
          ...prev,
          [seriesId]: {
            ...prev[seriesId],
            [key]: {
              ...keyData,
              loaded: true,
              loading: false,
              data: {
                stenosisBranches: arrangeBitMask,
                branches: bitMaskSet,
              },
            },
          },
        }));
      },
    );
  }, 0);
}

export async function requestPolyDataModel(
  pool: BufferPoolItem,
  setBufferPool: React.Dispatch<React.SetStateAction<BufferPool>>,
  accessToken: string,
  seriesId: string,
  key: CPRBufferKeys,
  renderingQuality: RenderingQualityKeyType,
) {
  // TEMP(test): use CenterlineIds === 0 branch before 3D selection wiring.
  // Set to null to disable.
  const TEST_FORCE_CENTERLINE_ID: number | null = null;
  const volume = getVolumeFromSeriesId(seriesId, renderingQuality !== "high");
  if (!volume) return;

  const buildFfrGraphDataset = (
    input: vtkPolyData,
  ): {
    byNode: Record<
      number,
      {
        ffr: Array<{ x: number; y: number }>;
        area: Array<{ x: number; y: number }>;
        diameter: Array<{ x: number; y: number }>;
      }
    >;
  } | null => {
    const pointData = input.getPointData();
    const cellData = input.getCellData();
    const ffrArray =
      pointData.getArrayByName("FFR_Smoothing") ??
      pointData.getArrayByName("FFR");
    const areaArray = pointData.getArrayByName("Area");
    const distArray = pointData.getArrayByName("Distance");
    const centerlineIdsArray =
      cellData?.getArrayByName("CenterlineIds") ??
      pointData.getArrayByName("CenterlineIds");
    const centerlinePtIdsArray = pointData.getArrayByName("CenterlinePtIds");
    const r0x1l2Array = cellData?.getArrayByName("R0X1L2");
    if (!ffrArray || !areaArray || !distArray) return null;

    const ffrData = ffrArray.getData() as ArrayLike<number>;
    const areaData = areaArray.getData() as ArrayLike<number>;
    const distData = distArray.getData() as ArrayLike<number>;
    const centerlineIdsData = centerlineIdsArray?.getData() as
      | ArrayLike<number>
      | undefined;
    const centerlinePtIdsData = centerlinePtIdsArray?.getData() as
      | ArrayLike<number>
      | undefined;
    const r0x1l2Data = r0x1l2Array?.getData() as ArrayLike<number> | undefined;
    const pointCount =
      typeof input.getNumberOfPoints === "function"
        ? input.getNumberOfPoints()
        : 0;
    if (
      !ffrData?.length ||
      !areaData?.length ||
      !distData?.length ||
      pointCount <= 0
    )
      return null;

    // Branch split priority:
    // 1) polyline cells from centerline (PolyView logic)
    // 2) CenterlineIds + CenterlinePtIds mapping
    // 3) whole-point fallback
    const getCurveByPointIds = (pointIds: number[]) => {
      const ffr: Array<{ x: number; y: number }> = [];
      const area: Array<{ x: number; y: number }> = [];
      const diameter: Array<{ x: number; y: number }> = [];
      const rawX: number[] = [];

      pointIds.forEach((ptId) => {
        if (
          ptId < 0 ||
          ptId >= ffrData.length ||
          ptId >= areaData.length ||
          ptId >= distData.length
        ) {
          return;
        }
        const x = Number(distData[ptId]);
        const yFFR = Number(ffrData[ptId]);
        const yArea = Number(areaData[ptId]);
        if (
          !Number.isFinite(x) ||
          !Number.isFinite(yFFR) ||
          !Number.isFinite(yArea)
        )
          return;
        rawX.push(x);
        ffr.push({ x, y: yFFR });
        area.push({ x, y: yArea });
        diameter.push({ x, y: 2 * Math.sqrt(Math.max(yArea, 0) / Math.PI) });
      });

      if (!ffr.length || !area.length || !diameter.length) return null;

      // If x (Distance) is highly non-monotonic, replace with cumulative arc-length.
      let nonMonotonicCount = 0;
      for (let i = 1; i < rawX.length; i += 1) {
        if (!(rawX[i] >= rawX[i - 1])) nonMonotonicCount += 1;
      }
      const nonMonotonicRatio =
        rawX.length > 1 ? nonMonotonicCount / (rawX.length - 1) : 0;

      if (nonMonotonicRatio > 0.05) {
        const cumulative: number[] = new Array(pointIds.length).fill(0);
        for (let i = 1; i < pointIds.length; i += 1) {
          const prevPt = input.getPoints().getPoint(pointIds[i - 1]) as [
            number,
            number,
            number,
          ];
          const curPt = input.getPoints().getPoint(pointIds[i]) as [
            number,
            number,
            number,
          ];
          const dx = Number(curPt[0]) - Number(prevPt[0]);
          const dy = Number(curPt[1]) - Number(prevPt[1]);
          const dz = Number(curPt[2]) - Number(prevPt[2]);
          const seg = Math.sqrt(dx * dx + dy * dy + dz * dz);
          cumulative[i] = cumulative[i - 1] + (Number.isFinite(seg) ? seg : 0);
        }
        for (let i = 0; i < ffr.length; i += 1) {
          ffr[i].x = cumulative[i];
          area[i].x = cumulative[i];
          diameter[i].x = cumulative[i];
        }
      }

      return { ffr, area, diameter };
    };

    const getCurveFromCellId = (cellId: number) => {
      try {
        const cell = input.getCellPoints(cellId) as
          | { cellPointIds?: number[] | Uint32Array; cellType?: number }
          | undefined;
        const pointIdsRaw = cell?.cellPointIds;
        const pointIds = pointIdsRaw
          ? Array.from(pointIdsRaw as any)
          : undefined;
        if (!pointIds || pointIds.length < 2) return null;
        return getCurveByPointIds(pointIds);
      } catch {
        return null;
      }
    };

    const byNode: Record<
      number,
      {
        ffr: Array<{ x: number; y: number }>;
        area: Array<{ x: number; y: number }>;
        diameter: Array<{ x: number; y: number }>;
      }
    > = {};
    const bestByCenterlineId = new Map<
      number,
      {
        pointCount: number;
        curve: {
          ffr: Array<{ x: number; y: number }>;
          area: Array<{ x: number; y: number }>;
          diameter: Array<{ x: number; y: number }>;
        };
      }
    >();
    const cellCurves: Array<{
      cellId: number;
      cellType: number;
      arteryType: number | null;
      pointCount: number;
      curve: {
        ffr: Array<{ x: number; y: number }>;
        area: Array<{ x: number; y: number }>;
        diameter: Array<{ x: number; y: number }>;
      };
    }> = [];

    const numberOfCells =
      typeof input.getNumberOfCells === "function"
        ? input.getNumberOfCells()
        : 0;
    if (numberOfCells > 0) {
      if (TEST_FORCE_CENTERLINE_ID !== null && centerlineIdsData) {
        for (let cellId = 0; cellId < numberOfCells; cellId += 1) {
          const cid = Number(centerlineIdsData[cellId]);
          if (!Number.isFinite(cid) || cid !== TEST_FORCE_CENTERLINE_ID) {
            continue;
          }
          const forcedCurve = getCurveFromCellId(cellId);
          if (forcedCurve) {
            byNode[0] = forcedCurve;
            break;
          }
        }
      }

      for (let cellId = 0; cellId < numberOfCells; cellId += 1) {
        let pointCountOfCell = 0;
        let cellType = -1;
        try {
          const cell = input.getCellPoints(cellId) as
            | { cellPointIds?: number[] | Uint32Array; cellType?: number }
            | undefined;
          pointCountOfCell = cell?.cellPointIds?.length ?? 0;
          cellType = Number(cell?.cellType ?? -1);
        } catch {
          pointCountOfCell = 0;
          cellType = -1;
        }
        // PolyView uses polyline cells only.
        if (cellType !== 4) continue;
        if (pointCountOfCell < 2) continue;
        const curve = getCurveFromCellId(cellId);
        if (!curve) continue;
        const arteryType = r0x1l2Data
          ? Number(r0x1l2Data[cellId] ?? NaN)
          : null;
        const centerlineIdRaw =
          centerlineIdsData && cellId < centerlineIdsData.length
            ? Number(centerlineIdsData[cellId])
            : NaN;
        if (Number.isFinite(centerlineIdRaw)) {
          const centerlineId = Math.trunc(centerlineIdRaw);
          const prevBest = bestByCenterlineId.get(centerlineId);
          if (!prevBest || pointCountOfCell > prevBest.pointCount) {
            bestByCenterlineId.set(centerlineId, {
              pointCount: pointCountOfCell,
              curve,
            });
          }
        }
        cellCurves.push({
          cellId,
          cellType,
          arteryType: Number.isFinite(arteryType as number)
            ? (arteryType as number)
            : null,
          pointCount: pointCountOfCell,
          curve,
        });
      }
    }

    // Primary keying for FFR selection: real CenterlineIds from picked branch.
    bestByCenterlineId.forEach((v, k) => {
      byNode[k] = v.curve;
    });

    if (!byNode[0] && cellCurves.length > 0) {
      cellCurves.sort((a, b) => b.pointCount - a.pointCount);

      // If one cell is almost the whole centerline, prefer the next branch-like cell.
      const almostWholeThreshold = Math.max(1, Math.floor(pointCount * 0.95));
      const eligible = cellCurves.filter(
        (c) => c.pointCount < almostWholeThreshold,
      );
      const pickLongestByArtery = (artery: number) =>
        eligible
          .filter((c) => c.arteryType === artery)
          .sort((a, b) => b.pointCount - a.pointCount)[0];

      const rcaCell = pickLongestByArtery(0);
      const lcxCell = pickLongestByArtery(1);
      const ladCell = pickLongestByArtery(2);

      // Temporary mapping:
      // node 0 -> RCA(0) preferred, else best non-whole branch
      // node 1 -> LCX(1), node 2 -> LAD(2) when available
      const fallbackBest =
        eligible.sort((a, b) => b.pointCount - a.pointCount)[0] ??
        cellCurves[0];
      byNode[0] = (rcaCell ?? fallbackBest).curve;
      if (lcxCell) byNode[1] = lcxCell.curve;
      if (ladCell) byNode[2] = ladCell.curve;
    }

    if (!byNode[0] && centerlineIdsData && centerlinePtIdsData) {
      const selectedIds: number[] = [];
      for (let i = 0; i < pointCount; i += 1) {
        if (Number(centerlineIdsData[i]) !== 0) continue;
        const ptId = Number(centerlinePtIdsData[i]);
        if (Number.isFinite(ptId)) selectedIds.push(ptId);
      }
      if (selectedIds.length > 0) {
        selectedIds.sort((a, b) => Number(distData[a]) - Number(distData[b]));
        const curve = getCurveByPointIds(selectedIds);
        if (curve) byNode[0] = curve;
      }
    }

    if (!byNode[0]) {
      const fallbackIds = Array.from({ length: pointCount }, (_, i) => i).sort(
        (a, b) => Number(distData[a]) - Number(distData[b]),
      );
      const curve = getCurveByPointIds(fallbackIds);
      if (curve) byNode[0] = curve;
    }

    if (!byNode[0]) return null;
    console.log("[FFR graph dataset] node keys", Object.keys(byNode));
    return { byNode };
  };

  const keyData = pool[key] as BufferPoolDataRaw;
  if (keyData.loading) return;

  const json = pool.ct_ffr.data;

  const getJsonKeyFromPath = (path: ParameterPath) => {
    if (!path) return undefined;
    const firstPath = path[0];
    if (Array.isArray(firstPath)) return firstPath[firstPath.length - 1];
    return path[path.length - 1] as string | undefined;
  };
  const jsonKey = getJsonKeyFromPath(keyData.path);
  const jsonMap = json ? (json as Record<string, unknown>) : undefined;
  const jsonTarget = jsonKey ? jsonMap?.[jsonKey] : undefined;
  const target =
    typeof jsonTarget === "string"
      ? jsonTarget.replace(/^\.\//, "")
      : keyData.target;

  // 🔹 상태 업데이트 함수 (내부 정의)
  const updateState = (newData: BufferPoolDataRaw) => {
    setBufferPool((prev) => ({
      ...prev,
      [seriesId]: {
        ...prev[seriesId],
        [key]: newData,
      },
    }));
  };

  // 🔹 loading 시작
  updateState({ ...keyData, loading: true });

  downloadQueue.enqueue(async () => {
    const res = await fetchSeries3DModel({
      accessToken,
      seriesId,
      target,
    });

    if (key === 'FFR_aorta') {
      const buffer = res as ArrayBuffer;
      const view = new DataView(buffer);
      const INITIAL_POINT_OFFSET = 0x1b; // 27 bytes
      const FLOAT_COUNT = 6;
      const FLOAT_SIZE = 4;
      const REQUIRED_SIZE = INITIAL_POINT_OFFSET + FLOAT_COUNT * FLOAT_SIZE;

      const values: number[] = [];
      for (let i = 0; i < FLOAT_COUNT; i++) {
        values.push(
          view.getFloat32(
            INITIAL_POINT_OFFSET + i * FLOAT_SIZE,
            true, // Little Endian
          ),
        );
      }
    }
    if (!res) {
      console.log("requestVTP 없음", res, target);
      updateState({
        ...keyData,
        loading: false,
        loaded: true,
      });
      return;
    }

    const isStl = keyData.target.toLowerCase().endsWith('.stl');
    const reader = isStl
      ? vtkSTLReader.newInstance()
      : vtkXMLPolyDataReader.newInstance();

    reader.parseAsArrayBuffer(res);
    const polyData = reader.getOutputData(0) as vtkPolyData;
    polyData.buildLinks();
    const points = polyData.getPoints().getData();
    const { spacing, origin, dimensions } = volume;

    const ffrGraphDataset =
      key === "FFR_centerline" ? buildFfrGraphDataset(polyData) : null;

    // const pointData = polyData.getPointData();
    // const cellData = polyData.getCellData();
    // pointData.getArrays().forEach((arr) => {
    //   console.log(key, '[point]', arr.getName(), arr.getData());
    // });
    // cellData.getArrays().forEach((arr) => {
    //   console.log(key, '[cell]', arr.getName(), arr.getData());
    // });

    // if (key === 'FFR_centerline') {
    //   for (let i = 0; i < polyData.getNumberOfCells(); i += 1) {
    //     const ptList = polyData.getCellPoints(i) as {
    //       cellType: number;
    //       cellPointIds: number[] | Uint32Array | Int32Array;
    //     };
    //     console.log(ptList);
    //   }
    // }

    runWorkerTask({
      type: "decodeVtpToPolyDataBuffers",
      points,
      dimensions,
      spacing,
      origin,
    })
      .then(({ positions }) => {
        polyData.getPoints().setData(positions, 3);
        const normalsFilter = vtkPolyDataNormals.newInstance();
        normalsFilter.setInputData(polyData);
        normalsFilter.setComputePointNormals(true);
        normalsFilter.setComputeCellNormals(false);
        normalsFilter.update();

        // 🔹 완료 상태
        const output = normalsFilter.getOutputData() as vtkPolyData;
        if (key === "FFR_centerline") {
          output.buildLinks();
        }

        setBufferPool((prev) => {
          const nextSeries = {
            ...prev[seriesId],
            [key]: {
              ...keyData,
              data: output,
              loading: false,
              loaded: true,
            } as BufferPoolDataRaw,
          } as any;

          if (key === "FFR_centerline") {
            nextSeries.ffrGraphDataset = {
              ...(prev[seriesId]?.ffrGraphDataset ?? {}),
              loaded: true,
              loading: false,
              error: !ffrGraphDataset,
              data: ffrGraphDataset ?? undefined,
            };
          }

          return {
            ...prev,
            [seriesId]: nextSeries,
          };
        });
      })
      .catch(() => {
        setBufferPool((prev) => {
          const nextSeries = {
            ...prev[seriesId],
            [key]: {
              ...keyData,
              loading: false,
              loaded: true,
              error: true,
            } as BufferPoolDataRaw,
          } as any;

          if (key === "FFR_centerline") {
            nextSeries.ffrGraphDataset = {
              ...(prev[seriesId]?.ffrGraphDataset ?? {}),
              loaded: true,
              loading: false,
              error: true,
              data: undefined,
            };
          }

          return {
            ...prev,
            [seriesId]: nextSeries,
          };
        });
      });
  });
}

export async function requestDraco(
  pool: BufferPoolItem,
  setBufferPool: React.Dispatch<React.SetStateAction<BufferPool>>,
  accessToken: string,
  seriesId: string,
  key: CPRBufferKeys,
  renderingQuality: RenderingQualityKeyType,
) {
  const targetVolume = getVolumeFromSeriesId(
    seriesId,
    renderingQuality !== "high",
  ); // getOriginalVolumeFromSeriesId(seriesId);
  if (!targetVolume) return;

  const fetchSource = {
    accessToken,
    seriesId,
  };

  const keyData = pool[key] as BufferPoolDataRaw;
  if (keyData.loading) {
    return;
  }
  keyData.loading = true;
  setBufferPool((prev) => ({
    ...prev,
    [seriesId]: { ...prev[seriesId], [key]: keyData },
  }));
  downloadQueue.enqueue(async () => {
    const { target } = keyData;
    const res = await fetchSeries3DModel({ ...fetchSource, target });
    if (!res) {
      // 파일이없을때 냅따... [최선일까.]
      setBufferPool((prev) => ({
        ...prev,
        [seriesId]: {
          ...prev[seriesId],
          [key]: {
            ...keyData,
            loaded: true,
            loading: false,
          },
        },
      }));
      return;
    }
    runWorkerTask({
      type: "decodeDracoToPolyDataBuffers",
      res,
      dimensions: targetVolume.dimensions,
      spacing: targetVolume.spacing,
      origin: targetVolume.origin,
    }).then(({ positions, cellHeap }) => {
      const points = vtkPoints.newInstance();
      points.setData(positions, 3);
      const polys = vtkCellArray.newInstance({ values: cellHeap });
      const polyData = vtkPolyData.newInstance();
      polyData.setPoints(points);
      polyData.setPolys(polys);
      //뒤집어야대서 이거 못씀

      // // vtkPolyNormals로 저장
      // const normalsFilter = vtkPolyDataNormals.newInstance();
      // normalsFilter.setInputData(polyData);
      // normalsFilter.update();
      setBufferPool((prev) => ({
        ...prev,
        [seriesId]: {
          ...prev[seriesId],
          [key]: {
            ...keyData,
            loaded: true,
            loading: false,
            data: polyData,
          },
        },
      }));
    });
  });
}

export async function refreshBufferPoolJson(
  pool: BufferPoolItem,
  setBufferPool: React.Dispatch<React.SetStateAction<BufferPool>>,
  accessToken: string,
  seriesId: string,
  key: CPRBufferKeys,
) {
  const fetchSource = {
    accessToken,
    seriesId,
  };
  const keyData = pool[key] as BufferPoolDataRaw;
  const CacheControl = {
    Pragma: "no-cache",
    Expires: "0",
  };

  await downloadQueue.enqueue(async () => {
    console.log(`${key} 받는 나.`);
    const res = await fetchSeriesJson({
      ...fetchSource,
      target: keyData.target,
      CacheControl,
    });
    console.log(`${key} 받는 나.`, res);
    setBufferPool((prev) => ({
      ...prev,
      [seriesId]: {
        ...prev[seriesId],
        [key]: {
          ...keyData,
          loaded: true,
          loading: false,
          data: res,
        },
      },
    }));
  });
}
export async function requestBufferPoolJson(
  pool: BufferPoolItem,
  setBufferPool: React.Dispatch<React.SetStateAction<BufferPool>>,
  accessToken: string,
  seriesId: string,
  key: CPRBufferKeys,
) {
  const fetchSource = {
    accessToken,
    seriesId,
  };
  const keyData = pool[key] as BufferPoolDataRaw;
  if (keyData.loading) {
    return;
  }
  keyData.loading = true;
  setBufferPool((prev) => ({
    ...prev,
    [seriesId]: { ...prev[seriesId], [key]: keyData },
  }));

  const CacheControl = {
    Pragma: "no-cache",
    Expires: "0",
  };

  downloadQueue.enqueue(async () => {
    console.log(`${key} 받는 나.`);
    const res = await fetchSeriesJson({
      ...fetchSource,
      target: keyData.target,
      CacheControl,
    });
    setBufferPool((prev) => ({
      ...prev,
      [seriesId]: {
        ...prev[seriesId],
        [key]: {
          ...keyData,
          loaded: true,
          loading: false,
          data: res,
        },
      },
    }));
  });
}


function isMatchingServerFrontDataCprManifestForAnalysis(
  value: unknown,
  analysisType: AnalysisType,
) {
  const manifest = getCprCrossSectionImagesFromParameter(value);
  if (!manifest?.revision?.startsWith("front-data:")) return false;
  return manifest.revision.split(":").includes(analysisType);
}

function shouldReplaceCprCrossSectionImagesForServerFrontData(
  keyData: BufferPoolDataRaw | undefined,
  mode: CprCrossSectionImageMode,
  analysisType: AnalysisType,
) {
  if (keyData?.frontDataAvailability === "missing") return false;
  if (mode !== "server") return false;
  if (!isServerCprFrontDataAnalysisType(analysisType)) return false;
  return !isMatchingServerFrontDataCprManifestForAnalysis(
    keyData?.data,
    analysisType,
  );
}

function getLoadedBranchSignature(manifest: CprCrossSectionImages | null | undefined) {
  if (!manifest) return "";
  return Object.entries(manifest.branches ?? {})
    .map(([branchId, branch]) =>
      [
        branchId,
        branch?.loaded === true ? "1" : "0",
        branch?.cpr ? "cpr" : "-",
        branch?.crossSection ? "cs" : "-",
      ].join(":"),
    )
    .sort()
    .join("|");
}

function mergeLoadedCprBranchesForSameRevision(
  nextManifest: CprCrossSectionImages,
  currentManifest: CprCrossSectionImages | null | undefined,
) {
  if (!currentManifest || currentManifest.revision !== nextManifest.revision) {
    return nextManifest;
  }

  let changed = false;
  const branches = { ...nextManifest.branches };
  Object.entries(currentManifest.branches ?? {}).forEach(([branchKey, currentBranch]) => {
    const nextBranch = branches[branchKey];
    if (currentBranch?.loaded === true && nextBranch?.loaded !== true) {
      branches[branchKey] = {
        ...nextBranch,
        ...currentBranch,
      };
      changed = true;
    }
  });

  return changed
    ? {
        ...nextManifest,
        branches,
      }
    : nextManifest;
}

function areCprImageManifestsEquivalentForPoolUpdate(
  a: CprCrossSectionImages | null | undefined,
  b: CprCrossSectionImages | null | undefined,
) {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.revision === b.revision &&
    getLoadedBranchSignature(a) === getLoadedBranchSignature(b)
  );
}

export function requestCprCrossSectionImages(
  pool: BufferPoolItem,
  setBufferPool: React.Dispatch<React.SetStateAction<BufferPool>>,
  accessToken: string,
  seriesId: string,
  mode: CprCrossSectionImageMode,
  appConfig: AppConfig,
  analysisType: AnalysisType,
  onDone?: () => void,
  manifestTarget?: string,
  currentVOI?: VOI,
  mainBranchIds?: readonly number[],
) {
  if (!isCprCrossSectionImageModeEnabled(mode)) return false;
  void appConfig;

  const key = "cprCrossSectionImages" as CPRBufferKeys;
  const keyData = pool[key] as BufferPoolDataRaw | undefined;
  if (!keyData || !("target" in keyData)) return false;

  const requestKey =
    mode === "server" && isServerCprFrontDataAnalysisType(analysisType)
      ? ["server-front-data", seriesId, analysisType, ...(mainBranchIds ?? [])].join("|")
      : ["manifest", seriesId, mode, manifestTarget ?? keyData.target].join("|");
  const currentRequestKey = (keyData as { requestKey?: string }).requestKey;
  const canSupersedeLoadingRequest =
    mode === "server" &&
    isServerCprFrontDataAnalysisType(analysisType) &&
    currentRequestKey !== requestKey;
  const shouldCheckFrontDataAvailability =
    mode === "server" && isServerCprFrontDataAnalysisType(analysisType);

  if (keyData.loading && !canSupersedeLoadingRequest) return false;
  if (shouldCheckFrontDataAvailability && keyData.frontDataAvailability === "missing") {
    return false;
  }

  const shouldReplaceLoadedData = shouldReplaceCprCrossSectionImagesForServerFrontData(
    keyData,
    mode,
    analysisType,
  );
  if (keyData.loaded && !shouldReplaceLoadedData) return false;

  const requestedAt = Date.now();
  const nextKeyData = {
    ...keyData,
    loading: true,
    error: false,
    frontDataAvailability: shouldCheckFrontDataAvailability
      ? keyData.frontDataAvailability
      : undefined,
    requestedAt,
    requestKey,
    requestedAnalysisType: analysisType,
  };

  setBufferPool((prev) => ({
    ...prev,
    [seriesId]: {
      ...prev[seriesId],
      [key]: nextKeyData,
    },
  }));

  const CacheControl = {
    Pragma: "no-cache",
    Expires: "0",
  };

  downloadQueue.enqueue(async () => {
    try {
      const target = manifestTarget ?? keyData.target;
      let data: CprCrossSectionImages | null = null;
      let frontDataAvailability = nextKeyData.frontDataAvailability;

      if (shouldCheckFrontDataAvailability) {
        const hasFrontData = await checkServerCprFrontDataAvailableOnce({
          accessToken,
          seriesId,
        });
        frontDataAvailability = hasFrontData ? "available" : "missing";
      }

      if (frontDataAvailability === "available") {
        try {
          data = await loadServerCprFrontDataImageManifestOnce({
            accessToken,
            seriesId,
            analysisType,
            mainBranchIds,
            currentVOI,
          });
        } catch (frontDataErr) {
          console.warn(
            "[requestCprCrossSectionImages] front_data load failed; fallback to manifest target",
            frontDataErr,
          );
        }
      } else if (frontDataAvailability === "missing") {
        console.warn(
          "[requestCprCrossSectionImages] front_data/view_data.json missing; fallback to legacy mode",
          { seriesId, analysisType },
        );
      }

      if (!data && frontDataAvailability !== "missing") {
        const rootManifest = await fetchSeriesJson({
          accessToken,
          seriesId,
          target,
          CacheControl,
        });
        data = iscprCrossSectionImages(rootManifest)
          ? await loadcprCrossSectionImages({
              accessToken,
              seriesId,
              manifestTarget: target,
              rootManifest,
              CacheControl,
            })
          : (rootManifest as unknown as CprCrossSectionImages);
      }

      setBufferPool((prev) => {
        const currentSeries = prev[seriesId];
        const currentKeyData = currentSeries?.[key] as
          | (BufferPoolDataRaw & { requestKey?: string; requestedAt?: number })
          | undefined;
        if (
          !currentSeries ||
          currentKeyData?.requestKey !== requestKey ||
          currentKeyData?.requestedAt !== requestedAt
        ) {
          return prev;
        }
        const currentManifest = getCprCrossSectionImagesFromParameter(
          currentKeyData?.data,
        );
        const loadedManifest = getCprCrossSectionImagesFromParameter(data);
        const nextData = loadedManifest
          ? mergeLoadedCprBranchesForSameRevision(loadedManifest, currentManifest)
          : data;

        const nextManifest = getCprCrossSectionImagesFromParameter(nextData);
        const hasEquivalentManifest = areCprImageManifestsEquivalentForPoolUpdate(
          currentManifest,
          nextManifest,
        );
        if (
          frontDataAvailability !== "missing" &&
          hasEquivalentManifest &&
          currentKeyData.loaded === true &&
          currentKeyData.loading !== true &&
          currentKeyData.error !== true
        ) {
          return prev;
        }

        return {
          ...prev,
          [seriesId]: {
            ...currentSeries,
            [key]: {
              ...nextKeyData,
              loaded: true,
              loading: false,
              error: false,
              frontDataAvailability,
              requestedAt: undefined,
              requestKey: undefined,
              requestedAnalysisType: undefined,
              data:
                frontDataAvailability === "missing"
                  ? currentKeyData.data
                  : (nextData ?? undefined),
            },
          },
        };
      });
    } catch (err) {
      console.warn(`[requestCprCrossSectionImages] failed mode=${mode}`, err);
      setBufferPool((prev) => {
        const currentSeries = prev[seriesId];
        const currentKeyData = currentSeries?.[key] as
          | (BufferPoolDataRaw & { requestKey?: string; requestedAt?: number })
          | undefined;
        if (
          !currentSeries ||
          currentKeyData?.requestKey !== requestKey ||
          currentKeyData?.requestedAt !== requestedAt
        ) {
          return prev;
        }
        return {
          ...prev,
          [seriesId]: {
            ...currentSeries,
            [key]: {
              ...nextKeyData,
              loaded: false,
              loading: false,
              error: true,
              frontDataAvailability: undefined,
              requestedAt: undefined,
              requestKey: undefined,
              requestedAnalysisType: undefined,
            },
          },
        };
      });
    } finally {
      onDone?.();
    }
  }, 100);

  return true;
}

export async function requestTerrData(
  pool: BufferPoolItem,
  setBufferPool: React.Dispatch<React.SetStateAction<BufferPool>>,
  accessToken: string,
  seriesId: string,
  jsonFile?: any,
) {
  const key = "territory" as CPRBufferKeys;
  const keyData = pool[key] as BufferPoolDataRaw;
  if (keyData.loading) return;
  keyData.loading = true;
  setBufferPool((prev) => ({
    ...prev,
    [seriesId]: { ...prev[seriesId], [key]: keyData },
  }));

  const CacheControl = { Pragma: "no-cache", Expires: "0" };
  const fetchSource = { accessToken, seriesId };

  downloadQueue.enqueue(async () => {
    console.log(`${key} 다운로드 시작`);
    let json: any;
    if (jsonFile) {
      json = jsonFile;
    } else {
      json = await fetchSeriesJson({
        ...fetchSource,
        target: "territory%2Fterritory",
        CacheControl,
      });
    }
    if (!json) return;
    console.log(`${key} JSON 받는 나`, json["MyoCardial"]["MyoCardial_path"]);

    const fetchList = [
      {
        target: json["MyoCardial"]["MyoCardial_path"],
        name: "territory",
        type: "8bit",
      },
      {
        target: json["MyoCardial"]["centerline_path"],
        name: "centerline",
        type: "16bit",
      },
      {
        target: json["MyoCardial"]["MyoCardial_quality_path"],
        name: "territoryMappingData",
        type: "32bit",
      },
    ];

    const results = await Promise.all(
      fetchList.map((f) => {
        const target = f.target.replace(/^\.\//, "").replaceAll("/", "%2F");

        return fetchSeriesArrayBuffer({
          accessToken,
          seriesId,
          target,
        }).then((res) => ({
          ...res,
          name: f.name,
          type: f.type,
        }));
      }),
    );
    const dataObj: Record<
      string,
      Uint8Array | Uint32Array | Record<number, number>
    > = {};
    for (const { data, status, name, type } of results) {
      if (status !== 200) continue;
      if (name === "territoryMappingData") {
        dataObj[name] = parseMappingData(data) as Record<number, number>;
      } else {
        const compressed = isCompressed(data);
        const decompressed = compressed ? decompress(data) : data;
        const header = compressed ? readHeader(decompressed) : undefined;
        const imageData = compressed ? readImage(header!, decompressed) : data;
        if (type === "32bit") {
          dataObj[name] = new Uint32Array(imageData);
        } else if (type === "16bit") {
          dataObj[name] = new Uint16Array(imageData);
        } else {
          dataObj[name] = new Uint8Array(imageData);
        }
      }
    }
    const { bitMaskSet, arrangeBitMask } = buildBitMaskIndex(
      dataObj["mappingBranches"] as Uint32Array,
    );
    setBufferPool((prev) => ({
      ...prev,
      [seriesId]: {
        ...prev[seriesId],
        [key]: {
          ...keyData,
          loaded: true,
          loading: false,
          data: {
            json,
            territory: dataObj["territory"],
            centerline: dataObj["centerline"],
            territoryMappingData: dataObj["territoryMappingData"],
            mappingBranches: arrangeBitMask,
          },
        },
      },
    }));
  });
}
export async function requestMappingData(
  // bufferPool: BufferPool,
  pool: BufferPoolItem,
  setBufferPool: React.Dispatch<React.SetStateAction<BufferPool>>,
  accessToken: string,
  seriesId: string,
  key: CPRBufferKeys,
) {
  const keyData = pool[key] as BufferPoolDataRaw;
  if (keyData.loading) return;
  keyData.loading = true;
  setBufferPool((prev) => ({
    ...prev,
    [seriesId]: { ...prev[seriesId], [key]: keyData },
  }));

  const fetchSource = { accessToken, seriesId };

  downloadQueue.enqueue(async () => {
    console.log(`${key} 받는 나.`);
    const { data, status } = await fetchSeriesArrayBuffer({
      ...fetchSource,
      target: keyData.target,
    });
    if (status !== 200) {
      setBufferPool((prev) => ({
        ...prev,
        [seriesId]: {
          ...prev[seriesId],
          [key]: {
            loaded: true,
            loading: false,
          },
        },
      }));
      return;
    }
    const res = parseMappingData(data) as Record<number, number>;
    setBufferPool((prev) => ({
      ...prev,
      [seriesId]: {
        ...prev[seriesId],
        [key]: {
          loaded: true,
          loading: false,
          data: res,
        },
      },
    }));
  });
}

export async function requestCPRData(
  // bufferPool: BufferPool,
  pool: BufferPoolItem,
  setBufferPool: React.Dispatch<React.SetStateAction<BufferPool>>,
  accessToken: string,
  seriesId: string,
  key: string,
  force: boolean = false,
) {
  const fetchSource = {
    accessToken,
    seriesId,
  };

  const keyData = pool[key] as BufferPoolDataRaw | undefined;
  if (!keyData || !("target" in keyData)) {
    return;
  }
  if (keyData.loading && !force) {
    return;
  }
  const requestedAt = Date.now();
  keyData.loading = true;
  keyData.requestedAt = requestedAt;
  setBufferPool((prev) => ({
    ...prev,
    [seriesId]: { ...prev[seriesId], [key]: keyData },
  }));

  const queuePriority = key.startsWith(TAVI_POOL_KEY_PREFIX) ? 1 : 100;
  downloadQueue.enqueue(async () => {
    console.log(`${key} 받는 나.`);
    try {
      const res = (await Promise.race([
        requestBufferPool(fetchSource, keyData),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error("request timeout")), 15000),
        ),
      ])) as BufferPoolDataRaw;
      setBufferPool((prev) => ({
        ...prev,
        [seriesId]: {
          ...prev[seriesId],
          [key]: {
            ...res,
            requestedAt: undefined,
          },
        },
      }));
    } catch (err) {
      console.warn(`[requestCPRData] failed key=${key}`, err);
      setBufferPool((prev) => ({
        ...prev,
        [seriesId]: {
          ...prev[seriesId],
          [key]: {
            ...((prev[seriesId]?.[key] as BufferPoolDataRaw | undefined) ??
              keyData),
            loading: false,
            loaded: false,
            error: true,
            requestedAt: undefined,
          } as BufferPoolDataRaw,
        },
      }));
    }
  }, queuePriority);
}

export async function arrangeLabelDataWorker(
  pool: BufferPoolItem,
  setBufferPool: React.Dispatch<React.SetStateAction<BufferPool>>,
  seriesId: string,
  tagDimensions: [number, number, number],
) {
  if (!pool["branchLabel"].loaded) {
    console.log("아직.", pool["branchLabel"]);
    return;
  }
  if (pool["labelData"].data) {
    return;
  }

  const { renderingQuality } = configStore.get().generalSetting;
  const lowQ = renderingQuality !== "high";

  const volume = getVolumeFromSeriesId(seriesId, lowQ);
  if (!volume) {
    console.log("not volume");
    return;
  }

  const { dimensions, numVoxels, direction, spacing, origin } = volume;
  const rate = lowQ ? 0.5 : 1;

  if (!dimensions || numVoxels === 0) throw "뭥미;";

  const [width, height, depth] = dimensions;
  // const widthHeight = width * height;

  const labelData = pool["labelData"];
  labelData.data = {};
  const splitLabelData = pool["splitLabelData"];
  splitLabelData.data = {};
  const oriLabelData = pool["oriLabelData"];

  const unsharedArray = pool["branchLabel"].data as Uint8Array;

  if (unsharedArray.length < 4) return;
  const view = new DataView(
    unsharedArray.buffer,
    unsharedArray.byteOffset,
    unsharedArray.byteLength,
  );
  let offset = 0;
  offset += 4;

  let id = 0;
  const u16 = new Uint16Array(
    unsharedArray.buffer,
    unsharedArray.byteOffset,
    unsharedArray.byteLength / 2,
  );

  const hasOriLabelData =
    lowQ &&
    ((sharedContext.oriLabelData as vtkImageData).get("id") as { id: string })
      .id === seriesId;
  if (hasOriLabelData) {
    oriLabelData.data = {};
  }
  const tagNumVoxels = tagDimensions[0] * tagDimensions[1] * tagDimensions[2];
  while (offset + 4 <= unsharedArray.byteLength) {
    const count = view.getInt32(offset, true);
    const last = offset + 8 * (count + 1) > unsharedArray.byteLength;
    const itemsOffset = offset + 4;
    const labeldata = count > 0 ? new Uint8Array(numVoxels) : null;
    const oriLabeldata =
      lowQ && count > 0 ? new Uint8Array(tagNumVoxels) : null;
    if (labeldata) {
      let base = itemsOffset >> 1;
      for (let i = 0; i < count; ++i) {
        const val = u16[base];
        const x = Math.floor(u16[base + 1] * rate);
        const y = Math.floor(u16[base + 2] * rate);
        const z = Math.floor(u16[base + 3] * rate);
        const idx = x + (y + z * height) * width;
        labeldata[idx] = val;
        if (oriLabeldata) {
          const x = Math.floor(u16[base + 1]);
          const y = Math.floor(u16[base + 2]);
          const z = Math.floor(u16[base + 3]);
          const idx = x + (y + z * tagDimensions[1]) * tagDimensions[0];
          oriLabeldata[idx] = val;
        }
        base += 4;
      }
    }
    if (lowQ) {
      if (!hasOriLabelData) {
        splitLabelData.data = {
          ...(splitLabelData.data as Record<number, Uint8Array | null>),
          [id]: oriLabeldata,
        };
      } else {
        // 내가할게
        oriLabelData.data = {
          ...(oriLabelData.data as Record<number, vtkImageData>),
          [id]: raw_to_vtkimage({
            scalarArray: oriLabeldata,
            dimensions: sharedContext.oriLabelData.getDimensions(),
            spacing: sharedContext.oriLabelData.getSpacing(),
            origin: sharedContext.oriLabelData.getOrigin(),
            direction: sharedContext.oriLabelData.getDirection(),
          }) as vtkImageData,
        };
        splitLabelData.data = undefined;
        splitLabelData.loaded = false;
        oriLabelData.loaded = true;
      }
    }
    const imageData = raw_to_vtkimage({
      scalarArray: labeldata,
      dimensions,
      spacing,
      origin,
      direction,
    }) as vtkImageData;

    labelData.data = {
      ...(labelData.data as Record<number, vtkImageData>),
      [id]: imageData,
    };

    if (last) {
      labelData.loaded = true;
      setBufferPool((prev) => {
        const res = {
          ...prev[seriesId],
          labelData,
        };
        if (lowQ) {
          if (!hasOriLabelData) {
            splitLabelData.loaded = true;
            res.splitLabelData = splitLabelData;
            console.log({ splitLabelData }, "요거 끝");
          } else {
            oriLabelData.loaded = true;
            res.oriLabelData = oriLabelData;
            console.log({ oriLabelData }, "요거 끝");
            //splitLabelData는 이제 필요없음
          }
        }
        return {
          ...prev,
          [seriesId]: res,
        };
      });
    }

    id++;
    offset += 4 + 8 * count;
  }
}

export async function arrangeBranchDatasetWorker(
  // bufferPool: BufferPool,
  pool: BufferPoolItem,
  setBufferPool: React.Dispatch<React.SetStateAction<BufferPool>>,
  seriesId: string,
  pixelSpacing: number,
  planarSpacingInterpolated: number,
) {
  //체크
  if (!pool["new_area_path"].loaded) {
    console.log("없어", pool["new_area_path"]);
    return;
  }
  if (!pool["new_diameter"].loaded) {
    console.log("없어", pool["new_diameter"]);
    return;
  }
  if (!pool["new_wall_path"].loaded) {
    console.log("없어", pool["new_wall_path"]);
    return;
  }
  if (!pool["new_wall_area_path"].loaded) {
    console.log("없어", pool["new_wall_area_path"]);
    return;
  }
  if (!pool["centerline"].loaded) {
    console.log("없어", pool["centerline"]);
    return;
  }
  if (!pool["fcx"].loaded) {
    console.log("없어", pool["fcx"]);
    return;
  }
  if (!pool["fcy"].loaded) {
    console.log("없어", pool["fcy"]);
    return;
  }
  if (!pool["fcz"].loaded) {
    console.log("없어", pool["fcz"]);
    return;
  }
  const { renderingQuality } = configStore.get().generalSetting;
  const { imageData } = getVolumeFromSeriesId(
    seriesId,
    renderingQuality !== "high",
  )!;
  if (!imageData) {
    console.log("없어 imageData");
    return;
  }
  if (!pool["plaque_info_path"].loaded) {
    console.log("없어", pool["plaque_info_path"]);
    return;
  }
  if (!pool["type_total"].loaded) {
    console.log("없어", pool["type_total"]);
    return;
  }

  const branchGraphDataset = pool["branchGraphDataset"];
  if (branchGraphDataset.data) {
    return;
  }

  branchGraphDataset.data = new BranchInfo(
    pool.plaque_info_path.data as ArrayBuffer,
    pool.new_area_path.data as Int32Array,
    pool.new_diameter.data as Int32Array,
    pool.new_wall_area_path.data as Int32Array,
    pool.new_wall_path.data as Int32Array,
    pool.centerline.data as Int32Array,
    pool.fcx.data as Float32Array,
    pool.fcy.data as Float32Array,
    pool.fcz.data as Float32Array,
    imageData,
    // imageData,
    pool.type_total.data as Int32Array,
    renderingQuality !== "high",
    pixelSpacing,
    planarSpacingInterpolated,
  );

  const paramData = pool.parameter.data as CalciumAnalysisResult;
  let lesioninfo =
    configStore.get().stenosisLesionInfo.length > 0
      ? configStore.get().stenosisLesionInfo
      : undefined;

  let relesioncheck = false;
  if (configStore.get().stenosisLesionInfo.length > 0) {
    configStore.get().stenosisLesionInfo.map((les) => {
      les.map((l) => {
        if (l.length === 4) relesioncheck = true;
      });
    });
  }

  if (lesioninfo === undefined || relesioncheck) {
    let les_temp = paramData.SavedInfo.Coronary.auto_lesion_info;
    if (branchGraphDataset.data) {
      les_temp = les_temp.map((val, index) =>
        val.map((v) => {
          const mid = (branchGraphDataset.data as BranchInfo).getStenosisMid(
            index,
            v[0],
            v[1],
          );
          const infodata = (
            branchGraphDataset.data as BranchInfo
          ).getPlaqueTotalInfo(index, v[0], mid, v[1]);
          const stenosis = infodata
            ? Math.max(0, Math.abs((1 - infodata.stenosisDegree) * 100))
            : v[2];
          return [v[0], mid, v[1], stenosis, v[3]];
        }),
      );
    }
    lesioninfo = les_temp;
  }
  const stenosistext = calcStenosisText(
    paramData,
    lesioninfo,
    branchGraphDataset.data,
  );
  const newtype = paramData.SavedInfo.Coronary.newType;
  if (newtype) {
    const data_total: number[] = new Array(newtype.length);
    let saveindex: number[] = [];
    for (let i = 0; i < newtype.length; i++) {
      const idx = branchGraphDataset.data.getOriginBranchId(newtype[i][1]);
      if (saveindex.includes(newtype[i][1])) {
        data_total[i] = -1;
        continue;
      }
      saveindex.push(newtype[i][1]);
      data_total[i] = idx.id;
    }
    configStore.setData({
      motherBranch: data_total,
    });
  }

  configStore.setData({
    stenosisResult: stenosistext,
    stenosisLesionInfo: lesioninfo,
  });

  branchGraphDataset.loaded = true;
  setBufferPool((prev) => ({
    ...prev,
    [seriesId]: {
      ...prev[seriesId],
      branchGraphDataset,
    },
  }));
}

export async function splitAortaValveCalciumDataWorker(
  bufferPoolItem: BufferPoolItem,
  setBufferPool: React.Dispatch<React.SetStateAction<BufferPool>>,
  seriesId: string,
  config: AppConfig,
  setData: (data: Partial<AppConfig>) => void,
  downcheck: React.MutableRefObject<boolean>,
  options?: { includeAvc?: boolean },
) {
  const includeAvc = options?.includeAvc ?? true;
  const id = config.aorta_id;
  if (!bufferPoolItem) return;
  const requiredKeys: CPRBufferKeys[] = ["aorta_centerline"];

  if (requiredKeys.some((key) => !bufferPoolItem[key]?.loaded)) {
    return;
  }

  const { renderingQuality } = configStore.get().generalSetting;
  const lowQ = renderingQuality !== "high";
  const volume = getVolumeFromSeriesId(seriesId, lowQ);
  if (!volume) {
    console.log("not volume");
    return;
  }

  const labelvolume = getLabelVolumeFromSeriesId(seriesId, lowQ);
  if (!labelvolume) {
    console.log("not label volume");
    return;
  }

  const { imageData, voxelManager } = labelvolume;
  if (!voxelManager || !imageData) return;

  const centerData = bufferPoolItem["centerData"];
  //없으면 무조건 패스 똑같이
  if (!centerData.data) {
    centerData.data = {};
  } else {
    // 에러 플래그를 사용해버리기?
    if ((centerData.data as Record<number, vtkPolyData>)[id]) {
      return;
    }
  }
  const unsharedAortaCenter = bufferPoolItem["aorta_centerline"]
    .data as Float32Array;

  // const check = (centerData.data as Record<number, vtkPolyData>)[id];
  // if (check) return;

  const { origin, direction } = volume;
  const rate = lowQ ? 0.5 : 1;
  const spacing = volume.spacing.map((v) => v * rate) as typeof volume.spacing;

  const maskData = voxelManager!.getCompleteScalarDataArray!() as Uint8Array;
  if (!imageData?.getPointData().getScalars()) {
    const scalars = vtkDataArray.newInstance({
      name: "",
      values: maskData,
      numberOfComponents: 1,
    });
    imageData.getPointData().setScalars(scalars);
  }

  const last = true;
  downcheck.current = true;
  runWorkerTask({
    type: "aortaCenterData",
    unsharedAortaCenter,
    id,
    last,
  })
    .then(({ data_arr, id, last }) => {
      const flat = data_arr.flat();
      const flatFloat32 = new Float32Array(flat.length);
      flatFloat32.set(flat);
      const centerDataResult = createCenterline(
        flatFloat32,
        spacing,
        origin,
        direction,
      )!;

      // 센터
      centerData.data = {
        ...(centerData.data as Record<number, vtkPolyData>),
        [id]: centerDataResult,
      };

      const segmentationState = segmentation.state;
      const segmentationId = lowQ
        ? `segmentation_${seriesId}_labelmapHalf`
        : `segmentation_${seriesId}_labelmap`;
      const seg = segmentationState.getSegmentation(segmentationId);

      const aortaSet = seg?.cachedStats["aorta"] as Set<number>;
      const aorticValveCalciumSet = seg?.cachedStats["avc"] as Set<number>;
      const arrangeBitMask = seg?.cachedStats["arrangeBitMask"] as Set<number>;

      const aorta = Object.entries(arrangeBitMask)
        .filter(([key, _]) => aortaSet?.has(Number(key)))
        .map(([_, value]) => value);

      const avc = includeAvc
        ? Object.entries(arrangeBitMask)
            .filter(([key, _]) => aorticValveCalciumSet?.has(Number(key)))
            .map(([_, value]) => value)
        : [];

      const aortaRGB = hexToRgb(config.colors.aorta);
      const avcRGB = hexToRgb(config.colors.avc);

      for (let i = 0; i < config.aorta_colors.length; i++) {
        config.aorta_colors[i] = [i, 0, 0, 0];
        config.aorta_opacitys[i] = [i, 0];
      }

      for (const idx of aorta) {
        config.aorta_colors[idx] = [idx, ...aortaRGB];
        config.aorta_opacitys[idx] = [idx, 0.5];
      }

      for (const idx of avc) {
        config.aorta_colors[idx] = [idx, ...avcRGB];
        config.aorta_opacitys[idx] = [idx, 0.5];
      }

      setData({ aorta_indexs: aorta });
      setData({ avc_indexs: avc });
      setData({ aorta_colors: config.aorta_colors });
      setData({ aorta_opacitys: config.aorta_opacitys });

      const pointsArr = centerDataResult.getPoints().getData();
      const numPoints = pointsArr.length / 3;
      let start_index = numPoints - 1;
      for (let i = numPoints - 1; i >= 0; i--) {
        const index: Vector3 = [
          pointsArr[i * 3 + 0],
          pointsArr[i * 3 + 1],
          pointsArr[i * 3 + 2],
        ];
        const scalar = imageData!.getScalarValueFromWorld(index);
        if (aorta && aorta.includes(scalar)) {
          start_index = i;
          break;
        }
      }
      setData({
        aorta_start_index: start_index,
        aorta_current_index: start_index,
        tavi_start_index: config.tavi_start_index ?? 0,
        tavi_current_index: config.tavi_current_index ?? 0,
      });

      if (last) {
        //여기가 atomic이 아니라 걱정
        centerData.loaded = true;
        setBufferPool((prev) => ({
          ...prev,
          [seriesId]: { ...prev[seriesId], centerData },
        }));
      }
    })
    .catch((err) => {
      centerData.error = true;
    });
}

function toArrayBufferForTavi(data: unknown): ArrayBuffer | null {
  if (!data) return null;
  if (data instanceof ArrayBuffer) return data;
  if (
    typeof SharedArrayBuffer !== "undefined" &&
    data instanceof SharedArrayBuffer
  ) {
    return new Uint8Array(data).slice().buffer;
  }
  if (ArrayBuffer.isView(data)) {
    const v = data as ArrayBufferView;
    return new Uint8Array(v.buffer, v.byteOffset, v.byteLength).slice().buffer;
  }
  return null;
}

function getTaviRawVecArray(
  bufferpool: BufferPoolItem,
  key: "centerTangent" | "center_dx" | "center_dy",
): Float64Array | null {
  const parsed = (bufferpool as Record<string, any>)["tavi.parsed"]?.data as
    | Record<string, unknown>
    | undefined;
  const item = (bufferpool as Record<string, any>)[`tavi.${key}`];
  const raw = (parsed?.[key] as unknown) ?? item?.data;
  if (!raw) return null;

  if (raw instanceof Float64Array) return raw;
  if (raw instanceof ArrayBuffer) return new Float64Array(raw);
  if (ArrayBuffer.isView(raw)) {
    const v = raw as ArrayBufferView;
    return new Float64Array(
      v.buffer,
      v.byteOffset,
      Math.floor(v.byteLength / Float64Array.BYTES_PER_ELEMENT),
    );
  }
  return null;
}

function applyTaviOrientationTensors(
  polyData: vtkPolyData,
  bufferpool: BufferPoolItem,
  imageData: vtkImageData,
) {
  const points = polyData.getPoints()?.getData() as Float32Array | undefined;
  if (!points || points.length < 3) return;

  const n = Math.floor(points.length / 3);
  const zRaw = getTaviRawVecArray(bufferpool, "centerTangent");
  const xRaw = getTaviRawVecArray(bufferpool, "center_dx");
  const yRaw = getTaviRawVecArray(bufferpool, "center_dy");
  if (!zRaw || !xRaw || !yRaw) return;

  const spacing = imageData.getSpacing() as [number, number, number];
  const direction = imageData.getDirection() as mat3;
  const tensors = new Float32Array(n * 16);

  const readWorldVec = (arr: Float64Array, i: number): vec3 | null => {
    const p = i * 3;
    if (p + 2 >= arr.length) return null;
    // Server payload: [x, y, z]
    const v = vec3.fromValues(arr[p + 0], arr[p + 1], arr[p + 2]);
    const scaled = vec3.fromValues(
      v[0] * spacing[0],
      v[1] * spacing[1],
      v[2] * spacing[2],
    );
    vec3.transformMat3(v, scaled, direction);
    if (vec3.sqrLen(v) < 1e-12) return null;
    vec3.normalize(v, v);
    return v;
  };

  for (let i = 0; i < n; i++) {
    const xAxis = readWorldVec(xRaw, i);
    const yAxis = readWorldVec(yRaw, i);
    const zAxis = readWorldVec(zRaw, i);
    if (!xAxis) continue;
    if (!yAxis) continue;
    if (!zAxis) continue;
    vec3.normalize(xAxis, xAxis);
    vec3.normalize(yAxis, yAxis);
    vec3.normalize(zAxis, zAxis);

    const off = i * 16;
    tensors[off + 0] = xAxis[0];
    tensors[off + 1] = xAxis[1];
    tensors[off + 2] = xAxis[2];
    tensors[off + 3] = 0;
    tensors[off + 4] = yAxis[0];
    tensors[off + 5] = yAxis[1];
    tensors[off + 6] = yAxis[2];
    tensors[off + 7] = 0;
    tensors[off + 8] = zAxis[0];
    tensors[off + 9] = zAxis[1];
    tensors[off + 10] = zAxis[2];
    tensors[off + 11] = 0;
    tensors[off + 12] = points[i * 3 + 0];
    tensors[off + 13] = points[i * 3 + 1];
    tensors[off + 14] = points[i * 3 + 2];
    tensors[off + 15] = 1;
  }

  const tensorArray = vtkDataArray.newInstance({
    name: "Orientation",
    numberOfComponents: 16,
    values: tensors,
  });
  polyData.getPointData().setTensors(tensorArray);
  polyData.getPointData().addArray(tensorArray);
  polyData.modified();
}

function upsertTaviCenterlineToCenterData(
  bufferPoolItem: BufferPoolItem,
  setBufferPool: React.Dispatch<React.SetStateAction<BufferPool>>,
  seriesId: string,
  config: AppConfig,
): boolean {
  const centerId = config.tavi_aorta_id ?? config.aorta_id;
  const centerData = bufferPoolItem["centerData"];
  const current = centerData.data as Record<number, vtkPolyData> | undefined;
  if (current?.[centerId]) return true;

  const { renderingQuality } = configStore.get().generalSetting;
  const lowQ = renderingQuality !== "high";
  const labelVolume = getLabelVolumeFromSeriesId(seriesId, lowQ);
  const imageData = labelVolume?.imageData as vtkImageData | undefined;
  if (!imageData) return false;

  const polyData = getTaviCenterlinePolyData(bufferPoolItem, imageData);
  if (!polyData) return false;

  const nextData = { ...(current ?? {}), [centerId]: polyData };
  const nextCenterData = {
    ...centerData,
    loaded: true,
    data: nextData,
  } as BufferPoolData;

  setBufferPool((prev) => ({
    ...prev,
    [seriesId]: {
      ...prev[seriesId],
      centerData: nextCenterData,
    },
  }));
  return true;
}

function getTaviAnnulusCenterIdxFromPool(
  bufferPoolItem: BufferPoolItem,
): number {
  const parsed = (bufferPoolItem as Record<string, any>)["tavi.parsed"]
    ?.data as Record<string, any> | undefined;
  const fromParsed = parsed?.annulus?.centerIdx;
  if (
    typeof fromParsed === "number" &&
    Number.isFinite(fromParsed) &&
    fromParsed >= 0
  ) {
    return Math.floor(fromParsed);
  }

  const raw = (bufferPoolItem as Record<string, any>)["tavi.annulus"]?.data;
  const buf = toArrayBufferForTavi(raw);
  if (!buf || buf.byteLength < 4) return 0;
  const idx = new DataView(buf).getInt32(0, true);
  return Number.isFinite(idx) && idx >= 0 ? idx : 0;
}

function applyTaviAortaVisualConfig(
  seriesId: string,
  config: AppConfig,
  setData: (data: Partial<AppConfig>) => void,
) {
  const { renderingQuality } = configStore.get().generalSetting;
  const lowQ = renderingQuality !== "high";
  const segmentationState = segmentation.state;
  const segmentationId = lowQ
    ? `segmentation_${seriesId}_labelmapHalf`
    : `segmentation_${seriesId}_labelmap`;
  const seg = segmentationState.getSegmentation(segmentationId);
  if (!seg?.cachedStats) return;

  const aortaSet = seg.cachedStats["aorta"] as Set<number> | undefined;
  const arrangeBitMask = seg.cachedStats["arrangeBitMask"] as
    | Record<number, number>
    | undefined;
  if (!aortaSet || !arrangeBitMask) return;

  const aorta = Object.entries(arrangeBitMask)
    .filter(([key]) => aortaSet.has(Number(key)))
    .map(([, value]) => value);

  const aortaRGB = hexToRgb(config.colors.aorta);
  for (let i = 0; i < config.aorta_colors.length; i++) {
    config.aorta_colors[i] = [i, 0, 0, 0];
    config.aorta_opacitys[i] = [i, 0];
  }
  for (const idx of aorta) {
    config.aorta_colors[idx] = [idx, ...aortaRGB];
    config.aorta_opacitys[idx] = [idx, 0.5];
  }

  setData({ aorta_indexs: aorta });
  setData({ avc_indexs: [] });
  setData({ aorta_colors: config.aorta_colors });
  setData({ aorta_opacitys: config.aorta_opacitys });
}

export async function splitTaviDataWorker(
  bufferPoolItem: BufferPoolItem,
  setBufferPool: React.Dispatch<React.SetStateAction<BufferPool>>,
  seriesId: string,
  config: AppConfig,
  setData: (data: Partial<AppConfig>) => void,
  downcheck: React.MutableRefObject<boolean>,
) {
  if (!bufferPoolItem?.parameter?.loaded) return;

  const parsedItem = (bufferPoolItem as Record<string, any>)["tavi.parsed"] as
    | BufferPoolData
    | undefined;
  if (parsedItem?.loaded) {
    // Parsed payload may exist while centerData cache is missing.
    // Keep this path idempotent and try to restore tavi aorta centerline cache.
    upsertTaviCenterlineToCenterData(
      bufferPoolItem,
      setBufferPool,
      seriesId,
      config,
    );
    return;
  }
  if (parsedItem?.loading) return;

  const missingResourceKeys = TAVI_RESOURCE_KEYS.filter((k) => {
    const item = (bufferPoolItem as Record<string, any>)[k] as
      | BufferPoolDataRaw
      | undefined;
    if (!item) return true;
    if (item.retryExhausted) return false;
    return !item.loaded || !item.data;
  });
  if (missingResourceKeys.length > 0) {
    console.log("missingResourceKeys", missingResourceKeys);
    return;
  }

  const expectedNames = [...TAVI_RESOURCE_NAMES];

  const items: Record<string, ArrayBuffer | null> = {};
  for (const name of expectedNames) {
    const key = `${TAVI_POOL_KEY_PREFIX}${name}`;
    const item = (bufferPoolItem as Record<string, any>)[key] as
      | BufferPoolDataRaw
      | undefined;
    if (item?.retryExhausted) {
      items[name] = null;
      continue;
    }
    const raw = item?.data;
    const buffer = toArrayBufferForTavi(raw);
    items[name] = buffer;
  }

  const taviParsed = {
    loaded: false,
    loading: true,
  } as BufferPoolData;
  (bufferPoolItem as Record<string, any>)["tavi.parsed"] = taviParsed;

  setBufferPool((prev) => ({
    ...prev,
    [seriesId]: {
      ...prev[seriesId],
      "tavi.parsed": taviParsed,
    },
  }));

  runWorkerTask({
    type: "parseTaviData",
    items,
  })
    .then(({ parsed }) => {
      const parsedObj = (parsed ?? {}) as Record<string, unknown>;
      const missingKeys = expectedNames.filter(
        (name) => parsedObj[name] == null,
      );
      console.log("[splitTaviDataWorker] parsed complete", {
        total: expectedNames.length,
        missing: missingKeys.length,
        missingKeys,
      });
      console.log("[splitTaviDataWorker] parsed payload", parsedObj);
      setBufferPool((prev) => ({
        ...prev,
        [seriesId]: {
          ...prev[seriesId],
          "tavi.parsed": {
            loaded: true,
            loading: false,
            data: parsed,
          } as BufferPoolData,
        },
      }));
      const annulusIdx =
        typeof (parsedObj as any)?.annulus?.centerIdx === "number"
          ? Math.max(0, Math.floor((parsedObj as any).annulus.centerIdx))
          : getTaviAnnulusCenterIdxFromPool(bufferPoolItem);
      setData({
        tavi_start_index: annulusIdx,
        tavi_current_index: annulusIdx,
        aorta_start_index: config.aorta_start_index ?? 0,
        aorta_current_index: config.aorta_current_index ?? 0,
      });
      applyTaviAortaVisualConfig(seriesId, config, setData);
      upsertTaviCenterlineToCenterData(
        bufferPoolItem,
        setBufferPool,
        seriesId,
        config,
      );
    })
    .catch((err) => {
      console.error("[splitTaviDataWorker] parse failed", err);
      setBufferPool((prev) => ({
        ...prev,
        [seriesId]: {
          ...prev[seriesId],
          "tavi.parsed": {
            loaded: false,
            loading: false,
            error: true,
          } as BufferPoolData,
        },
      }));
    });
}

export async function splitBaseCenterDataWorker(
  // bufferPool: BufferPool,
  bufferPoolItem: BufferPoolItem,
  setBufferPool: React.Dispatch<React.SetStateAction<BufferPool>>,
  seriesId: string,
  config: AppConfig,
  setData: (data: Partial<AppConfig>) => void,
  downcheck: React.MutableRefObject<boolean>,
) {
  // const buffers = bufferPoolItem;//existBufferPoolSeries(seriesId);
  if (!bufferPoolItem) return;
  const requiredKeys: CPRBufferKeys[] = [
    "centerline",
    "fcx",
    "fcy",
    "fcz",

    "fxx",
    "fxy",
    "fxz",
    "fyx",
    "fyy",
    "fyz",
    "newType",

    // 'aorta_centerline',
  ];

  if (requiredKeys.some((key) => !bufferPoolItem[key]?.loaded)) {
    return;
  }

  const { renderingQuality } = configStore.get().generalSetting;
  const lowQ = renderingQuality !== "high";
  const volume = getVolumeFromSeriesId(seriesId, lowQ);
  if (!volume) {
    console.log("허거덩..볼륨 왜없지");
    return;
  }

  const labelvolume = getLabelVolumeFromSeriesId(seriesId, lowQ);
  if (!labelvolume) {
    console.log("not label volume");
    return;
  }

  const splitData = bufferPoolItem["splitData"];
  const centerData = bufferPoolItem["centerData"];
  const oriCenterData = bufferPoolItem["oriCenterData"];
  const fxData = bufferPoolItem["fxData"];
  const fyData = bufferPoolItem["fyData"];
  if (!fxData.data) fxData.data = {};
  if (!fyData.data) fyData.data = {};
  //없으면 무조건 패스
  if (!centerData.data) {
    centerData.data = {};
    splitData.data = {} as Record<number, Vector3[]>;
  } else {
    // aorta로 인해 만들어져있을 가능성이 있다.
    // 이것이 여기것인가는 splitData로 따져버리기
    //누군가 만들고있어.
    if (splitData.data) {
      return;
    } else {
      //아냐 내가만들거야
      splitData.data = {} as Record<number, Vector3[]>;
    }
  }

  if (!oriCenterData.data) {
    oriCenterData.data = {};
  }

  const { imageData, voxelManager } = labelvolume;
  if (!imageData || !voxelManager) return;

  const { dimensions, origin, direction } = volume;
  const rate = lowQ ? 0.5 : 1;
  const spacing = volume.spacing.map((v) => v * rate) as typeof volume.spacing;
  const data_width = dimensions[0];
  const data_height = dimensions[1];
  const expandfront = config.coronary_front_expand;
  const expandback = config.coronary_back_expand;

  const labeldata = voxelManager!.getCompleteScalarDataArray!() as Uint8Array;
  if (!imageData?.getPointData().getScalars()) {
    const scalars = vtkDataArray.newInstance({
      name: "",
      values: labeldata,
      numberOfComponents: 1,
    });
    imageData.getPointData().setScalars(scalars);
  }

  const unsharedCenter = bufferPoolItem["centerline"].data as Int32Array;
  const unsharedFcx = bufferPoolItem["fcx"].data as Float32Array;
  const unsharedFcy = bufferPoolItem["fcy"].data as Float32Array;
  const unsharedFcz = bufferPoolItem["fcz"].data as Float32Array;
  const newTypeIds = bufferPoolItem["newType"].data as Int32Array;
  const paramData = bufferPoolItem.parameter.data as CalciumAnalysisResult;
  const newTypes = paramData.SavedInfo.Coronary.newType;

  const unsharedFxx = bufferPoolItem["fxx"].data as Float32Array;
  const unsharedFxy = bufferPoolItem["fxy"].data as Float32Array;
  const unsharedFxz = bufferPoolItem["fxz"].data as Float32Array;
  const unsharedFyx = bufferPoolItem["fyx"].data as Float32Array;
  const unsharedFyy = bufferPoolItem["fyy"].data as Float32Array;
  const unsharedFyz = bufferPoolItem["fyz"].data as Float32Array;

  const segmentationState = segmentation.state;
  const segmentationId = lowQ
    ? `segmentation_${seriesId}_labelmapHalf`
    : `segmentation_${seriesId}_labelmap`;
  const seg = segmentationState.getSegmentation(segmentationId);

  const aortaSet = seg?.cachedStats["aorta"] as Set<number>;
  const arrangeBitMask = seg?.cachedStats["arrangeBitMask"] as Set<number>;

  const aortalabel = Object.entries(arrangeBitMask)
    .filter(([key, _]) => aortaSet?.has(Number(key)))
    .map(([_, value]) => value);

  const totalBlocks = unsharedCenter[0];
  let idx = 1;
  let branch_area = new Map();
  downcheck.current = true;
  for (let id = 0; id < totalBlocks; id++) {
    const blockSize = unsharedCenter[idx++];
    // const blockSize = sharedCenter[idx++];
    // const centerlineIndex = data.slice(idx,idx + blockSize);
    const last = id + 1 === totalBlocks;
    runWorkerTask({
      type: "splitCenterData",
      unsharedCenter,
      labeldata,
      aortalabel,
      idx,
      blockSize,
      unsharedFcx,
      unsharedFcy,
      unsharedFcz,
      unsharedFxx,
      unsharedFxy,
      unsharedFxz,
      unsharedFyx,
      unsharedFyy,
      unsharedFyz,
      spacing,
      direction,
      data_width,
      data_height,
      expandfront,
      expandback,
      newTypeIds,
      newTypes,
      rate,
    })
      // .then(({ data_arr, ori_data_arr, datax, datay, base_arr, brancharea }) => {
      .then(({ data_arr, datax, datay, brancharea }) => {
        // 스플릿
        splitData.data = {
          ...(splitData.data as Record<number, Vector3[]>),
          [id]: data_arr,
        };

        const centerDataResult = createCenterline(
          data_arr,
          spacing,
          origin,
          direction,
        )!;

        // const centerDataResult_ori = createCenterline(
        //   ori_data_arr,
        //   spacingValue,
        //   origin,
        //   direction,
        // )!;

        fxData.data = {
          ...(fxData.data as Record<number, Vector3[]>),
          [id]: datax,
        };

        fyData.data = {
          ...(fyData.data as Record<number, Vector3[]>),
          [id]: datay,
        };

        // 센터
        centerData.data = {
          ...(centerData.data as Record<number, vtkPolyData>),
          [id]: centerDataResult,
        };

        // const clone = vtkPolyData.newInstance({state:structuredClone(centerDataResult.getState())});

        // oriCenterData.data = {
        //   ...(oriCenterData.data as Record<number, vtkPolyData>),
        //   [id]: clone,
        // };

        // oriCenterData.data = {
        //   ...(oriCenterData.data as Record<number, vtkPolyData>),
        //   [id]: centerDataResult_ori,
        // };

        // const branch_area = config.branchArea;
        branch_area.set(id, brancharea);

        if (last) {
          //여기가 atomic이 아니라 걱정
          splitData.loaded = true;
          centerData.loaded = true;
          oriCenterData.loaded = true;
          fxData.loaded = true;
          fyData.loaded = true;
          setData({ branchArea: branch_area });
          setBufferPool((prev) => ({
            ...prev,
            [seriesId]: {
              ...prev[seriesId],
              splitData,
              centerData,
              oriCenterData,
              fxData,
              fyData,
            },
          }));
        }
      })
      .catch((err) => {
        console.error(err);
        splitData.error = true;
        centerData.error = true;
        oriCenterData.error = true;
        fxData.error = true;
        fyData.error = true;
      });
    idx += blockSize;
  }
}

export function applyXMirrorTransform(
  world: [number, number, number],
  centerX: number,
) {
  const x_world = -world[0] + 2 * centerX;
  const focus: [number, number, number] = [-x_world, world[1], world[2]];
  const savedTranslation: [number, number, number] = [
    x_world,
    world[1],
    world[2],
  ];
  return { focus, savedTranslation };
}

export function fitActorToCanvas(
  renderer: vtkRenderer,
  bounds: [number, number, number, number, number, number],
  canvas: HTMLCanvasElement,
  rate: number = 1.1,
) {
  const [xmin, xmax, ymin, ymax, zmin, zmax] = bounds;

  // 세로 크기만 계산
  const w = xmax - xmin;
  const h = zmax - zmin;

  let len = Math.max(w, h);
  const camera = renderer.getActiveCamera();
  camera.setParallelProjection(true);

  const rect = canvas.getBoundingClientRect();
  const vw = rect.width;
  const vh = rect.height;

  const actorAspect = w / h;
  const canvasAspect = vw / vh;

  let parallelScale;
  if (actorAspect > canvasAspect) {
    // 가로가 더 긴 경우
    parallelScale = w / canvasAspect / 2;
  } else {
    // 세로가 더 긴 경우
    parallelScale = h / 2;
  }


  //let scale = Math.max(len / 2, (len / 2) * (vh / vw));
  // camera.setParallelScale(scale * rate);
  camera.setParallelScale(100 * rate);

  renderer.resetCameraClippingRange();
}

export function axesLineRenderer(
  context: React.MutableRefObject<VRContext | SurfaceContext | null>,
  // Pickinfo: PickPoints
) {
  if (!context.current) return;
  // if(!Pickinfo || !Pickinfo.position) return;
  // const position = [-Pickinfo.position[0],Pickinfo.position[1],Pickinfo.position[2]] as [number, number,number];
  createAxesLinesAt({ context });
}

function safeGetResliceOutputData(
  reslice: vtkImageReslice | undefined,
  tag: string,
): vtkImageData | null {
  if (!reslice) return null;
  try {
    const input = (reslice as any).getInputData?.();
    if (!input) {
      console.warn(`[${tag}] reslice input is empty`);
      return null;
    }
    const out = reslice.getOutputData?.() as vtkImageData | null;
    if (!out) return null;
    const scalars = out.getPointData?.()?.getScalars?.();
    if (!scalars) {
      console.warn(`[${tag}] reslice output has no scalars`);
      return null;
    }
    return out;
  } catch (err) {
    console.warn(`[${tag}] getOutputData failed`, err);
    return null;
  }
}

function ensureImageScalars(
  data: vtkImageData,
  mode: "hu" | "label",
  tag: string,
): boolean {
  try {
    const pd = data.getPointData?.();
    if (!pd) return false;
    const existingScalars = pd.getScalars?.();
    if (existingScalars) return true;

    const meta = (
      data as unknown as { get?: (name?: string) => unknown }
    ).get?.("voxelManager") as
      | {
          voxelManager?: {
            getCompleteScalarDataArray?: () => ArrayLike<number>;
            numberOfComponents?: number;
          };
        }
      | undefined;
    const voxelManager = meta?.voxelManager;
    const voxelArray = voxelManager?.getCompleteScalarDataArray?.();

    if (voxelArray && voxelArray.length > 0) {
      const scalars = vtkDataArray.newInstance({
        name: mode,
        values: voxelArray as number[] | ArrayLike<number>,
        numberOfComponents: voxelManager?.numberOfComponents ?? 1,
      });
      pd.setScalars?.(scalars);
      return !!pd.getScalars?.();
    }

    console.warn(`[${tag}] missing ${mode} scalars`);
    return false;
  } catch (err) {
    console.warn(`[${tag}] ensureImageScalars failed`, err);
    return false;
  }
}

function getCrossSectionNonZeroLabelBounds(labelImageData?: vtkImageData | null) {
  const scalars = labelImageData?.getPointData?.().getScalars?.();
  const data = scalars?.getData?.() as ArrayLike<number> | undefined;
  const dimensions = labelImageData?.getDimensions?.() as
    | [number, number, number]
    | undefined;
  if (!data || !dimensions || dimensions.length < 2) return null;

  const width = dimensions[0] ?? 0;
  const height = dimensions[1] ?? 0;
  if (width <= 0 || height <= 0) return null;

  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;

  const limit = Math.min(data.length, width * height);
  for (let i = 0; i < limit; i += 1) {
    if (!data[i]) continue;
    const y = Math.floor(i / width);
    const x = i - y * width;
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }

  if (![minX, minY, maxX, maxY].every(Number.isFinite)) return null;
  return { minX, minY, maxX, maxY, width, height };
}

function fitCrossSectionCameraToImageCenter(
  context: React.MutableRefObject<CrossSectionContext | null>,
  labelImageData?: vtkImageData | null,
) {
  if (!context.current || !labelImageData) return;
  const { renderer, renderWindow } = context.current;
  const camera = renderer?.getActiveCamera?.();
  if (!camera) return;

  const dimensions = labelImageData.getDimensions?.() as
    | [number, number, number]
    | undefined;
  if (!dimensions || dimensions[0] <= 0 || dimensions[1] <= 0) return;

  const spacing =
    (labelImageData.getSpacing?.() as [number, number, number] | undefined) ??
    [1, 1, 1];
  const origin =
    (labelImageData.getOrigin?.() as [number, number, number] | undefined) ??
    [0, 0, 0];

  const container = renderWindow?.getInteractor?.().getContainer?.();
  const viewportWidth = Math.max(1, container?.clientWidth ?? 1);
  const viewportHeight = Math.max(1, container?.clientHeight ?? 1);
  const aspect = viewportWidth / viewportHeight;
  const sx = Math.abs(spacing[0] || 1);
  const sy = Math.abs(spacing[1] || 1);

  const bounds = getCrossSectionNonZeroLabelBounds(labelImageData);
  const labelWidth = bounds
    ? Math.max(1, (bounds.maxX - bounds.minX + 1) * sx)
    : Math.max(1, dimensions[0] * sx * 0.18);
  const labelHeight = bounds
    ? Math.max(1, (bounds.maxY - bounds.minY + 1) * sy)
    : Math.max(1, dimensions[1] * sy * 0.18);
  const visibleHeight = Math.max(
    labelHeight * 3.25,
    (labelWidth / Math.max(0.25, aspect)) * 3.25,
    Math.min(44, Math.max(labelWidth, labelHeight) * 5.25),
  );
  const currentScale = camera.getParallelScale?.() as number | undefined;
  if (!Number.isFinite(currentScale) || (currentScale as number) <= 0) return;

  const nextParallelScale = Math.min(
    currentScale as number,
    Math.max(7, visibleHeight * 0.2),
  );

  // Server frame_vectors cross section is generated with the target point at the
  // reslice image center.  Keep the camera centered on that image center so the
  // initial zoom behaves like the legacy view and does not drift toward an
  // asymmetric label bounding box.  The label bounds are still used above only
  // to choose a reasonable zoom level.
  const centerX = origin[0] + (dimensions[0] - 1) * 0.5 * spacing[0];
  const centerY = origin[1] + (dimensions[1] - 1) * 0.5 * spacing[1];
  const focal = camera.getFocalPoint?.() ?? [0, 0, 0];
  const position = camera.getPosition?.() ?? [0, 0, 1];
  const deltaX = centerX - focal[0];
  const deltaY = centerY - focal[1];

  camera.setParallelScale(nextParallelScale);
  camera.setFocalPoint(centerX, centerY, focal[2]);
  camera.setPosition(position[0] + deltaX, position[1] + deltaY, position[2]);
  renderer?.resetCameraClippingRange?.();
}

export async function createCrossSection(
  context: React.MutableRefObject<CrossSectionContext | null>,
  // seriesId: string,
  imageData: vtkImageData,
  labelData: vtkImageData,
  bufferpool: BufferPoolItem,
  branchId: number,
  currentVOI: VOI,
  analysistype?: AnalysisType,
  colorPoints?: Array<[number, number, number, number]>,
  opacityPoints?: Array<[number, number]>,
  enableEditableOutline: boolean = false,
  axesOverride?: mat4,
) {
  if (!context.current) return;
  const { renderWindow, renderer, actorIds } = context.current;
  // const {imageData} = getOriginalVolumeFromSeriesId(seriesId)!;
  if (!imageData || !labelData || !bufferpool) return;
  if (!renderer) return;
  if (!ensureImageScalars(imageData, "hu", "createCrossSection:imageData"))
    return;
  const hasLabelScalars = ensureImageScalars(
    labelData,
    "label",
    "createCrossSection:labelData",
  );

  const useServerFrontDataGuard = isServerFrontDataVtkMode(
    bufferpool,
    analysistype,
  );
  renderer.removeAllActors();
  if (useServerFrontDataGuard) {
    Object.keys(actorIds).forEach((actorKey) => {
      delete actorIds[actorKey];
    });
    (context.current as CrossSectionContext & {
      analysisType?: AnalysisType;
      branchId?: number;
      serverFrontDataVtkMode?: boolean;
    }).analysisType = analysistype;
    (context.current as CrossSectionContext & {
      analysisType?: AnalysisType;
      branchId?: number;
      serverFrontDataVtkMode?: boolean;
    }).branchId = branchId;
    (context.current as CrossSectionContext & {
      analysisType?: AnalysisType;
      branchId?: number;
      serverFrontDataVtkMode?: boolean;
      serverFrontDataCameraFitOnInit?: boolean;
    }).serverFrontDataVtkMode = true;
    (context.current as CrossSectionContext & {
      analysisType?: AnalysisType;
      branchId?: number;
      serverFrontDataVtkMode?: boolean;
      serverFrontDataCameraFitOnInit?: boolean;
    }).serverFrontDataCameraFitOnInit = false;
  } else {
    (context.current as CrossSectionContext & {
      analysisType?: AnalysisType;
      branchId?: number;
      serverFrontDataVtkMode?: boolean;
    }).analysisType = undefined;
    (context.current as CrossSectionContext & {
      analysisType?: AnalysisType;
      branchId?: number;
      serverFrontDataVtkMode?: boolean;
    }).branchId = undefined;
    (context.current as CrossSectionContext & {
      analysisType?: AnalysisType;
      branchId?: number;
      serverFrontDataVtkMode?: boolean;
      serverFrontDataCameraFitOnInit?: boolean;
    }).serverFrontDataVtkMode = false;
    (context.current as CrossSectionContext & {
      analysisType?: AnalysisType;
      branchId?: number;
      serverFrontDataVtkMode?: boolean;
      serverFrontDataCameraFitOnInit?: boolean;
    }).serverFrontDataCameraFitOnInit = false;
  }

  const centerData = (
    bufferpool.centerData.data as Record<number, vtkPolyData>
  )[branchId];
  if (!centerData) {
    console.warn(
      `[createCrossSection] no centerline data. analysisType=${analysistype}, branchId=${branchId}`,
    );
    return;
  }

  const index = 0 + configStore.get().coronary_front_expand;
  const { renderingQuality } = configStore.get().generalSetting;
  const rate = renderingQuality !== "high" ? 0.5 : 1;
  const basewidth =
    (analysistype === "aorticValveCalcium" || analysistype === "tavi"
      ? configStore.get().aorta_basewidth
      : configStore.get().basewidth) * rate;

  const axes =
    axesOverride ??
    CalculateResliceAxes(
      bufferpool,
      branchId,
      index,
      analysistype,
      centerData,
      imageData,
    );
  if (!Array.from(axes).every((v) => Number.isFinite(v))) {
    console.warn(
      `[createCrossSection] invalid reslice axes. analysisType=${analysistype}, branchId=${branchId}, index=${index}`,
    );
    return;
  }

  const {
    mapper: CSMapper,
    actor: CSActor,
    reslice: CSReslice,
  } = createCrossSectionActorAndMapper({
    data: imageData,
    size: basewidth,
    axes: axes,
    currentVOI,
    upscale:
      analysistype === "aorticValveCalcium" || analysistype === "tavi"
        ? undefined
        : configStore.get().upscale,
  });

  const paintBundle = hasLabelScalars
    ? createCrossSectionActorAndMapper({
        data: labelData,
        size: basewidth,
        axes: axes,
        currentVOI,
        isPaintData: true,
        colorPoints: colorPoints,
        opacityPoints: opacityPoints,
        upscale:
          analysistype === "aorticValveCalcium" || analysistype === "tavi"
            ? undefined
            : configStore.get().upscale,
      })
    : null;
  const CSPaintMapper = paintBundle?.mapper;
  const CSPaintActor = paintBundle?.actor;
  const CSPaintReslice = paintBundle?.reslice;

  renderer.addActor(CSActor);
  actorIds["CSActor"] = CSActor;

  //if (analysistype === 'fai') {
  if (CSPaintActor) {
    renderer.addActor(CSPaintActor);
    actorIds["CSPaintActor"] = CSPaintActor;
  } else {
    delete actorIds["CSPaintActor"];
  }
  if (CSPaintReslice && CSPaintMapper) {
    if (analysistype !== "aorticValveCalcium" && analysistype !== "tavi") {
      // aorticValveCalcium는 upscale 필요없음
      const updata = rowdataUpscaleFunction({
        reslice: CSPaintReslice,
        upscale: configStore.get().upscale,
        isPaintData: true,
      });
      if (updata) {
        CSPaintMapper.setInputData(updata);
      }

      if (
        analysistype === "plaque"
        // || analysistype === 'stenosis'
        // || analysistype === 'isr'
      ) {
        if (updata) {
          const wallpts = getVtkImageOutlinePoints(updata, [1, 2, 3, 255]);

          // const wallfill = createOutlineActorFromPoints(
          //   wallpts,
          //   hexToNormalizedRGB(config.colors.NONCALCIFIED),
          //   true,
          // );
          // actorIds['wallfill'] = wallfill;
          // renderer.addActor(wallfill);

          // const lowpts = getVtkImageOutlinePoints(
          //   updata,
          //   [1],
          //   // true,
          // );
          // const lowfill = createOutlineActorFromPoints(
          //   lowpts,
          //   hexToNormalizedRGB(config.colors.LOWATTENUATION),
          //   true,
          // );
          // actorIds['lowfill'] = lowfill;
          // renderer.addActor(lowfill);

          // const calpts = getVtkImageOutlinePoints(
          //   updata,
          //   [3],
          //   // true,
          // );
          // const calfill = createOutlineActorFromPoints(
          //   calpts,
          //   hexToNormalizedRGB(config.colors.CALCIUM),
          // );
          // actorIds['calfill'] = calfill;
          // renderer.addActor(calfill);

          const wallOutline = createOutlineActorFromPoints(
            wallpts,
            hexToNormalizedRGB(configStore.get().colors.walloutline),
            false,
            1,
          );
          actorIds["wallOutline"] = wallOutline;
          renderer.addActor(wallOutline);
        }
      }

      if (updata) {
        const lumenpts = useServerFrontDataGuard
          ? getFastVtkImageBoundaryOutlinePoints(updata, [255])
          : getVtkImageOutlinePoints(updata, [255]);
        // const lumenfill = createOutlineActorFromPoints(
        //   lumenpts,
        //   hexToNormalizedRGB(config.colors.LUMEN),
        //   true,
        // );
        // actorIds['lumenfill'] = lumenfill;
        // renderer.addActor(lumenfill);

        const lumenOutline = createOutlineActorFromPoints(
          lumenpts,
          hexToNormalizedRGB(configStore.get().colors.lumenoutline),
          false,
          1,
        );
        actorIds["lumenOutline"] = lumenOutline;
        renderer.addActor(lumenOutline);
      }
    } else {
      const paintRawData = safeGetResliceOutputData(
        CSPaintReslice,
        "createCrossSection:CSPaintReslice",
      );
      if (paintRawData) {
        CSPaintMapper.setInputData(paintRawData);
      }
    }
  }

  const paintInputForTest = CSPaintMapper?.getInputData?.() as
    | vtkImageData
    | undefined;
  if (enableEditableOutline && paintInputForTest) {
    const primaryValues =
      analysistype === "aorticValveCalcium" || analysistype === "tavi"
        ? getAortaOnlyLabelValues()
        : getNonZeroLabelValues(paintInputForTest);
    let testOutlinePoints = buildEditableOutlinePoints(
      paintInputForTest,
      primaryValues,
      6,
    );
    if (!hasOutlinePoints(testOutlinePoints)) {
      testOutlinePoints = buildEditableOutlinePoints(
        paintInputForTest,
        getNonZeroLabelValues(paintInputForTest),
        6,
      );
    }
    if (hasOutlinePoints(testOutlinePoints)) {
      const allLabelOutlineTest = createOutlineActorFromPoints(
        testOutlinePoints,
        [0.47, 1.0, 0.23],
        false,
        1,
      );
      actorIds["allLabelOutlineTest"] = allLabelOutlineTest;
      renderer.addActor(allLabelOutlineTest);

      const allLabelOutlineTestHandles = createOutlineHandleActorFromPoints(
        testOutlinePoints,
        [0.47, 1.0, 0.23],
        4,
      );
      actorIds["allLabelOutlineTestHandles"] = allLabelOutlineTestHandles;
      renderer.addActor(allLabelOutlineTestHandles);
    }
  } else {
    const oldOutlineActor = actorIds["allLabelOutlineTest"] as
      | vtkActor
      | undefined;
    if (oldOutlineActor) {
      renderer.removeActor(oldOutlineActor);
      delete actorIds["allLabelOutlineTest"];
    }
    const oldHandleActor = actorIds["allLabelOutlineTestHandles"] as
      | vtkActor
      | undefined;
    if (oldHandleActor) {
      renderer.removeActor(oldHandleActor);
      delete actorIds["allLabelOutlineTestHandles"];
    }
    const oldLongAxisActor = actorIds["allLabelLongAxis"] as
      | vtkActor
      | undefined;
    if (oldLongAxisActor) {
      renderer.removeActor(oldLongAxisActor);
      delete actorIds["allLabelLongAxis"];
    }
    const oldShortAxisActor = actorIds["allLabelShortAxis"] as
      | vtkActor
      | undefined;
    if (oldShortAxisActor) {
      renderer.removeActor(oldShortAxisActor);
      delete actorIds["allLabelShortAxis"];
    }
  }

  renderer.resetCamera();
  renderer.getActiveCamera().setParallelProjection(true);
  if (axesOverride && paintInputForTest) {
    fitCrossSectionCameraToImageCenter(context, paintInputForTest);
    if (useServerFrontDataGuard) {
      context.current.serverFrontDataCameraFitOnInit = true;
    }
  }

  const camera = renderer.getActiveCamera();

  // cswindow.render();
  // context.current = {
  //   ...context.current,
  //   imageData,
  //   centerlineData,
  //   labelData,
  //   CSReslice,
  //   CSPaintReslice,
  //   index,
  //   zoom: camera.getParallelScale(),
  //   width: basewidth,
  //   height: basewidth,
  //   spacing: imageData.getSpacing(),
  //   markerline_visibility: true,
  //   colorPoints: colorPoints,
  //   opacityPoints: opacityPoints,
  // };

  context.current["imageData"] = imageData;
  context.current["centerlineData"] = centerData;
  context.current["labelData"] = labelData;
  context.current["CSReslice"] = CSReslice;
  context.current["CSPaintReslice"] = CSPaintReslice;
  context.current["index"] = index;
  context.current["zoom"] = camera.getParallelScale();
  context.current["width"] = basewidth;
  context.current["height"] = basewidth;
  context.current["spacing"] = imageData.getSpacing();
  context.current["markerline_visibility"] = true;
  context.current["colorPoints"] = colorPoints;
  context.current["opacityPoints"] = opacityPoints;

  //renderWindow.render();
  // scheduleRender(className!, { type: 'updateRenderWindow' });
}

export function getTaviCenterlinePolyData(
  bufferpool: BufferPoolItem,
  imageData: vtkImageData,
) {
  const parsed = (bufferpool as Record<string, any>)["tavi.parsed"]?.data as
    | Record<string, unknown>
    | undefined;
  const item = (bufferpool as Record<string, any>)["tavi.centerline"];
  const raw = parsed?.centerline ?? item?.data;
  const buf = toArrayBufferForTavi(raw);
  if (!buf || buf.byteLength < 24) return null;

  const f64 = new Float64Array(buf);
  if (f64.length < 6) return null;
  const { renderingQuality } = configStore.get().generalSetting;
  const lowQ = renderingQuality !== "high";
  const buildPoly = (voxelScale: number) => {
    const flat = new Float32Array(f64.length);
    // Server payload: [x, y, z]
    for (let i = 0; i + 2 < f64.length; i += 3) {
      flat[i + 0] = f64[i + 0] * voxelScale; // x
      flat[i + 1] = f64[i + 1] * voxelScale; // y
      flat[i + 2] = f64[i + 2] * voxelScale; // z
    }
    const polyData = createCenterline(
      flat,
      imageData.getSpacing() as [number, number, number],
      imageData.getOrigin() as [number, number, number],
      imageData.getDirection() as any,
    );
    if (polyData) {
      applyTaviOrientationTensors(polyData, bufferpool, imageData);
    }
    return polyData;
  };

  const scale = lowQ ? 0.5 : 1.0;
  const polyData = buildPoly(scale);
  if (!polyData) return null;
  return polyData;
}

export function getTaviCenterTangentAtIndex(
  bufferpool: BufferPoolItem,
  index: number,
  imageData?: vtkImageData,
): Vector3 | null {
  const parsed = (bufferpool as Record<string, any>)["tavi.parsed"]?.data as
    | Record<string, unknown>
    | undefined;
  const item = (bufferpool as Record<string, any>)["tavi.centerTangent"];
  const raw = parsed?.centerTangent ?? item?.data;
  if (!raw || index < 0) return null;

  let arr: Float64Array | null = null;
  if (raw instanceof ArrayBuffer) {
    arr = new Float64Array(raw);
  } else if (ArrayBuffer.isView(raw)) {
    const v = raw as ArrayBufferView;
    arr = new Float64Array(
      v.buffer,
      v.byteOffset,
      Math.floor(v.byteLength / Float64Array.BYTES_PER_ELEMENT),
    );
  }
  if (!arr) return null;

  const p = index * 3;
  if (p + 2 >= arr.length) return null;

  // Server tangent payload: [x, y, z]
  const tangent = vec3.fromValues(arr[p + 0], arr[p + 1], arr[p + 2]);

  if (imageData) {
    const spacing = imageData.getSpacing() as [number, number, number];
    const direction = imageData.getDirection() as mat3;
    const scaled = vec3.fromValues(
      tangent[0] * spacing[0],
      tangent[1] * spacing[1],
      tangent[2] * spacing[2],
    );
    vec3.transformMat3(tangent, scaled, direction);
  }

  if (vec3.sqrLen(tangent) < 1e-12) return null;
  vec3.normalize(tangent, tangent);
  return [tangent[0], tangent[1], tangent[2]];
}

export function uniqueValuesUint8_simple(arr: Uint8Array): number[] {
  return Array.from(new Set(arr)).sort((a, b) => a - b);
}

function getNonZeroLabelValues(data: vtkImageData): number[] {
  const scalars = data.getPointData().getScalars();
  if (!scalars) return [];
  const arr = scalars.getData() as Uint8Array;
  const seen = new Uint8Array(256);
  const values: number[] = [];
  for (let i = 0; i < arr.length; i++) {
    const v = arr[i];
    if (v > 0 && seen[v] === 0) {
      seen[v] = 1;
      values.push(v);
    }
  }
  return values;
}

function getAortaOnlyLabelValues(): number[] {
  const cfg = configStore.get();
  const aorta = cfg.aorta_indexs ?? [];
  const avcSet = new Set(cfg.avc_indexs ?? []);
  const filtered = aorta.filter((v) => v > 0 && !avcSet.has(v));
  return filtered.length > 0 ? filtered : aorta.filter((v) => v > 0);
}

function simplifyOutlines(
  outlines: [number, number][][],
  step: number = 6,
): [number, number][][] {
  const safeStep = Math.max(2, step | 0);
  return outlines
    .map((outline) => {
      if (!outline || outline.length <= safeStep + 1) return outline ?? [];
      const reduced: [number, number][] = [];
      for (let i = 0; i < outline.length; i += safeStep) {
        reduced.push(outline[i]);
      }
      const last = outline[outline.length - 1];
      const tail = reduced[reduced.length - 1];
      if (!tail || tail[0] !== last[0] || tail[1] !== last[1])
        reduced.push(last);
      return reduced;
    })
    .filter((outline) => outline.length >= 2);
}

function hasOutlinePoints(outlines: [number, number][][]): boolean {
  return outlines.some((o) => o && o.length >= 2);
}

function buildEditableOutlinePoints(
  data: vtkImageData,
  values: number[],
  simplifyStep: number = 6,
): [number, number][][] {
  if (!values.length) return [];
  const centerOutlines = getVtkImageOutlinePoints(data, values, true);
  const centerSimplified = simplifyOutlines(centerOutlines, simplifyStep);
  if (hasOutlinePoints(centerSimplified)) return centerSimplified;

  const fullOutlines = getVtkImageOutlinePoints(data, values, false);
  return simplifyOutlines(fullOutlines, simplifyStep);
}

function createOutlineHandleActorFromPoints(
  outlines: [number, number][][],
  color: [number, number, number] = [0.47, 1.0, 0.23],
  pointSize: number = 4,
) {
  const points = vtkPoints.newInstance();
  const verts = vtkCellArray.newInstance();

  let pointIdx = 0;
  outlines.forEach((outline) => {
    outline.forEach(([x, y]) => {
      points.insertNextPoint(x, y, 0);
      verts.insertNextCell([pointIdx]);
      pointIdx++;
    });
  });

  const polyData = vtkPolyData.newInstance();
  polyData.setPoints(points);
  polyData.setVerts(verts);

  const mapper = vtkMapper.newInstance();
  mapper.setInputData(polyData);

  const actor = vtkActor.newInstance();
  actor.setMapper(mapper);
  actor.getProperty().setColor(color);
  actor.getProperty().setPointSize(pointSize);
  actor.getProperty().setRepresentationToPoints();

  return actor;
}

function createOutlineHandleMapper(
  outlines: [number, number][][],
  oldMapper?: vtkMapper | null,
) {
  const polyData = oldMapper
    ? (oldMapper.getInputData() as vtkPolyData)
    : vtkPolyData.newInstance();
  const points = vtkPoints.newInstance();
  const verts = vtkCellArray.newInstance();
  let pointIdx = 0;
  outlines.forEach((outline) => {
    outline.forEach(([x, y]) => {
      points.insertNextPoint(x, y, 0);
      verts.insertNextCell([pointIdx]);
      pointIdx++;
    });
  });
  polyData.setPoints(points);
  polyData.setVerts(verts);

  if (oldMapper) {
    oldMapper.setInputData(polyData);
    return oldMapper;
  }
  const mapper = vtkMapper.newInstance();
  mapper.setInputData(polyData);
  return mapper;
}

export function countValueUint8(arr: Uint8Array, target: number): number {
  if ((target | 0) !== target || target < 0 || target > 255) {
    throw new RangeError("target must be an integer 0..255");
  }
  let cnt = 0;
  for (let i = 0; i < arr.length; i++) {
    // 타입이 Uint8이므로 === 비교만 하면 됩니다.
    if (arr[i] === target) cnt++;
  }
  return cnt;
}

export async function updateMidCrossSection(
  context: React.MutableRefObject<CrossSectionContext | null>,
  bufferpool: BufferPoolItem,
  branchId: number,
  newIndex: number,
  analysistype: AnalysisType,
  upscale: number,
  enableEditableOutline: boolean = false,
  axesOverride?: mat4,
) {
  if (!context.current) return;
  const { renderer, actorIds, CSPaintReslice, CSReslice, rendererId } =
    context.current;
  const currentCrossSectionState = context.current as CrossSectionContext & {
    analysisType?: AnalysisType;
    branchId?: number;
    serverFrontDataVtkMode?: boolean;
  };
  if (currentCrossSectionState.serverFrontDataVtkMode) {
    if (
      currentCrossSectionState.analysisType &&
      currentCrossSectionState.analysisType !== analysistype
    ) {
      return;
    }
    if (
      Number.isFinite(currentCrossSectionState.branchId) &&
      currentCrossSectionState.branchId !== branchId
    ) {
      return;
    }
  }
  if (!bufferpool || !CSReslice) return;
  if (newIndex < 0) newIndex = 0;
  const huImageData = context.current["imageData"] as vtkImageData | undefined;
  const paintImageData = context.current["labelData"] as
    | vtkImageData
    | undefined;
  if (!huImageData) return;
  if (
    !ensureImageScalars(huImageData, "hu", "updateMidCrossSection:imageData")
  ) {
    return;
  }
  const hasLabelScalars =
    !!paintImageData &&
    ensureImageScalars(
      paintImageData,
      "label",
      "updateMidCrossSection:labelData",
    );

  const centerData =
    (context.current["centerlineData"] as vtkPolyData | undefined) ??
    (bufferpool.centerData.data as Record<number, vtkPolyData>)[branchId];

  if (!axesOverride) {
    const maxindex = centerData.getPointData().getTensors().getNumberOfTuples();
    if (maxindex <= newIndex) newIndex = maxindex - 1;
  }

  if (CSReslice) {
    if (CSReslice?.setResliceAxes) {
      const axes =
        axesOverride ??
        CalculateResliceAxes(
          bufferpool,
          branchId,
          newIndex,
          analysistype,
          centerData,
          context.current["imageData"] as vtkImageData | undefined,
        );
      CSReslice.setResliceAxes(axes);
      if (CSPaintReslice && hasLabelScalars) {
        CSPaintReslice.setResliceAxes(axes);
      }

      if (analysistype !== "aorticValveCalcium" && analysistype !== "tavi") {
        // aorticValveCalcium는 upscale 필요없음
        const uphu = rowdataUpscaleFunction({
          reslice: CSReslice,
          upscale: upscale,
          isPaintData: false,
        });
        const huActor = actorIds["CSActor"] as vtkImageSlice;
        const humapper = huActor.getMapper();
        if (uphu) humapper.setInputData(uphu);

        const updata =
          CSPaintReslice && hasLabelScalars
            ? rowdataUpscaleFunction({
                reslice: CSPaintReslice,
                upscale: upscale,
                isPaintData: true,
              })
            : null;

        // 단면 리얼 area 계산
        //       const reslicedImage = CSPaintReslice.getOutputData();
        // const labelArray = reslicedImage.getPointData().getScalars().getData() as Uint8Array;

        // // 1) 단면에서 카운트(예: label=255가 채워진 픽셀 수)
        // const dAreaCount = countValueUint8(labelArray, 255);

        // // 2) 스케일 (planar = pixel spacing, vertical = slice spacing)
        // //    보통 in-plane은 X/Y 동일하지만 다를 수 있음. 요구식은 동일 pScale을 사용하므로 X spacing을 대표로 사용.
        // const [sx, sy, sz] = reslicedImage.getSpacing(); // [pX, pY, v]
        // const pScale = sx;     // 필요에 따라 (sx+sy)/2 로 바꿔도 됨
        // const vScale = sz;

        // // 3) fx, fy 추출 (gl-matrix mat4: column-major, translation at [12..14])
        // //    첫 번째 컬럼 = fx, 두 번째 컬럼 = fy
        // const fxX = axes[0], fxY = axes[1], fxZ = axes[2];
        // const fyX = axes[4], fyY = axes[5], fyZ = axes[6];
        // const fcX = axes[12], fcY = axes[13], fcZ = axes[14];

        // // 4) 실공간 단위로 변환된 in-plane 스텝 크기
        // //    fShowRealX = sqrt( (fxX*pScale)^2 + (fxY*pScale)^2 + (fxZ*vScale)^2 )
        // //    fShowRealY = sqrt( (fyX*pScale)^2 + (fyY*pScale)^2 + (fyZ*vScale)^2 )
        // const fShowRealX = Math.hypot(fxX * pScale, fxY * pScale, fxZ * vScale);
        // const fShowRealY = Math.hypot(fyX * pScale, fyY * pScale, fyZ * vScale);

        // // 5) 면적 = 카운트 * (in-plane 스텝X * in-plane 스텝Y)
        // const dShowAreaCount = dAreaCount * fShowRealX * fShowRealY;

        // console.log('dShowAreaCount:', dShowAreaCount, 'newIndex', newIndex);

        const paintActor = actorIds["CSPaintActor"] as
          | vtkImageSlice
          | undefined;
        const paintmapper = paintActor?.getMapper?.();
        if (updata && paintmapper) {
          const displayPaintData = updata;
          paintmapper.setInputData(displayPaintData);
          if (
            axesOverride &&
            !currentCrossSectionState.serverFrontDataVtkMode
          ) {
            fitCrossSectionCameraToImageCenter(context, displayPaintData);
          }
        }

        if (
          updata &&
          analysistype === "plaque"
          // || analysistype === 'stenosis'
          // || analysistype === 'isr'
        ) {
          const wallpts = getVtkImageOutlinePoints(updata, [1, 2, 3, 255]);

          // const wallfill = actorIds['wallfill'] as vtkActor;
          // if (wallfill) {
          //   wallfill.setMapper(
          //     createOutlineMapper(wallpts, wallfill.getMapper(), true),
          //   );
          // }

          // const lowfill = actorIds['lowfill'] as vtkActor;
          // if (lowfill) {
          //   console.log('update low');
          //   const lowpts = getVtkImageOutlinePoints(
          //     updata,
          //     [1],
          //     // true,
          //   );
          //   lowfill.setMapper(
          //     createOutlineMapper(lowpts, lowfill.getMapper(), true),
          //   );
          // }
          // const calfill = actorIds['calfill'] as vtkActor;
          // if (calfill) {
          //   console.log('update cal');
          //   const callpts = getVtkImageOutlinePoints(
          //     updata,
          //     [3],
          //     // true,
          //   );
          //   calfill.setMapper(
          //     createOutlineMapper(callpts, calfill.getMapper(), true),
          //   );
          // }

          const wallOutline = actorIds["wallOutline"] as vtkActor;
          if (wallOutline) {
            wallOutline.setMapper(
              createOutlineMapper(wallpts, wallOutline.getMapper()),
            );
          }
        }

        const lumenOutline = actorIds["lumenOutline"] as vtkActor;
        if (lumenOutline && updata) {
          const lumenpts = currentCrossSectionState.serverFrontDataVtkMode
            ? getFastVtkImageBoundaryOutlinePoints(updata, [255])
            : getVtkImageOutlinePoints(updata, [255]);
          // const lumenfill = actorIds['lumenfill'] as vtkActor;
          // if (lumenfill) {
          //   lumenfill.setMapper(
          //     createOutlineMapper(lumenpts, lumenfill.getMapper(), true),
          //   );
          // }

          lumenOutline.setMapper(
            createOutlineMapper(lumenpts, lumenOutline.getMapper()),
          );
        }
      } else {
        const huActor = actorIds["CSActor"] as vtkImageSlice;
        const huMapper = huActor?.getMapper();
        const huData = safeGetResliceOutputData(
          CSReslice,
          "updateMidCrossSection:CSReslice",
        );
        if (huMapper && huData) huMapper.setInputData(huData);

        if (CSPaintReslice && hasLabelScalars) {
          const paintActor = actorIds["CSPaintActor"] as
            | vtkImageSlice
            | undefined;
          const paintMapper = paintActor?.getMapper?.();
          const paintData = safeGetResliceOutputData(
            CSPaintReslice,
            "updateMidCrossSection:CSPaintReslice",
          );
          if (paintMapper && paintData) {
            paintMapper.setInputData(paintData);
          }
        }
      }
    }
  }

  //const lumenOutline = actorIds['lumenOutline'] as vtkActor;
  //if (lumenOutline) {
  //  const lumenpts = getvtkoutlinepoints(
  //    CSPaintReslice,
  //    [255],
  //    basewidth,
  //    true,
  //  );
  //  lumenOutline.setMapper(
  //    createOutlineMapper(lumenpts, lumenOutline.getMapper()),
  //  );
  //}

  // const wallOutline = actorIds['wallOutline'] as vtkActor;
  // if (analysistype === 'plaque' && wallOutline) {
  //   const wallpts = getvtkoutlinepoints(
  //     CSPaintReslice,
  //     [1, 2, 3, 255],
  //     basewidth,
  //     true,
  //   );
  //   wallOutline.setMapper(
  //     createOutlineMapper(wallpts, wallOutline.getMapper()),
  //   );
  // }
  const paintActor = actorIds["CSPaintActor"] as vtkImageSlice;
  const paintMapper = paintActor?.getMapper();
  const paintInputForTest = paintMapper?.getInputData() as vtkImageData;
  if (enableEditableOutline && paintInputForTest) {
    const primaryValues =
      analysistype === "aorticValveCalcium" || analysistype === "tavi"
        ? getAortaOnlyLabelValues()
        : getNonZeroLabelValues(paintInputForTest);
    let testOutlinePointsWithCenter = buildEditableOutlinePoints(
      paintInputForTest,
      primaryValues,
      6,
    );
    if (!hasOutlinePoints(testOutlinePointsWithCenter)) {
      testOutlinePointsWithCenter = buildEditableOutlinePoints(
        paintInputForTest,
        getNonZeroLabelValues(paintInputForTest),
        6,
      );
    }
    const testOutlineActor = actorIds["allLabelOutlineTest"] as vtkActor;
    const testOutlineHandlesActor = actorIds[
      "allLabelOutlineTestHandles"
    ] as vtkActor;
    if (testOutlineActor) {
      testOutlineActor.setMapper(
        createOutlineMapper(
          testOutlinePointsWithCenter,
          testOutlineActor.getMapper(),
        ),
      );
      if (testOutlineHandlesActor) {
        testOutlineHandlesActor.setMapper(
          createOutlineHandleMapper(
            testOutlinePointsWithCenter,
            testOutlineHandlesActor.getMapper() as vtkMapper,
          ),
        );
      }
    } else if (hasOutlinePoints(testOutlinePointsWithCenter)) {
      const newTestOutlineActor = createOutlineActorFromPoints(
        testOutlinePointsWithCenter,
        [0.47, 1.0, 0.23],
        false,
        1,
      );
      actorIds["allLabelOutlineTest"] = newTestOutlineActor;
      renderer.addActor(newTestOutlineActor);
      const newTestOutlineHandlesActor = createOutlineHandleActorFromPoints(
        testOutlinePointsWithCenter,
        [0.47, 1.0, 0.23],
        4,
      );
      actorIds["allLabelOutlineTestHandles"] = newTestOutlineHandlesActor;
      renderer.addActor(newTestOutlineHandlesActor);
    }
  } else {
    const oldOutlineActor = actorIds["allLabelOutlineTest"] as
      | vtkActor
      | undefined;
    if (oldOutlineActor) {
      renderer.removeActor(oldOutlineActor);
      delete actorIds["allLabelOutlineTest"];
    }
    const oldHandleActor = actorIds["allLabelOutlineTestHandles"] as
      | vtkActor
      | undefined;
    if (oldHandleActor) {
      renderer.removeActor(oldHandleActor);
      delete actorIds["allLabelOutlineTestHandles"];
    }
    const oldLongAxisActor = actorIds["allLabelLongAxis"] as
      | vtkActor
      | undefined;
    if (oldLongAxisActor) {
      renderer.removeActor(oldLongAxisActor);
      delete actorIds["allLabelLongAxis"];
    }
    const oldShortAxisActor = actorIds["allLabelShortAxis"] as
      | vtkActor
      | undefined;
    if (oldShortAxisActor) {
      renderer.removeActor(oldShortAxisActor);
      delete actorIds["allLabelShortAxis"];
    }
  }
  // renderWindow.render();

  // context.current = {
  //   ...context.current,
  //   index: newIndex,
  //   // prevIndex: pickinfo.index,
  // };

  context.current["index"] = newIndex;
  //요기는 왜 안넘어갈까
  // scheduleRender(className!, { type: 'updateRenderWindow' });
  requestAnimationFrame(() => {
    scheduleRender(rendererId, { type: "updateRenderWindow" });
  });
}

export function moveCamera(
  context: React.MutableRefObject<Context | null>,
  deltaX: number,
  deltaY: number,
) {
  if (!context.current) return;
  const { renderer, renderWindow, viewportId } = context.current;
  const camera = renderer.getActiveCamera();
  const interactor = renderWindow.getInteractor();
  const container = interactor.getContainer();
  const width = container?.clientWidth ?? 512;
  const height = container?.clientHeight ?? 512;

  const parallelScale = camera.getParallelScale();
  const worldPerPixel = (2 * parallelScale) / height;

  let moveXWorld = deltaX * worldPerPixel;
  let moveYWorld = deltaY * worldPerPixel;

  MoveImageAndCamera(renderWindow, renderer, moveXWorld, moveYWorld);

  if (viewportId === "CPR") {
    const cprContext = context as React.MutableRefObject<CPRContext>;
    const { rendererId } = cprContext.current;

    cprContext.current.overlayTranslation![0] += deltaX;
    cprContext.current.overlayTranslation![1] += deltaY;

    vec3.add(
      cprContext.current.savedTranslation,
      cprContext.current.savedTranslation,
      [moveXWorld, moveYWorld, 0],
    );
    cprContext.current.moveCountY! += moveYWorld;

    scheduleRender(
      `MarkerLine.${rendererId?.split(".")[1]}.${cprContext.current.mode}`,
      { type: "panelmove" },
    );
  }
}

export async function createCPR(
  context: React.MutableRefObject<CPRContext | null>,
  imageData: vtkImageData,
  // seriesId: string,
  labelData: vtkImageData,
  centerlineData: vtkPolyData,
  mode: "stretched" | "straightened",
  analysisType: AnalysisType,
  currentVOI: VOI,
  colorPoints?: Array<[number, number, number, number]>,
  opacityPoints?: Array<[number, number]>,
) {
  if (!context.current) return;
  if (centerlineData === undefined) return;
  if (!imageData || !labelData) return;
  if (
    !centerlineData.getPoints ||
    centerlineData.getPoints().getNumberOfPoints() < 2
  )
    return;

  // const {imageData} = getOriginalVolumeFromSeriesId(seriesId)!;
  const { renderWindow, renderer, actorIds, angle, axis } = context.current;
  // axis 계산 (회전축)

  if (!renderer || !imageData) return;
  if (!ensureImageScalars(imageData, "hu", "createCPR:imageData")) return;
  const hasLabelScalars = ensureImageScalars(
    labelData,
    "label",
    "createCPR:labelData",
  );
  renderer.removeAllActors();
  let new_axis = axis!;

  const maxindex = centerlineData.getPoints().getNumberOfPoints();
  const startpoint = centerlineData
    .getPoints()
    .getPoint(configStore.get().coronary_front_expand);
  const endpoint = centerlineData
    .getPoints()
    .getPoint(maxindex - 1 - configStore.get().coronary_back_expand);
  vec3.sub(
    new_axis,
    [endpoint[0], endpoint[1], endpoint[2]],
    [startpoint[0], startpoint[1], startpoint[2]],
  );
  vec3.normalize(new_axis, new_axis);
  if (mode === "straightened") new_axis = [0, 0, 1];
  new_axis = [0, 0, 1];
  const width_size =
    mode === "stretched"
      ? 300
      : analysisType === "aorticValveCalcium"
        ? 150
        : 100;

  const { mapper: CPRMapper, actor: CPRActor } = createCPRActorAndMapper({
    data: imageData,
    mode,
    width: width_size,
    centerline: centerlineData,
    currentVOI,
  });

  const paintBundle = hasLabelScalars
    ? createCPRActorAndMapper({
        mode: mode,
        data: labelData,
        width: width_size,
        centerline: centerlineData,
        currentVOI,
        isPaintData: true,
        colorPoints,
        opacityPoints,
      })
    : null;
  const CPRPaintActor = paintBundle?.actor;

  renderer.addActor(CPRActor);
  if (CPRPaintActor) renderer.addActor(CPRPaintActor);

  actorIds["CPRActor"] = CPRActor;
  if (CPRPaintActor) actorIds["CPRPaintActor"] = CPRPaintActor;
  else delete actorIds["CPRPaintActor"];

  context.current.genericRenderWindow?.resize?.();
  renderer.resetCamera();
  renderer.getActiveCamera().setParallelProjection(true);
  // renderWindow.render();

  const pointsArr = centerlineData.getPoints().getData();
  const maxheight = pointsArr.length / 3;

  const camera = renderer.getActiveCamera();

  context.current["labelData"] = labelData;
  context.current["imageData"] = imageData;
  context.current["centerlineData"] = centerlineData;
  context.current["axis"] = new_axis;
  context.current["mode"] = mode;
  context.current["width"] = width_size;
  context.current["height"] = maxheight;
  context.current["spacing"] = imageData.getSpacing();
  context.current["zoom"] = camera.getParallelScale();
  context.current["markerline_visibility"] = true;
  context.current["colorPoints"] = colorPoints;
  context.current["opacityPoints"] = opacityPoints;

  updateCPRAngleAndPath(context, angle, 0, true);
}

export function handleNodeChange(
  centerlineData: vtkPolyData,
  newIndex: number,
) {
  if (!centerlineData) {
    console.log("centerlinedata 없다");
    return null;
  }
  const numPts = centerlineData.getNumberOfPoints();
  let expandidx = newIndex;
  const position = centerlineData.getPoints().getPoint(expandidx) as [
    number,
    number,
    number,
  ];

  const points = centerlineData.getPoints();
  const prevIndex = expandidx > 0 ? expandidx - 1 : 0;
  const nextIndex = expandidx < numPts - 1 ? expandidx + 1 : numPts - 1;
  const prevPos = points.getPoint(prevIndex) as [number, number, number];
  const nextPos = points.getPoint(nextIndex) as [number, number, number];
  const normal: [number, number, number] = [
    nextPos[0] - prevPos[0],
    nextPos[1] - prevPos[1],
    nextPos[2] - prevPos[2],
  ];
  return { position, normal };
}

export function resetCPRView(
  context: React.MutableRefObject<CPRContext | null>,
  renderset: boolean = true,
) {
  if (context.current) {
    context.current.moveCountY = 0;
    context.current.overlayTranslation = [0, 0];
    context.current.savedTranslation = [0, 0, 0];
    context.current.renderer.resetCamera();
    updateCPRAngleAndPath(context, 0);
    if (renderset) {
      context.current.genericRenderWindow?.resize();
      renderVtkContextWindow(context.current, "resetCPRView");
    }
  }
}

export async function updateCPRAngleAndPath(
  context: React.MutableRefObject<CPRContext | null>,
  updatedAngle: number,
  moveY: number = 0,
  init: boolean | undefined = undefined,
  zoomset: boolean = true,
) {
  if (!context.current) return;
  const { savedTranslation, axis, rendererId, mode } = context.current;

  const radAngle = radiansFromDegrees(updatedAngle);
  handleCPRAngle(context, radAngle, savedTranslation, axis!);

  if (!savedTranslation) return;

  updateCPRPath(context, moveY);
  context.current.angle = updatedAngle;

  scheduleRender(rendererId, { type: "updateRenderWindow", zoomset, init });
}

function updateCPRPath(
  context: React.MutableRefObject<CPRContext | null>,
  moveY: number = 0,
) {
  if (!context.current) return;
  const { actorIds, renderer, canvas, rendererId } = context.current;

  const actor = actorIds["CPRActor"] as vtkImageSlice;
  if (!actor) return;
  const mapper = actor.getMapper() as unknown as vtkImageCPRMapper;
  if (!mapper) return;
  const bounds = canvas.getBoundingClientRect();
  const viewportWidth = Math.max(1, bounds.width || canvas.width || 1);
  const viewportHeight = Math.max(1, bounds.height || canvas.height || 1);
  const cprpath = pathFunc(
    actor as vtkImageSlice,
    mapper as vtkImageCPRMapper,
    renderer,
    viewportWidth,
    viewportHeight,
    moveY,
  );
  context.current.path = cprpath;

  scheduleRender(
    `MarkerLine.${rendererId?.split(".")[1]}.${context.current.mode}`,
    { type: "updatePath", newPath: cprpath },
  );
  //  context.current.paths[mode!] = cprpath;
}

export async function captureCanvas(
  promiseCapture: Promise<string>[],
  useRotate: boolean = false,
): Promise<string> {
  const [polyImagePromise] = promiseCapture;
  const polyImage = await polyImagePromise;

  const background = new Image();
  background.src = polyImage;
  await new Promise<void>((res) => {
    background.onload = () => res();
  });

  const originalCanvas = document.createElement("canvas");
  const wh =
    background.width > background.height ? background.height : background.width;

  originalCanvas.width = wh;
  originalCanvas.height = wh;

  const originalCtx = originalCanvas.getContext("2d");
  if (!originalCtx) return "";

  if (useRotate) {
    originalCtx.translate(wh / 2, wh / 2);
    originalCtx.rotate(Math.PI / 2); // 90도 회전 (시계 방향)
    originalCtx.drawImage(
      background,
      (background.width - wh) / 2,
      (background.height - wh) / 2,
      wh,
      wh,
      -wh / 2,
      -wh / 2,
      wh,
      wh,
    );
  } else {
    originalCtx.drawImage(
      background,
      (background.width - wh) / 2,
      (background.height - wh) / 2,
      wh,
      wh,
      0,
      0,
      wh,
      wh,
    );
  }
  return originalCanvas.toDataURL("image/jpeg");
}

export async function captureOneDirections(
  context: React.RefObject<Context>,
  useRotate: boolean = false,
) {
  if (!context.current) return;
  const { renderer, renderWindow, viewportId } = context.current;
  renderWindow.render();
  let url = "";
  if (useRotate) {
    url = await captureCanvaswithNotBlank(renderWindow.captureImages(), 90);
  } else {
    url = await captureCanvas(renderWindow.captureImages(), false);
    const [u, w, h] = await trimMarginsToContent(url);
    url = u;
  }
  return url;
  // return captureCanvas(renderWindow.captureImages(),useRotate);
}

export function updateRenderWindow(
  context: React.MutableRefObject<Context | null>,
) {
  // const ctx = context?.current;
  // if (!ctx) return;

  // if (ctx.viewportId === 'VR' || ctx.viewportId === 'Surface3D') {
  //   if (!context) return;
  //   if (!context.current) return;
  //   if (!context.current.renderWindow) return;

  //   context.current?.renderWindow.render();
  // } else {
  //   const rw = (ctx as any).renderWindow;
  //   if (!rw || rw.isDeleted?.()) return;

  //   // vtkOpenGLRenderWindow 얻기
  //   const glrw =
  //     rw.getOpenGLRenderWindow?.() ?? rw.getViews?.()?.[0]?.getRenderable?.(); // vtk 버전에 따라

  //   const canvas: HTMLCanvasElement | undefined = glrw?.getCanvas?.();
  //   if (!canvas) return;

  //   // 1) 캔버스 사이즈/DOM 상태 체크
  //   if (canvas.width === 0 || canvas.height === 0 || !canvas.isConnected)
  //     return;

  //   // 2) GL 컨텍스트 체크
  //   const gl: WebGL2RenderingContext | WebGLRenderingContext | undefined =
  //     glrw?.getContext?.();
  //   if (!gl) return;

  //   // 3) 컨텍스트 로스트 시 스킵
  //   if ((gl as any).isContextLost?.() || (ctx as any).__glLost) return;

  //   try {
  //     rw.render();
  //   } catch (err) {
  //     console.warn('render skipped due to GL error:', err);
  //   }
  // }

  if (!context) return;
  if (!context.current) return;
  const { renderWindow, renderer, viewportId } = context.current;
  if (!renderWindow || !renderer) return;

  const views = renderWindow.getViews();
  if (!views || views.length === 0) return;

  const openGLRenderWindow = views[0] as vtkOpenGLRenderWindow;
  if (!openGLRenderWindow) return;

  const canvas = openGLRenderWindow.getCanvas?.();
  if (!canvas || !canvas.isConnected) {
    console.log('엥 canvas ', canvas);
    return;
  }

  const size = openGLRenderWindow.getSize();
  if (!size || size[0] === 0 || size[1] === 0) {
    console.log('엥 size', size);
    return;
  }

  if (viewportId === 'VR') {
    renderer?.updateLightsGeometryToFollowCamera();
  }
  const actors = renderer.getActors();
  const volumes = renderer.getVolumes();
  const hasValidActor = actors.some((actor) => {
    const mapper = (actor as vtkActor).getMapper();
    return mapper
      ? !!(mapper.getInputData() || mapper.getInputConnection())
      : false;
  });
  const hasValidVolume = volumes.some((volume) => {
    const mapper = (volume as vtkVolume).getMapper();
    return mapper
      ? !!(mapper.getInputData() || mapper.getInputConnection())
      : false;
  });

  if (hasValidActor || hasValidVolume) {
    // renderer?.resetCameraClippingRange();
    // renderer?.updateLightsGeometryToFollowCamera();
    renderVtkContextWindow(context.current as Context, "updateRenderWindow");
  }
}

export function getIndexToWorld(
  seriesId: string,
  index?: [number, number, number],
  flipZ?: boolean,
): [number, number, number] | undefined {
  const { renderingQuality } = configStore.get().generalSetting;
  const labelVolume = getVolumeFromSeriesId(
    seriesId,
    renderingQuality !== "high",
  );
  if (!labelVolume?.imageData) return;
  const { imageData, dimensions } = labelVolume;
  if (!index) index = [dimensions[0] / 2, dimensions[1] / 2, dimensions[2] / 2];
  if (flipZ) index[2] = dimensions[2] - index[2] - 1;
  return imageData.indexToWorld(index) as [number, number, number];
}

export function ensureMainBranchIds(branchGraphDataset: any, typeDatas: any) {
  const BranchInfo = branchGraphDataset as BranchInfo;
  if (!BranchInfo) return;
  const branchindex = BranchInfo.getBranchIndex();
  const firstInfoMap: {
    [key: number]: { branch: number; lastIdx: number };
  } = {};

  for (let i = 0; i < branchindex.length; i++) {
    const branch = branchindex[i];
    if (!branch.length) continue;

    const lastIdx = branch[branch.length - 1];
    const value = typeDatas[lastIdx];

    if (value !== undefined && !(value in firstInfoMap)) {
      firstInfoMap[value] = { branch: i, lastIdx };
    }
  }

  const branchInfo = Object.entries(firstInfoMap).map(([value, info]) => ({
    value: Number(value),
    firstBranch: info.branch,
    firstLastIdx: info.lastIdx,
  }));

  const mainBranchIds: [number, number, number] = [
    branchInfo[0]?.firstBranch ?? 0,
    branchInfo[1]?.firstBranch ?? 0,
    branchInfo[2]?.firstBranch ?? 0,
  ];
  return mainBranchIds;
}

export function mergeActorBounds(
  actorNames: string | string[] | undefined,
  actorIds: Record<string, { getBounds: () => Bounds }>,
): Bounds | undefined {
  if (!actorNames) return undefined;
  const names = Array.isArray(actorNames) ? actorNames : [actorNames];
  if (names.length === 0) return undefined;

  const firstActor = actorIds[names[0]];
  if (!firstActor) return undefined;

  const bounds = [...firstActor.getBounds()] as Bounds;

  for (let i = 1; i < names.length; i++) {
    const actor = actorIds[names[i]];
    if (!actor) continue;

    const b = actor.getBounds();

    bounds[0] = Math.min(bounds[0], b[0]);
    bounds[1] = Math.max(bounds[1], b[1]);
    bounds[2] = Math.min(bounds[2], b[2]);
    bounds[3] = Math.max(bounds[3], b[3]);
    bounds[4] = Math.min(bounds[4], b[4]);
    bounds[5] = Math.max(bounds[5], b[5]);
  }

  return bounds;
}

export function updateResetCamera(
  context: React.MutableRefObject<SurfaceContext | VRContext | null>,
  analysisType: AnalysisType,
  pool?: BufferPoolItem,
) {
  if (!context.current) return;
  const { renderer, viewportId, canvas, actorIds } = context.current;
  const actorName = getActorNameByAnalysisType(analysisType);
  const isCoronary = actorName === 'coronary';
  const isAorta = analysisType === 'tavi' && actorName === 'aorta';
  type Bounds = [number, number, number, number, number, number];
  const fitRatio = isCoronary || isAorta ? 0.7 : undefined;
  const flipXBounds = (bounds: Bounds): Bounds => [
    -bounds[1], // flipped minX
    -bounds[0], // flipped maxX
    bounds[2],
    bounds[3],
    bounds[4],
    bounds[5],
  ];

  const resetCameraWithBounds = (bounds?: Bounds, useFit = true) => {
    if (!bounds) return;
    renderer.resetCamera(bounds);
    if (useFit) {
      fitActorToCanvas(renderer, bounds, canvas, fitRatio);
    }
    renderer.resetCameraClippingRange();
    return bounds;
  };

  const mergeBounds = (target: Bounds, source: Bounds) => {
    for (let i = 0; i < 6; i += 2) {
      target[i] = Math.min(target[i], source[i]);
      target[i + 1] = Math.max(target[i + 1], source[i + 1]);
    }
  };

  const getPolyDataBounds = (bounds: Bounds) => {
    const key =
      analysisType === 'FFR' && !configStore.get().EditMode
        ? 'FFR_centerline'
        : (`segmentationResult_${actorName}` as keyof BufferPoolItem);

    const polyData = pool?.[key]?.data as vtkPolyData | undefined;

    if (!polyData) return bounds;
    const b = polyData.getBounds() as Bounds;

    return key !== 'FFR_centerline' ? flipXBounds(b) : b;
  };

  const getSegmentationBounds = (): Bounds | undefined => {
    if (analysisType !== 'segmentation' || !pool) return undefined;
    const mergedBounds: Bounds = [
      Infinity,
      -Infinity,
      Infinity,
      -Infinity,
      Infinity,
      -Infinity,
    ];

    for (const labelName of getSegmentationAnalysisType(analysisType)) {
      const key = `segmentationResult_${labelName}` as keyof BufferPoolItem;
      const polyData = pool[key]?.data as vtkPolyData | undefined;
      if (!polyData) continue;
      mergeBounds(mergedBounds, polyData.getBounds() as Bounds);
    }
    return Number.isFinite(mergedBounds[0])
      ? flipXBounds(mergedBounds)
      : undefined;
  };

  const segmentationBounds = getSegmentationBounds();
  if (viewportId === 'VR') {
    const name = getVolumeNameByAnalysisType(analysisType);
    const volume = name ? actorIds[name] : undefined;
    if (!volume) return;
    const bounds =
      segmentationBounds ??
      (isCoronary || isAorta
        ? getPolyDataBounds(volume.getBounds())
        : volume.getBounds());
    return resetCameraWithBounds(bounds);
  } else if (viewportId === 'Surface3D') {
    const actor = actorName ? actorIds[actorName] : undefined;
    const actorBounds = actor ? actor.getBounds() : getActorsBounds(context);
    const bounds =
      segmentationBounds ?? getPolyDataBounds(actorBounds as Bounds);


    if(analysisType === 'FFR' && configStore.get().EditMode){
      renderer.resetCamera();
      renderer.resetCameraClippingRange();
      return bounds;
    }
    return resetCameraWithBounds(bounds, isCoronary || isAorta);
  }
}

export function updateCameraForAnalysis(
  context: React.MutableRefObject<SurfaceContext | null>,
  analysisType: AnalysisType,
  seriesId: string,
  pool?: BufferPoolItem,
) {
  if (!context.current) return;
  const { renderer, viewportId, normal, actorIds } = context.current;
  const camera: vtkCamera = renderer.getActiveCamera();

  const applyFocusAndRender = () => {
    if (analysisType !== 'FFR') {
      axesLineRenderer(context);
    }
    requestAnimationFrame(() => {
      if (analysisType === 'FFR') {
        scheduleRender(viewportId!, { type: 'updateRenderWindow' });
      } else {
        if (context.current?.focus) {
          scheduleRender('Axial', {
            type: 'focus',
            world: context.current?.focus,
            request: true,
          });
        }
      }
    });
  };

  const setFocus = ({
    focus,
    savedTranslation,
  }: {
    focus?: Vector3;
    savedTranslation?: Vector3;
  }) => {
    if (focus) {
      context.current!.focus = focus;
    }
    if (savedTranslation) {
      context.current!.savedTranslation = savedTranslation;
    }
  };

  const getBoundsCenter = (bounds: number[]): Vector3 => [
    (bounds[0] + bounds[1]) / 2,
    (bounds[2] + bounds[3]) / 2,
    (bounds[4] + bounds[5]) / 2,
  ];

  const getCombinedCenter = (b1: number[], b2: number[]): Vector3 =>
    [0, 2, 4].map(
      (i) => (Math.min(b1[i], b2[i]) + Math.max(b1[i + 1], b2[i + 1])) / 2,
    ) as Vector3;

  //avc에서만 normal 사용하는데 해당 각도로 회전하기위해서 사용
  if (analysisType === "aorticValveCalcium" && normal) {
    const curPos = camera.getPosition() as [number, number, number];
    const curFocal = camera.getFocalPoint() as [number, number, number];
    const focal: [number, number, number] = context.current.focus as [
      number,
      number,
      number,
    ];
    const dist = Math.sqrt(vtkMath.distance2BetweenPoints(curPos, curFocal));
    camera.setFocalPoint(...focal);
    vtkMath.normalize(normal);
    const newPos: [number, number, number] = [
      focal[0] - normal[0] * dist,
      focal[1] + normal[1] * dist,
      focal[2] + normal[2] * dist,
    ];
    camera.setViewUp(0, 0, -1);
    camera.setPosition(...newPos);
    //camera.orthogonalizeViewUp();
  }

  //카메라 초기셋팅
  const bounds = updateResetCamera(context, analysisType, pool);
  //renderer.resetCameraClippingRange();
  let center = bounds ? getBoundsCenter(bounds) : undefined;

  //회전축, 3d 축 좌표 설정 기준
  if (analysisType === "segmentation") {
    center = getIndexToWorld(seriesId!) as Vector3;
    if (!center) return;
    setFocus({
      focus: center,
      savedTranslation: [-center[0], center[1], center[2]],
    });
  } else if (analysisType === "myocardial") {
    const coronaryBounds = actorIds.coronary?.getBounds?.();

    const lvWallBounds = actorIds.lvWall?.getBounds?.();

    if (!coronaryBounds || !lvWallBounds) return;

    center = getCombinedCenter(coronaryBounds, lvWallBounds);
    setFocus({
      focus: context.current?.focus
        ? undefined
        : [-center[0], center[1], center[2]],
      savedTranslation: center,
    });
  } else if (
    analysisType === "calciumScore" ||
    analysisType === "wallthickness" ||
    analysisType === "tavi" ||
    analysisType === "FFR"
  ) {
    if (!center) return;

    const isFfrEditMode = configStore.get().EditMode && analysisType === 'FFR';
    const focus = isFfrEditMode
      ? (getIndexToWorld(seriesId!) as Vector3)
      : ([-center[0], center[1], center[2]] as Vector3);

    const savedTranslation = isFfrEditMode
      ? ([-focus[0], focus[1], focus[2]] as Vector3)
      : center;

    setFocus({
      focus,
      savedTranslation,
    });

  } else if (!["aorticValveCalcium"].includes(analysisType)) {
    //avc는 최상위 함수 init3DCameraForAnalysis 에서 처리
    center = getIndexToWorld(seriesId!) as Vector3;
    if (!center) return;
    setFocus({
      focus: center,
      savedTranslation: [-center[0], center[1], center[2]],
    });
  }
  applyFocusAndRender();
}

export function MarkerLinesMoveRender(
  mainIndex: number,
  branchId: number,
  markerindex: number[],
  index: number[],
  pool: BufferPoolItem,
  update3d: Boolean = true,
) {
  if (!pool) return;
  let scheduled: (() => void)[] = [];
  const newPickedPoints = markerindex.map((dragIndex, i) => {
    if (pool.centerData.data === undefined) return null;
    if (!Number.isFinite(index[i])) return null;
    const nodedata = handleNodeChange(
      (pool.centerData.data as Record<number, vtkPolyData>)[branchId],
      index[i],
    );
    if (nodedata === null) return null;
    scheduled.push(() =>
      scheduleRender(`CrossSection.${mainIndex}.${dragIndex}`, {
        type: "markerMove",
        which: dragIndex,
        newIndex: index[i],
      }),
    );

    const { position, normal } = nodedata;
    const isAorticBranch =
      branchId === configStore.get().aorta_id ||
      branchId === (configStore.get() as any).tavi_aorta_id;
    return {
      position,
      normal,
      index: isAorticBranch
        ? index[i]
        : index[i] - configStore.get().coronary_front_expand,
    };
  });

  if (newPickedPoints === null) {
    console.log("MarkerLineMoveRender 실패 / newPickedPoints 값 null");
    return;
  }
  const param = {
    type: "polygonMove",
    pickPoints: index,
  };

  const EditMode = configStore.get().EditMode;
  const isEditMode =
    EditMode === EditNames.lumen || EditMode === EditNames.regulator;
  requestAnimationFrame(() => {
    scheduled.forEach((f) => f());

    scheduleRender(`MarkerLine.${mainIndex}.stretched`, param);
    scheduleRender(`MarkerLine.${mainIndex}.straightened`, param);

    if (!isEditMode) {
      //수정모드일때는 안보내도댐
      scheduleRender("Surface3D", {
        type: "polygonMove",
        mainIndex,
        which: "total",
        newPickedPoints,
        update3d,
      });

      scheduleRender("VR", {
        type: "polygonMove",
        mainIndex,
        which: "total",
        newPickedPoints,
        update3d,
      });
    }

    scheduleRender("Report", {
      type: "polygonMove",
      mainIndex,
      which: "total",
      newPickedPoints,
    });
    scheduleRender(`CPR-${mainIndex}-20`, {
      type: "polygonMove",
      mainIndex,
      which: "total",
      newPickedPoints,
    });
  });
}

function getActorsBounds(
  context: React.MutableRefObject<SurfaceContext | VRContext | null>,
): [number, number, number, number, number, number] | null {
  if (!context.current) return null;

  let minX = +Infinity,
    maxX = -Infinity;
  let minY = +Infinity,
    maxY = -Infinity;
  let minZ = +Infinity,
    maxZ = -Infinity;

  for (const [key, actor] of Object.entries(context.current.actorIds)) {
    if (!actor || typeof actor.getBounds !== "function") continue;
    if (key === "Axial" || key === "Coronal" || key === "Sagittal") continue;
    const b = actor?.getBounds?.();
    if (!b || !isFinite(b[0])) continue;

    if (b[0] < minX) minX = b[0];
    if (b[1] > maxX) maxX = b[1];
    if (b[2] < minY) minY = b[2];
    if (b[3] > maxY) maxY = b[3];
    if (b[4] < minZ) minZ = b[4];
    if (b[5] > maxZ) maxZ = b[5];
  }

  if (!isFinite(minX) || !isFinite(maxX)) {
    return null;
  }
  return [minX, maxX, minY, maxY, minZ, maxZ];
}

export function setActorsCenter(
  context: React.MutableRefObject<SurfaceContext | VRContext | null>,
) {
  if (!context.current) return null;
  const bounds = getActorsBounds(context);
  if (!bounds) return null;
  const [minX, maxX, minY, maxY, minZ, maxZ] = bounds;
  context.current.actorsCenter = [
    (minX + maxX) / 2,
    (minY + maxY) / 2,
    (minZ + maxZ) / 2,
  ];
}

type RotateAxis = "azimuth" | "elevation" | "both";
export function rotateByDegreesAtCenter(
  context: React.MutableRefObject<Context | null>,
  options: RotateAxis = "both",
  deg: number,
  pivot?: Vector3, // 없으면 focalPoint 사용
) {
  if (!context.current) return;
  const { renderer, renderWindow } = context.current;

  const camera = renderer.getActiveCamera();
  const cameraPos = camera.getPosition();
  const cameraFp = camera.getFocalPoint();
  const viewUp = camera.getViewUp();

  const center: Vector3 = pivot ?? cameraFp;

  const trans = mat4.create();
  const newCamPos = vec3.create();
  const newFp = vec3.create();

  let azimuthAngle = 0;
  let elevationAngle = 0;

  if (options === "both") {
    azimuthAngle = vtkMath.radiansFromDegrees(deg);
    elevationAngle = vtkMath.radiansFromDegrees(deg);
  } else if (options === "azimuth") {
    azimuthAngle = vtkMath.radiansFromDegrees(deg);
  } else if (options === "elevation") {
    elevationAngle = vtkMath.radiansFromDegrees(deg);
  }

  // Azimuth: 좌우 회전
  const newViewUp = vec3.create();
  mat4.translate(trans, trans, center);
  mat4.rotate(trans, trans, azimuthAngle, viewUp);
  // Elevation: 상하 회전
  const sideAxis = vec3.create();
  const dop = camera.getDirectionOfProjection();
  vec3.cross(sideAxis, dop, viewUp); // 측면 축 (카메라 오른쪽 방향)

  mat4.rotate(trans, trans, elevationAngle, sideAxis);

  mat4.translate(trans, trans, [-center[0], -center[1], -center[2]]);

  vec3.transformMat4(newCamPos, cameraPos, trans);
  vec3.transformMat4(newFp, cameraFp, trans);
  const direction = vec3.add(vec3.create(), viewUp, cameraPos);
  vec3.transformMat4(newViewUp, direction, trans);

  camera.setPosition(newCamPos[0], newCamPos[1], newCamPos[2]);
  camera.setFocalPoint(newFp[0], newFp[1], newFp[2]);
  camera.setViewUp(
    newViewUp[0] - newCamPos[0],
    newViewUp[1] - newCamPos[1],
    newViewUp[2] - newCamPos[2],
  );
  //camera.orthogonalizeViewUp();

  renderer.resetCameraClippingRange();
  renderer.updateLightsGeometryToFollowCamera();
  renderVtkContextWindow(context.current, "rotateView");
}

export function togglePanManipulator(
  context: React.MutableRefObject<Context | null>,
  enabled: boolean,
) {
  if (!context.current) return;
  console.log(enabled);

  const interactor = context.current.renderWindow.getInteractor();
  const iStyle =
    interactor.getInteractorStyle() as vtkInteractorStyleManipulator;
  if (!iStyle?.getMouseManipulators) return;
  const pan = iStyle
    .getMouseManipulators()
    .find(
      (m: any) =>
        m?.getClassName?.() === "vtkMouseCameraTrackballPanManipulator" ||
        m?.getButton?.() === 3,
    );
  if (enabled) {
    if (!pan) return;
    iStyle.removeMouseManipulator(pan);
  } else {
    if (!pan) {
      const panManipulator = vtkMouseCameraTrackballPanManipulator.newInstance({
        button: 3, // 👉 오른쪽 마우스 버튼
      });
      iStyle.addMouseManipulator(panManipulator);
    }
  }
  iStyle.modified();
}

export function update2DLabel(context?: ContextPool) {
  function updatelabel(contextName: ContextKey) {
    scheduleRender(contextName, {
      type: 'updateRenderWindow',
      init: true,
      label: true,
    });
  }

  const currentConfig = configStore.get();
  const label = { ...currentConfig.label } as Label;
  const cprLabel = {
    ...label,
    ...(currentConfig.viewOverlayLabels?.cpr ?? {}),
  } as Label;
  const crossSectionLabel = {
    ...label,
    ...(currentConfig.viewOverlayLabels?.crossSection ?? {}),
  } as Label;

  const cprValues = Object.entries(cprLabel)
    .filter(([key]) => key !== 'lumenoutline')
    .map(([, value]) => value);
  const all2DValues = [
    ...(Object.values(label) as boolean[]),
    ...(cprValues as boolean[]),
    ...(Object.values(crossSectionLabel) as boolean[]),
  ];
  const allTrue = all2DValues.length > 0 && all2DValues.every(Boolean);
  const totalvisi = !allTrue;

  LabelAllOnOff(label, totalvisi);
  LabelAllOnOff(cprLabel, totalvisi);
  cprLabel.lumenoutline = false;
  LabelAllOnOff(crossSectionLabel, totalvisi);

  configStore.setData({
    label,
    viewOverlayLabels: {
      ...currentConfig.viewOverlayLabels,
      cpr: cprLabel,
      crossSection: crossSectionLabel,
      surface3D: currentConfig.viewOverlayLabels?.surface3D ?? {
        lesion: true,
        centerline: true,
        label: true,
        stenosisLesion: true,
      },
    },
  });

  const labelTargets = [
    'lumen',
    'plaque',
    'fai',
    'lumenoutline',
    'plaqueoutline',
    'aorta',
    'avc',
  ];
  if (context) {
    labelTargets.forEach((target) => {
      const cprValue =
        target === 'lumenoutline' ? false : (cprLabel as any)[target];
      labelorlineonoff('plaque', context, target, false, cprValue, 'cpr');
      labelorlineonoff(
        'plaque',
        context,
        target,
        false,
        (crossSectionLabel as any)[target],
        'crossSection',
      );
    });
  }

  for (let i = 0; i < 3; i++) {
    updatelabel(`CrossSection.0.${i}` as ContextKey);
    updatelabel(`CrossSection.1.${i}` as ContextKey);
    updatelabel(`CrossSection.2.${i}` as ContextKey);
    updatelabel(`CPR.${i}.straightened` as ContextKey);
  }
  updatelabel('CPR.0.stretched');

  (['Axial', 'Coronal', 'Sagittal'] as const).forEach((contextName) => {
    scheduleRender(contextName, {
      type: 'updateLabelmap',
      update2DLabel: true,
      visibility: totalvisi,
    });
  });
  // AxialLabelmapVisibility(totalvisi);
}

type CacheKey = `${string}|${string}`;
const alphaCache = new Map<CacheKey, Map<number, number>>();
export function AxialLabelmapVisibility(on: Boolean) {
  const viewportIds = getEnabledElements().map((v) => v.viewportId);
  if (viewportIds.length === 0) return;
  const viewportId = viewportIds[0];
  const seg = segmentation.activeSegmentation.getActiveSegmentation(viewportId);
  if (!seg) {
    console.log("세그멘테이션이 존재하지 않습니다.");
    return;
  }
  const { segmentationId } = seg;
  const arrangeBitMask = seg.cachedStats["arrangeBitMask"] as Record<
    number,
    number
  >;
  const colorLUTIndex = seg.cachedStats["colorLUTIndex"] as number;
  const colorLUT = segmentation.state.getColorLUT(colorLUTIndex)!;
  const vpIds =
    segmentation.state.getViewportIdsWithSegmentation(segmentationId);
  const key: CacheKey = `${viewportId}|${segmentationId}`;
  const perSegCache = alphaCache.get(key) ?? new Map<number, number>();
  let needsVisibilityUpdate = false;
  for (const [originBitStr, arrangeBit] of Object.entries(arrangeBitMask)) {
    const currentRGBA = colorLUT[arrangeBit] as Types.Color;
    if (!currentRGBA) continue;
    let nextAlpha: number;
    if (on) {
      const prev = perSegCache.get(arrangeBit);
      nextAlpha = prev ?? currentRGBA[3] ?? 255;
      perSegCache.delete(arrangeBit);
    } else {
      const curAlpha = currentRGBA[3] ?? 255;
      perSegCache.set(arrangeBit, curAlpha);
      nextAlpha = 0;
    }
    const newColor: Types.Color = [
      currentRGBA[0],
      currentRGBA[1],
      currentRGBA[2],
      nextAlpha,
    ];
    vpIds.forEach((vp) => {
      segmentation.config.color.setSegmentIndexColor(
        vp,
        segmentationId,
        arrangeBit,
        newColor,
      );
    });
    needsVisibilityUpdate ||= nextAlpha === 0;
  }
  if (perSegCache.size > 0) alphaCache.set(key, perSegCache);
  else alphaCache.delete(key);
  if (needsVisibilityUpdate) {
    const reps =
      segmentation.state.getSegmentationRepresentationsBySegmentationId(
        segmentationId,
      );
    for (const { representations } of reps) {
      for (const rep of representations) {
        const segments = rep.segments;
        const entries = Array.isArray(segments)
          ? segments.entries()
          : Object.entries(segments);
        for (const [k, segInfoAny] of entries as any) {
          const idx = Number(Array.isArray(segments) ? k : k);
          const segInfo = segInfoAny as { visible?: boolean };
          const rgba = colorLUT[idx];
          if (!rgba) continue;
          segInfo.visible = rgba[3] !== 0;
        }
      }
    }
  }
}

export function isLabelmapVisibleOverall(): boolean {
  const viewportIds = getEnabledElements().map((v) => v.viewportId);
  if (viewportIds.length === 0) return false;
  const viewportId = viewportIds[0];
  const seg = segmentation.activeSegmentation.getActiveSegmentation(viewportId);
  if (!seg) return false;
  const colorLUTIndex = seg.cachedStats["colorLUTIndex"] as number;
  const colorLUT = segmentation.state.getColorLUT(colorLUTIndex);
  if (!colorLUT) return false;
  for (let i = 0; i < colorLUT.length; i++) {
    const rgba = colorLUT[i];
    if (rgba && (rgba[3] ?? 0) !== 0) return true;
  }
  return false;
}

export function updatelabel(
  context: CPRContext | CrossSectionContext,
  target: string,
  setonoff?: boolean,
) {
  let isOn: boolean = false;
  const { actorIds, renderWindow, viewportId, opacityPoints } = context;
  let label = configStore.get().label;
  let paintActor: vtkImageSlice;

  if (viewportId === "CPR") {
    paintActor = actorIds["CPRPaintActor"] as vtkImageSlice;
    if (!paintActor) return isOn;
  } else if (viewportId === "CrossSection") {
    paintActor = actorIds["CSPaintActor"] as vtkImageSlice;
    if (!paintActor) return isOn;
  }

  if (target === "lumen") {
    isOn = label.lumen;
    if (setonoff !== undefined) isOn = setonoff;
    const opacity = isOn ? 0.5 : 0;
    LabelOpacityControl(paintActor!, renderWindow, [
      { index: 255, opacity: opacity },
    ]);
    // paintActor!.setVisibility(isOn);
  } else if (target === "plaque") {
    isOn = label.plaque;
    if (setonoff !== undefined) isOn = setonoff;
    const opacity = isOn ? 0.5 : 0;
    LabelOpacityControl(paintActor!, renderWindow, [
      { index: 1, opacity: opacity },
      { index: 2, opacity: opacity },
      { index: 3, opacity: opacity },
    ]);
  } else if (target === "fai") {
    isOn = label.fai;
    if (setonoff !== undefined) isOn = setonoff;
    const opacity = isOn ? 0.5 : 0;
    LabelOpacityControl(paintActor!, renderWindow, [
      { index: 251, opacity: opacity },
      { index: 252, opacity: opacity },
      { index: 253, opacity: opacity },
      { index: 254, opacity: opacity },
    ]);
  } else if (target === "lumenoutline") {
    isOn = label.lumenoutline;
    if (setonoff !== undefined) isOn = setonoff;
    const lumenOutline = actorIds["lumenOutline"] as vtkActor;
    if (lumenOutline) lumenOutline.setVisibility(isOn);
  } else if (target === "plaqueoutline") {
    isOn = label.plaqueoutline;
    if (setonoff !== undefined) isOn = setonoff;
    const wallOutline = actorIds["wallOutline"] as vtkActor;
    if (wallOutline) wallOutline.setVisibility(isOn);
  } else if (target === "markerline") {
    isOn = label.markerline;
  } else if (target === "guideline") {
    isOn = label.guideline;
  } else if (target === "stenosisline") {
    isOn = label.stenosisline;
  } else if (target === "isrline") {
    isOn = label.isrline;
  } else if (target === "lesionline") {
    isOn = label.lesionline;
  } else if (target === "aorta") {
    if (opacityPoints) {
      const currentConfig = configStore.get();
      isOn = label.aorta !== false;
      if (setonoff !== undefined) isOn = setonoff;
      const opacity = isOn ? 0.5 : 0;
      const aortaIndices = new Set(currentConfig.aorta_indexs ?? []);
      const avcIndices = new Set(currentConfig.avc_indexs ?? []);
      const opindex = [];
      for (let i = 0; i < opacityPoints!.length; i++) {
        const op = opacityPoints![i];
        const index = op[0];
        const isAortaIndex = aortaIndices.size > 0
          ? aortaIndices.has(index)
          : op[1] > 0 && !avcIndices.has(index);
        if (isAortaIndex) opindex.push({ index, opacity });
      }
      LabelOpacityControl(paintActor!, renderWindow, opindex);
    }
  } else if (target === "avc") {
    if (opacityPoints) {
      const currentConfig = configStore.get();
      isOn = label.avc !== false;
      if (setonoff !== undefined) isOn = setonoff;
      const opacity = isOn ? 0.5 : 0;
      const avcIndices = new Set(currentConfig.avc_indexs ?? []);
      const opindex = [];
      for (let i = 0; i < opacityPoints!.length; i++) {
        const op = opacityPoints![i];
        const index = op[0];
        if (avcIndices.has(index)) opindex.push({ index, opacity });
      }
      LabelOpacityControl(paintActor!, renderWindow, opindex);
    }
  }
  return isOn;
}
export function labelorlineonoff(
  analysisType: AnalysisType,
  context: ContextPool,
  target: string,
  rerender?: boolean,
  setonoff?: boolean,
  scope: 'all' | 'cpr' | 'crossSection' = 'all',
) {
  let isOn: boolean = false;
  function updatelabel_temp(contextName: ContextKey) {
    const con = context[contextName];
    if (!con) return;
    const contemp = con as CPRContext | CrossSectionContext;
    isOn = updatelabel(contemp, target, setonoff);

    if (rerender) {
      scheduleRender(contextName, {
        type: "updateRenderWindow",
        init: true,
        label: true,
      });
    }
  }

  if (scope === 'all' || scope === 'crossSection') {
    for (let i = 0; i < 7; i++) {
      updatelabel_temp(`CrossSection.0.${i}` as ContextKey);
      updatelabel_temp(`CrossSection.1.${i}` as ContextKey);
      updatelabel_temp(`CrossSection.2.${i}` as ContextKey);
    }
  }
  if (scope === 'all' || scope === 'cpr') {
    for (let i = 0; i < 3; i++) {
      updatelabel_temp(`CPR.${i}.straightened` as ContextKey);
    }
    updatelabel_temp("CPR.0.stretched");
  }

  // if (
  //   analysisType === 'plaque' ||
  //   analysisType === 'stenosis' ||
  //   analysisType === 'isr'
  // ) {
  //   updatelabel_temp('CPR.0.stretched');
  //   updatelabel_temp('CPR.0.straightened');
  //   updatelabel_temp('CrossSection.0.0');
  //   updatelabel_temp('CrossSection.0.1');
  //   updatelabel_temp('CrossSection.0.2');
  // }
  // if (analysisType === 'fai') {
  //   for (let i = 0; i < 3; i++) {
  //     updatelabel_temp(`CrossSection.0.${i}` as ContextKey);
  //     updatelabel_temp(`CrossSection.1.${i}` as ContextKey);
  //     updatelabel_temp(`CrossSection.2.${i}` as ContextKey);
  //     updatelabel_temp(`CPR.${i}.straightened` as ContextKey);
  //   }
  // }
  // if (analysisType === 'aorticValveCalcium') {
  //   updatelabel_temp('CPR.0.straightened');
  //   updatelabel_temp('CrossSection.0.0');
  // }
  return isOn;
}

const asRef = <T>(value: T) =>
  ({ current: value }) as React.MutableRefObject<T>;

export function setConstantPose(key: string, camera: vtkCamera) {
  const focalPoint = camera.getFocalPoint();
  const distance = camera.getDistance(); // 초점과의 거리
  switch (key) {
    case "P":
      camera.setPosition(
        focalPoint[0],
        focalPoint[1] - distance,
        focalPoint[2],
      );
      camera.setViewUp(0, 0, -1);
      break;
    case "A": // 앞에서 보기 (Y+)
      camera.setPosition(
        focalPoint[0],
        focalPoint[1] + distance,
        focalPoint[2],
      );
      camera.setViewUp(0, 0, -1);
      break;
    case "L": // 왼쪽에서 보기 (X+)
      camera.setPosition(
        focalPoint[0] + distance,
        focalPoint[1],
        focalPoint[2],
      );
      camera.setViewUp(0, 0, -1);
      break;
    case "R": // 오른쪽에서 보기 (X-)
      camera.setPosition(
        focalPoint[0] - distance,
        focalPoint[1],
        focalPoint[2],
      );
      camera.setViewUp(0, 0, -1);
      break;
    case "F": // 위에서 보기 (Z+)
      camera.setPosition(
        focalPoint[0],
        focalPoint[1],
        focalPoint[2] + distance,
      );
      camera.setViewUp(0, -1, 0);
      break;
    case "H": // 아래에서 보기 (Z-)
      camera.setPosition(
        focalPoint[0],
        focalPoint[1],
        focalPoint[2] - distance,
      );
      camera.setViewUp(0, 1, 0);
      break;
    default:
      return;
  }
}
export function setDirection3DModeling(
  context: ContextPool,
  contextName: ContextKey,
  key: string,
) {
  const con = context[contextName];
  if (!con) return;
  const { renderer, renderWindow, rendererId } = con as
    | SurfaceContext
    | VRContext;
  const camera: vtkCamera = renderer.getActiveCamera();
  setConstantPose(key, camera);
  renderer.resetCameraClippingRange();
  renderer.updateLightsGeometryToFollowCamera();
  scheduleRender(rendererId, { type: "updateRenderWindow" });
}

type TileKey = number;
type TileEntry = {
  actor: vtkActor;
  mapper: vtkMapper;
};

type RegulatorConnect3DMarkerArgs = {
  start?: Vector3 | null;
  end?: Vector3 | null;
};

const REGULATOR_CONNECT_START_KEY = "regulatorConnectStartMarker";
const REGULATOR_CONNECT_END_KEY = "regulatorConnectEndMarker";
const REGULATOR_CONNECT_LINE_KEY = "regulatorConnectLineMarker";

function createRegulatorConnectMarkerPolyData(center: Vector3, radius: number) {
  const [x, y, z] = center;
  const points = vtkPoints.newInstance();
  points.insertNextPoint(x - radius, y, z);
  points.insertNextPoint(x + radius, y, z);
  points.insertNextPoint(x, y - radius, z);
  points.insertNextPoint(x, y + radius, z);
  points.insertNextPoint(x, y, z - radius);
  points.insertNextPoint(x, y, z + radius);

  const lines = vtkCellArray.newInstance();
  lines.insertNextCell([0, 1]);
  lines.insertNextCell([2, 3]);
  lines.insertNextCell([4, 5]);

  const polyData = vtkPolyData.newInstance();
  polyData.setPoints(points);
  polyData.setLines(lines);
  return polyData;
}

function createRegulatorConnectLinePolyData(start: Vector3, end: Vector3) {
  const points = vtkPoints.newInstance();
  points.insertNextPoint(...start);
  points.insertNextPoint(...end);

  const lines = vtkCellArray.newInstance();
  lines.insertNextCell([0, 1]);

  const polyData = vtkPolyData.newInstance();
  polyData.setPoints(points);
  polyData.setLines(lines);
  return polyData;
}

function getOrCreateRegulatorConnectActor(
  context: SurfaceContext,
  key: string,
  color: Vector3,
  lineWidth: number,
) {
  const { actorIds, renderer } = context;
  let actor = actorIds[key] as vtkActor | undefined;
  if (!actor) {
    const mapper = vtkMapper.newInstance({
      scalarVisibility: false,
    });
    actor = vtkActor.newInstance();
    actor.setMapper(mapper);
    actor.setPickable(false);
    renderer.addActor(actor);
    actorIds[key] = actor;
  }

  actor.getProperty().setColor(...color);
  actor.getProperty().setLineWidth?.(lineWidth);
  return actor;
}

function setRegulatorConnectActorData(
  context: SurfaceContext,
  key: string,
  polyData: vtkPolyData | null,
  color: Vector3,
  lineWidth: number,
) {
  const actor = getOrCreateRegulatorConnectActor(
    context,
    key,
    color,
    lineWidth,
  );
  const mapper = actor.getMapper() as vtkMapper;
  if (!polyData) {
    actor.setVisibility(false);
    actor.modified();
    return;
  }

  mapper.setInputData(polyData);
  actor.setVisibility(true);
  actor.modified();
}

export function updateRegulatorConnect3DMarkers(
  context: React.MutableRefObject<SurfaceContext | null>,
  args: RegulatorConnect3DMarkerArgs,
) {
  if (!context.current) return;
  const start = args.start ?? null;
  const end = args.end ?? null;
  const markerRadiusMm = 3.0;

  setRegulatorConnectActorData(
    context.current,
    REGULATOR_CONNECT_START_KEY,
    start ? createRegulatorConnectMarkerPolyData(start, markerRadiusMm) : null,
    [0.0, 0.82, 1.0],
    2,
  );
  setRegulatorConnectActorData(
    context.current,
    REGULATOR_CONNECT_END_KEY,
    end ? createRegulatorConnectMarkerPolyData(end, markerRadiusMm) : null,
    [1.0, 0.8, 0.0],
    2,
  );
  setRegulatorConnectActorData(
    context.current,
    REGULATOR_CONNECT_LINE_KEY,
    start && end ? createRegulatorConnectLinePolyData(start, end) : null,
    [0.0, 0.82, 1.0],
    1,
  );

  renderVtkContextWindow(context.current, "regulatorConnectMarkers");
}

function polyDataFromMesh(positions: Float32Array, indices: Uint32Array) {
  const poly = vtkPolyData.newInstance();

  // Points
  const pts = vtkPoints.newInstance();
  pts.setData(positions, 3);
  poly.setPoints(pts);

  const ntri = indices.length / 3;
  const cells = new Uint32Array(ntri * 4);
  for (let t = 0, w = 0; t < ntri; ++t) {
    cells[w++] = 3;
    cells[w++] = indices[t * 3 + 0];
    cells[w++] = indices[t * 3 + 1];
    cells[w++] = indices[t * 3 + 2];
  }
  const ca = vtkCellArray.newInstance({ values: cells });
  poly.setPolys(ca);

  return poly;
}
export function createOrGetTileActor(
  map: Map<TileKey, TileEntry>,
  key: TileKey,
  renderer: any,
  color: Vector3,
): TileEntry {
  let e = map.get(key);
  if (e) return e;

  const mapper = vtkMapper.newInstance({
    interpolateScalarsBeforeMapping: false,
    scalarVisibility: false,
  });
  const actor = vtkActor.newInstance();
  actor.setMapper(mapper);
  actor.getProperty().setColor(...color);
  actor.setPickable(true);
  // actor.getProperty().setOpacity(opacity);
  actor.getProperty().setRepresentationToSurface(); // wireframe 원하면 변경
  actor.getProperty().setInterpolationToPhong();

  renderer.addActor(actor);
  e = { actor, mapper };
  map.set(key, e);
  return e;
}

export function updateTileActor(
  entry: TileEntry,
  positions: Float32Array,
  indices: Uint32Array,
) {
  if (positions.length === 0 || indices.length === 0) {
    entry.actor.setVisibility(false);
    // 필요 시 mapper InputData 해제도 가능: entry.mapper.setInputData(null as any)
    return;
  }

  const poly = polyDataFromMesh(positions, indices);
  entry.mapper.setInputData(poly);
  entry.actor.setVisibility(true);
}

// 전부 끄던가, 전부 키던가.
export function enterOrExitManualSegmentationVtk(
  context: SurfaceContext,
  isShow: boolean,
  segmentation: Segmentation,
) {
  const { actorIds, rendererId } = context;

  Object.entries(actorIds).forEach(([key, actor]) => {
    if (!segmentation[key as SegmentationControllerType]) return;
    const visible =
      isShow && segmentation[key as SegmentationControllerType].visibility;
    actor.setVisibility(visible);
    actor.modified();
  });
  scheduleRender(rendererId, { type: "updateRenderWindow" });
  fitDicomViewportsToScreen({ syncToAxial: true });
}

export function getCArmAngles(
  context: React.RefObject<SurfaceContext | VRContext | null>,
) {
  if (!context.current) return;
  const { renderer } = context.current;
  const camera: vtkCamera = renderer.getActiveCamera();
  const pos = camera.getPosition();
  const fp = camera.getFocalPoint();

  let dir = [-(fp[0] - pos[0]), -(fp[1] - pos[1]), fp[2] - pos[2]];

  const len = Math.hypot(dir[0], dir[1], dir[2]);
  dir = dir.map((v) => v / len);

  const rad2deg = 180 / Math.PI;

  const phi = Math.atan2(dir[0], dir[1]) * rad2deg;
  const theta = Math.asin(dir[2]) * rad2deg;
  //const rho = Math.sqrt(dir[0]*dir[0] + dir[1]*dir[1]);
  // const theta = Math.atan2(dir[2], rho) * rad2deg;

  const formatAngle = (v: number) => Number(Math.abs(v).toFixed(1));

  const primary = `${phi >= 0 ? "LAO" : "RAO"}: ${formatAngle(phi)}`;
  const secondary = `${theta >= 0 ? "CRA" : "CAU"}: ${formatAngle(theta)}`;

  return { primary, secondary };
}

function parseSignedCArmAngle(
  value: number | string,
  positiveLabel: string,
  negativeLabel: string,
) {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : undefined;
  }

  const text = value.trim().toUpperCase();
  const plainNumber = Number(text);
  if (Number.isFinite(plainNumber)) return plainNumber;

  const matched = text.match(/^([A-Z]+)\s*:?\s*(-?\d+(?:\.\d+)?)$/);
  if (!matched) return undefined;

  const [, label, rawAngle] = matched;
  const angle = Math.abs(Number(rawAngle));
  if (!Number.isFinite(angle)) return undefined;
  if (label === positiveLabel) return angle;
  if (label === negativeLabel) return -angle;

  return undefined;
}

export function setCameraByCArmAngles(
  context: React.RefObject<SurfaceContext | VRContext | null>,
  primary: number | string,
  secondary: number | string,
  roll?: number | string,
) {
  if (!context.current) return false;
  const { renderer, rendererId } = context.current;
  const camera: vtkCamera = renderer.getActiveCamera();
  const pos = camera.getPosition();
  const fp = camera.getFocalPoint();

  const primaryAngle = parseSignedCArmAngle(primary, "LAO", "RAO");
  const secondaryAngle = parseSignedCArmAngle(secondary, "CRA", "CAU");
  const rollText =
    typeof roll === "string"
      ? roll.toUpperCase().replace("ROT", "").replace(":", "").trim()
      : roll;
  const rollAngle = rollText === undefined ? 0 : Number(rollText);
  if (primaryAngle === undefined || secondaryAngle === undefined) return false;
  if (!Number.isFinite(rollAngle)) return false;

  const distance = Math.hypot(fp[0] - pos[0], fp[1] - pos[1], fp[2] - pos[2]);
  const safeDistance = Number.isFinite(distance) && distance > 0 ? distance : 1;
  const clampedSecondary = Math.max(-89.9, Math.min(89.9, secondaryAngle));

  const phi = (primaryAngle * Math.PI) / 180;
  const theta = (clampedSecondary * Math.PI) / 180;

  const dirX = Math.sin(phi) * Math.cos(theta);
  const dirY = Math.cos(phi) * Math.cos(theta);
  const dirZ = Math.sin(theta);
  const dirLen = Math.hypot(dirX, dirY, dirZ) || 1;

  const dir = [-dirX / dirLen, -dirY / dirLen, dirZ / dirLen];

  const camPos = [
    fp[0] - dir[0] * distance,
    fp[1] - dir[1] * distance,
    fp[2] - dir[2] * distance,
  ];
  camera.setPosition(camPos[0], camPos[1], camPos[2]);
  camera.setFocalPoint(fp[0], fp[1], fp[2]);

  const worldUp = [0, 0, -1] as Vector3;

  const forward = [fp[0] - camPos[0], fp[1] - camPos[1], fp[2] - camPos[2]];

  const fLen = Math.hypot(...forward) || 1;
  const f = forward.map((v) => v / fLen);

  const dot = worldUp[0] * f[0] + worldUp[1] * f[1] + worldUp[2] * f[2];

  let up = [
    worldUp[0] - dot * f[0],
    worldUp[1] - dot * f[1],
    worldUp[2] - dot * f[2],
  ];
  const upLen = Math.hypot(...up);
  if (upLen < 1e-6) {
    up = [0, 1, 0];
  } else {
    up = up.map((v) => v / upLen);
  }

  camera.setViewUp(up[0], up[1], up[2]);

  //    let right = [
  //   worldZ[1]*dirZ - worldZ[2]*dirY,
  //   worldZ[2]*dirX - worldZ[0]*dirZ,
  //   worldZ[0]*dirY - worldZ[1]*dirX,
  // ];

  // const rLen = Math.hypot(...right);
  // right = right.map(v => v / rLen);

  // const viewUp = [
  //   dirY*right[2] - dirZ*right[1],
  //   dirZ*right[0] - dirX*right[2],
  //   dirX*right[1] - dirY*right[0],
  // ] as Vector3;

  //camera.setViewUp(...worldZ);
  renderer.resetCameraClippingRange();
  renderer.updateLightsGeometryToFollowCamera();
  scheduleRender(rendererId, { type: "updateRenderWindow" });
  return true;
}

export function getPoolPolyDataBounds(
  pool: BufferPoolItem,
  label: string,
  flipX = true,
) {
  const key = `segmentationResult_${label}` as keyof BufferPoolItem;
  const polyData = pool[key]?.data as vtkPolyData | undefined;
  if (!polyData) {
    return;
  }

  const originalBounds = polyData.getBounds();
  const [minX, maxX, minY, maxY, minZ, maxZ] = originalBounds;

  const bounds: [number, number, number, number, number, number] = flipX
    ? [
        -originalBounds[1],
        -originalBounds[0],
        originalBounds[2],
        originalBounds[3],
        originalBounds[4],
        originalBounds[5],
      ]
    : originalBounds;

  const xCenter = ((minX + maxX) / 2) * (flipX ? -1 : 1);
  const center: Vector3 = [xCenter, (minY + maxY) / 2, (minZ + maxZ) / 2];
  return {
    center,
    bounds,
  };
}
